/* DRIVE IQ — ADVENTURE MODE V602.6 WEDDING VENUE CATEGORY TRUTH + FRESH SEARCH PROTECTION
   File: adventure-scan.js

   V602.5 focused repair:
   - Normalizes Google Places taxonomy tokens such as tourist_attraction, amusement_center,
     amusement_park, aquarium, museum, bowling_alley, park, and zoo into readable category text.
   - Includes Google primaryType / primaryTypeDisplayName in Category Truth evidence.
   - Adds a guarded Family IQ provider-metadata rescue for real attraction/recreation/tourism
     places while preserving nightclub, adult-entertainment, casino, civic, and geography blocks.
   - Broadens Family fallback queries so later fallback passes do not undo the stronger Family
     discovery language from Controls.
   - Preserves V602.4 partial-result timeout rescue and all map, pin, Place IQ, ranking,
     destination-center, result-commit, and provider-order behavior.

   V602.4 focused repair:
   - Preserves every successful provider result immediately as scan-owned partial inventory.
   - If the global scan watchdog expires after any provider returned candidates, commits those
     candidates instead of throwing away the whole scan as "Adventure scan timed out."
   - Late provider callbacks are ignored automatically by the existing scan-token authority
     after the rescued result set commits.
   - Does not change category truth, ranking, destination center, map, pins, Place IQ,
     provider ordering, fallback radius rules, or normal successful scan completion.

   V602.3 focused repair:
   - Raises the compact result shelf above every bottom-page overlay so its
     refinement controls receive touch authority.
   - Makes Show More Picks, Closer, More Local, and Similar prominent 44px controls.
   - Adds clear KEEP EXPLORING labeling without changing scan, ranking, pins,
     Place IQ, routing, or committed-result authority.
   - Preserves the current cards and pins while continuation discovery runs.
   - Adds a capture-level delegated click authority for dynamically rebuilt
     refinement buttons, with duplicate-event protection.

   V602.0 provider rebuild:
   - Makes Google Places the canonical primary inventory authority.
   - Treats Mapbox as map/routing geometry support, not Adventure inventory authority.
   - Restricts Geoapify to optional rural/outdoor/OSM fallback discovery.
   - Accepts Google Places API (New) response objects directly.
   - Preserves the proven scan lifecycle, Category Truth, ranking, local-market guard,
     pins, Place IQ, result shelf, refill settlement, and presentation authority.

   V600.83 focused repair:
   - Prevents destination scans from manufacturing a market across an entire region.
   - Adds category-specific hard radius ceilings for Food IQ, Local Favorites,
     Worth the Drive, Trending, Hidden Gems, Live Music, Nightlife, Waterfront,
     Outdoors, Family, Coffee, Dessert, Breakfast, and Pharmacy.
   - Treats township and other broad administrative destinations conservatively.
   - Filters final committed results against the same local-market authority.
   - Keeps the strongest honest local result set instead of fitting unrelated
     distant places into one destination scan.
   - Preserves ranking, Category Truth, provider sequencing, refill behavior,
     pins, shelf presentation, Place IQ, and V600.82 diagnostics.



   V600.81 focused repair:
   - Hard-settles the visible scan immediately after a valid result commit.
   - Updates scan state to completed before any presentation listener can fail.
   - Calls the UI map-results shell directly as a guaranteed fallback.
   - Emits results:committed and scan:settled compatibility events.
   - Wraps scan:completed emission so one faulty listener cannot abort painting.
   - Preserves the single-scan lifecycle and automatic compact shelf presentation.

   V600.80 focused stabilization:
   - Removes the automatic initial-depth bootstrap from the completed-scan path.
   - Prevents a successful 1–3 place scan from launching a second hidden global
     scan token after results, pins, and completion have already been committed.
   - Guarantees one user scan produces one beginScan lifecycle, one committed
     result session, one scan:completed event, and one presentation settlement.
   - Prevents the global SCANNING surface from being reactivated by a background
     depth request after the first result announcement.
   - Keeps the visible pin set, committed Places count, shelf count, and Place IQ
     session synchronized to the same immutable result pool.
   - Preserves user-driven Show More Picks, Closer, More Local, and Similar
     continuations; only the automatic post-completion depth scan is disabled.
   - Preserves V600.79 presentation watchdog, touch release, auto-shelf behavior,
     Places pill toggle, recommendation memory, and all category-truth rules.

   V600.78 focused repair:
   - Changes the successful initial-scan presentation from pill-only discovery to
     an automatic compact Top Picks shelf followed by a persistent Places pill.
   - Keeps the Places pill visible while the shelf is open so it remains the
     single open/close controller for the committed result session.
   - Makes the Places pill toggle the shelf instead of always issuing an open.
   - Uses the immutable committed result count for the pill and presentation event.
   - Opens the compact shelf only after the map surface, committed pins, and menu
     minimization have settled.
   - Never auto-opens the shelf during refill, destination editing, Place IQ, or
     an obsolete scan execution.
   - Preserves pins, map interaction, shelf scroll state, recommendation memory,
     continuation actions, and selected-place authority.


   V600.77 focused repair:
   - Adds one Scan Presentation Authority so the map, pins, shelf, Places pill,
     and Adventure menu no longer react independently to the same scan.
   - Keeps the Places pill hidden until committed results have been painted as
     pins and the map surface has been restored.
   - Makes successful initial scans settle in this order: commit results, reveal
     map, paint pins, recover camera, minimize the menu, then enable the Places pill.
   - Automatically opens the compact Top Picks shelf after presentation settles,
     while the Places pill remains the single open/close controller.
   - Preserves the active destination, committed pins, result pool, and shelf state
     when the Adventure menu is reopened to search somewhere else.
   - Prevents a count-only blank screen by revoking presentation readiness whenever
     the map surface or committed result set is unavailable.
   - Passes Destination IQ intelligence, expected inventory, and recovery guidance
     through the provider context so Candidate IQ can obey the destination authority.

   V600.76 focused repair:
   - Prevents prior-market recommendation memory from wiping out an otherwise
     valid initial destination scan.
   - Keeps normal no-repeat behavior when fresh unseen places are available.
   - If history exclusion alone would leave a populated market with zero
     accepted places, reuses the strongest previously seen non-hard-blocked
     places rather than returning a false "No strong places found" state.
   - Preserves Food chain blocking, Category Truth hard blocks, scoring,
     diversity, continuation rounds, pins, shelf behavior, and result commits.
   - Emits history-rescue diagnostics for Charleston, Gainesville, and similar
     dense-market failures.

   V600.75 focused repair:
   - Frames the dense core of a destination's results instead of allowing
     outer-market pins to determine the initial camera.
   - Keeps every committed place and pin available while using only the
     closest, most relevant result cluster for the first map view.
   - Uses up to the 16 closest validated places within 7 miles for broad
     destination categories, with existing fallbacks preserved.
   - Enforces a practical minimum result zoom so dense downtown scans do not
     open at a metro-wide scale.

   V600.74 focused repair:
   - Fits the map only to the validated result coordinates retained by
     resultDerivedBounds instead of handing every committed coordinate to
     the map controller.
   - Prevents one distant but valid place from pulling Charleston and other
     dense destination scans several zoom levels outward.
   - Tightens broad destination-result camera authority from 18 miles to
     12 miles while preserving all committed places, pins, and shelf results.
   - Changes camera framing only; discovery volume and ranking remain intact.

   V600.73 focused repair:
   - Expands Local Favorites Category Truth for real historic, cultural, tourism,
     waterfront, market, arts, garden, landmark, and independent retail experiences.
   - Recognizes those same provider categories as legitimate place-identity evidence.
   - Preserves all civic, emergency, malformed geography, utility, parking, warehouse,
     storage, apartment, and government hard blocks.
   - Prevents destination markets such as Charleston from collapsing to a false
     "No strong places found" state when Geoapify returns valid tourism metadata.
   - Does not weaken Coffee Truth, Food chain suppression, score thresholds,
     continuation behavior, committed results, pins, shelf, or photo persistence.

   V600.72 focused repair:
   - Keeps the committed Places-count pill visible and tappable whenever committed results exist.
   - Makes the pill derive its count from the immutable committed result set.
   - Reopens the existing result shelf without rescanning, clearing pins, or replacing the result session.
   - Prevents rerenders and asynchronous photo hydration from erasing a resolved place image.
   - Persists the first valid resolved photo URL by stable place key for the life of the session.
   - Preserves all V600.71 depth-settlement and V600.70 atomic shelf behavior.

   V600.71 focused repair:
   - Preserves the proven V600.70 atomic shelf, pins, Place IQ, and continuation settlement.
   - When an initial category scan commits only 1–3 trusted places, performs one silent
     depth pass before treating the first discovery session as settled.
   - Merges fresh unseen places into the same recommendation pool and updates the Places count.
   - Keeps the shelf closed during the silent depth pass and never presents it as Show More Picks.
   - Does not change PRIMARY_RECOMMENDATION_COUNT, card batching, shelf rendering, or user-driven continuation.
   - Runs at most once per destination/category session and never loops on zero growth.
   - Restricts automatic depth settlement to Food IQ, Live Music, Nightlife, Local Favorites,
     Trending, Hidden Gems, and Worth the Drive.
   - Preserves the global request watchdog and Candidate Source request timeouts.

   V600.70 focused repair:
   - Keeps the existing card track completely unchanged while Show More Picks runs.
   - Updates only the selected continuation button label during loading.
   - Stops refill guards and UI callbacks from rebuilding the shelf mid-request.
   - Prevents the header, card width, scroll position, and action-row layout from shifting.
   - Atomically swaps to the new recommendation batch only after refill completion.
   - Restores the exact prior shelf state on zero growth, failure, cancellation, or timeout.
   - Blocks duplicate continuation clicks without visually disabling the entire action row.
   - Preserves V600.69 settlement safeguards and the three-round discovery model.

   V600.69 focused repair:
   - Consolidates continuation completion and exhaustion into one settlement authority.
   - Blocks every exhaustion message while any refill/loading surface is active.
   - Clears loading first, restores the last usable shelf second, and only then
     decides whether the market is honestly exhausted.
   - Cancels stale delayed exhaustion callbacks when a new refill begins.
   - Prevents “Finding more trusted places” and “You’ve explored…” from appearing together.
   - Adds a DOM loading guard for external continuation banners.
   - Preserves the three-round discovery model and all V600.68 safeguards.

   V600.68 focused repair:
   - Prevents round-two and round-three discovery from getting stuck in loading.
   - Hard-releases refill state when scan-result commit is rejected.
   - Keeps the last usable shelf visible throughout continuation scans.
   - Adds a refill shelf guard that restores cards if another UI path hides them.
   - Reduces the refill watchdog to 18 seconds.
   - Releases loading on success, zero growth, failure, cancellation, timeout,
     obsolete execution, and rejected commit.
   - Preserves the three-round discovery model and all V600.67 behavior.

   V600.67 focused repair:
   - Locks each destination/category session to three meaningful discovery rounds.
   - Round 1 keeps the full initial trusted pool, up to 20.
   - Rounds 2 and 3 return up to 12 fresh unseen places each.
   - Replaces Another 3 New with Show More Picks.
   - Keeps the current shelf visible while the next round is loading.
   - Retries zero-growth provider responses across radius/query passes before exhaustion.
   - Never displays exhaustion while a refill is active.
   - Only declares the market explored after round 3 or after every refill path
     genuinely returns zero new trusted places.
   - Resets the three-round counter when destination, category, or discovery session changes.

   V600.66 focused repair:
   - Hard-locks the first opened Places shelf to the full trusted pool, capped at 20.
   - Removes the misleading “3 OF 8” presentation from Top Picks.
   - Keeps Another 3 New for provider refills after the full current pool is visible.
   - Adds a public runtime shelf authority so Places cannot fall back to an older three-card batch.
   - Corrects the exported build marker for live verification.
   - Restricts Food result-camera fitting to the destination area so one distant result
     cannot zoom Destin out toward Niceville or Valparaiso.
   - Adds Destin local-institution ranking authority and suppresses The Craft Bar as
     a default discovery chain result.

   V600.65 focused repair:
   - Shows the full trusted initial shelf instead of hiding five results behind Another 3.
   - Targets up to 20 visible places in strong destination markets.
   - Keeps Another 3 for genuinely new provider refills after the current pool is viewed.
   - Expands through as many as five internal retrieval passes before settling sparse markets.
   - Preserves same-market scan history so Scan Again excludes places already shown.
   - Removes obvious national and regional food chains, including Shrimp Basket and Papa Johns,
     unless the user explicitly searches for that brand.
   - Preserves local and small regional restaurant groups.

   V600.64 focused repair:
   - Treats Worth the Drive as a broad local-discovery intent, not an unknown strict category.
   - Routes Worth the Drive through the proven Local Favorites Category Truth profile.
   - Keeps Worth the Drive as a ranking/verdict signal rather than requiring those words in provider metadata.
   - Adds Detroit, Denver, and Baltimore to major-metro volume classification.
   - Preserves V600.63 fresh-session safeguards and the V600.60 scan lifecycle.

   V600.63 focused repair:
   - Fresh destination scans always begin with a clean recommendation session.
   - Clears stale shown-place, refill, cursor, and exhaustion state before a new market scan.
   - Never reports recommendation exhaustion during initial Top Picks settlement.
   - Distinguishes a true exhausted continuation from an empty/failed initial scan.
   - Preserves the proven V600.60 provider, commit, pin, timeout, and completion lifecycle.
   - Keeps the narrow safe-city-bounds guard used by V600.62.

   Purpose:
   - Own the Adventure scan lifecycle.
   - Own scan tokens, cancellation, timeout, fallback passes, and result commits.
   - Accept scan providers without allowing them to manipulate Mapbox or UI.
   - Emit scan:started, scan:progress, scan:completed, and scan:failed.
   - Ignore every delayed callback from an obsolete scan token.
   - Route result-card taps through the same shared Place IQ authority as pins.
   - Hide the shelf before focused Place IQ transitions.
   - Enforce category truth before results can commit.
   - Rank for local relevance, quality, distance, reviews, and variety.
   - Use density-aware visible result counts.
   - Rewrite dishonest provider labels into trusted user-facing categories.
   - Use raw candidate volume—not final visible count—to decide radius fallback.
   - Progressively relax ranking in dense markets without relaxing hard blocks.
   - Fill major cities from the existing candidate pool before re-querying.
   - Keep the full trusted result pool committed for pins and exploration.
   - Lead the shelf with three strong recommendations instead of every result.
   - Support non-repeating Another 3, Closer, More Local, and Similar batches.
   - Preserve explicit why-this-place evidence for every visible recommendation.
   - Treat the result shelf as persistent application state.
   - Distinguish open, minimized, and intentionally closed shelf states.
   - Restore the shelf after Place IQ closes unless the user explicitly closed it.
   - Recover the shelf if unrelated callbacks hide it after a completed scan.
   - Preserve non-repeating recommendation memory by destination, scope, and category.
   - Merge repeated scans into the existing trusted candidate pool.
   - Never recycle already shown places when the current pool is exhausted.
   - Serve sequential batches of up to three until no unseen trusted places remain.
   - Report honest exhaustion instead of silently restarting the queue.
   - Block only unmistakable non-place entities and malformed geographic records.
   - Preserve plausible real businesses when provider category metadata is incomplete.
   - Treat the committed recommendation pool as refillable working state.
   - Let Another 3, Closer, More Local, and Similar trigger targeted provider refills.
   - Merge refill candidates without repeating any place already shown.
   - Keep district/category context stable while candidate volume expands.
   - Curate each visible batch for experience variety, not just score order.
   - Reward district anchors and iconic venues without forcing them.
   - Add category-specific context bonuses for food, coffee, live music, nightlife, and outdoors.
   - Score experience signals such as live music, historic character, patios, rooftops, waterfront, and local institution status.
   - Apply category-sensitive Proximity Value so nearby wins when nearby makes sense.
   - Preserve a small exploration lane for high-confidence places outside the obvious top pattern.
   - Honor resetDiscovery and forceFreshMarket from Search IQ / Controls.
   - Never merge a fresh Near Me request with an exhausted recommendation session.
   - Retry sparse category searches with canonical provider-safe query variants.
   - Pass explicit fresh-market and intent context through to every provider.
   - Keep coffee and other quick-stop categories inside category-specific local radius caps.
   - Commit the first valid 1–3 recommendations immediately instead of auto-refilling.
   - Reserve Another 3 refills for explicit user actions after the first shelf is visible.
   - Prevent initial result painting from being misclassified as an Another 3 batch.
   - Serves every valid unseen place before requesting a refill.
   - Prevents Another 3 from freezing when only one or two unseen places remain.
   - Guarantees refill loading state release on completion, failure, cancellation, and timeout.
   - Detects zero-growth refills and continues controlled discovery without repaint loops.
   - Restores the last usable shelf instead of hiding the interface on false exhaustion.
   - Keeps refill completion separate from initial Top Picks settlement.
   - Blocks generated districts from Food, Coffee, Dessert, and other place-only searches.
   - Adds Water, Dessert, Family, and Scenic Category Truth profiles and aliases.
   - Hard-blocks police, sheriff, emergency, government, utility, jail, and parking entities.
   - Preserves districts only for category experiences where a district is a valid result.
   - Consumes Provider V600.17 Event IQ fields without fabricating live-event certainty.
   - Ranks Confirmed Tonight above Upcoming Event, recurring patterns, known music venues,
     and generic nightlife places for event-focused scans.
   - Treats the event as the recommendation headline while preserving venue identity.
   - Adds Tonight, Events, Karaoke, Trivia, Open Mic, DJ, Happy Hour, and Watch Party aliases.
   - Prevents expired event instances from winning recommendation slots.
   - Applies source-trust, freshness, start-time, and confidence bonuses.
   - Preserves recurring patterns as “Usually Happens Tonight,” never “Confirmed Tonight.”
   - Renders event-first shelf cards with event label, time, venue, area, and confidence.
   - Keeps normal place-first shelf cards for categories without usable event intelligence.
   - Emits Event IQ ranking coverage in completed scan metadata.
   - Adds Decision IQ signals for Worth the Drive, Local Favorite, Hidden Gem,
     Waterfront, Sunset Spot, Date Night, Family Friendly, Open Late,
     Tourist Favorite, Local Secret, Rainy Day, and Trending.
   - Scores those signals contextually instead of treating every badge equally.
   - Prevents weak or distant generic places from winning a Worth the Drive label.
   - Gives each shelf card one strongest recommendation verdict when supported.
   - Keeps event confidence ahead of generic recommendation badges.
   - Adds concise “why now” recommendation language to shelf reasons.
   - Preserves category truth, diversity, continuation, Event IQ, and all refill safeguards.
   - Normalizes every destination into one immutable camera-safe handoff shape.
   - Preserves canonical identity, destination profile, bounds, radius, market label,
     confidence, and child-market context across Search IQ → Scan IQ.
   - Validates latitude, longitude, and bounds before any map camera movement.
   - Prevents malformed or provider-only city payloads from blanking the map.
   - Falls back from fitBounds to flyTo when bounds are absent, reversed, or unsafe.
   - Forces the Mapbox canvas visible and calls resize after panel and shelf transitions.
   - Retries map recovery after layout settles without clearing the existing map first.
   - Gives Panama City, provider-backed cities, and curated destinations the same
     normalized map handoff used by proven destinations such as Destin.
   - Suspends persistent result-shelf recovery while destination editing is active.
   - Prevents the shelf watchdog from reopening Top Picks over autocomplete.
   - Keeps committed results retained but visually dormant during destination editing.
   - Resumes shelf persistence only after destination commit, edit cancel, or Places action.
   - Clears the shelf immediately when the linked destination session is explicitly cleared.
   - Commits results and map pins without automatically opening Top Picks.
   - Shows result availability through the Places control instead of a forced shelf.
   - Adds one explicit open/close shelf toggle for the Places control.
   - Prevents the shelf watchdog from restoring a shelf the user did not open.
   - Keeps pin selection routed into the exact Place IQ card while the shelf remains closed.
   - Keeps result memory, Another 3, Closer, More Local, and Similar intact after explicit open.
   - Separates the total trusted discovery pool from the initial Top Picks batch.
   - Targets 8–12 places in small towns, 12–18 in typical cities,
     18–24 in tourist destinations, and 24–36 in major metros when supported.
   - Raises provider candidate limits by market class instead of obeying one global cap.
   - Uses the full committed pool for pins and the Places count while still showing three cards first.
   - Expands sparse destination scans until the market-aware raw candidate floor is met.
   - Never manufactures volume when Category Truth leaves fewer trustworthy places.
   - Prevents stale category recommendation previews from covering the destination field.
   - Gives the main Adventure search/menu absolute visual priority over recommendation overlays.
   - Suppresses cards containing “Choose a destination for this recommendation.”
   - Clears stale Outdoors and category-preview panels when the menu opens or search begins.
   - Allows recommendation cards only inside the explicit V600 Top Picks shelf or Place IQ.
   - Stops reapplying broad destination bounds after results commit.
   - Builds final camera bounds only from validated committed result coordinates.
   - Rejects unsafe, oversized, or coastal-sprawl result bounds before Mapbox fit.
   - Falls back to one stable destination-center camera when result fitting is unsafe.
   - Runs map-surface recovery before and after the final camera operation.
   - Prevents Panama City and Panama City Beach geometry from leaving a blank map.
   - Classifies Tampa and Tampa Bay as major-metro discovery markets.
   - Targets 24–36 trusted Tampa-area places when Category Truth supports the volume.
   - Keeps continuation actions inside the existing shelf instead of entering global scan mode.
   - Prevents Another 3, Closer, More Local, and Similar from refitting or recovering the camera.
   - Prevents continuation requests from hiding the menu, shelf, Places control, or current pins.
   - Keeps the current cards visible with one calm shelf-level loading state.
   - Merges refill candidates once, updates pins once, and swaps cards atomically.
   - Suppresses scan:started, scan:progress, and scan:completed UI events for calm continuations.
   - Classifies markets from the immutable destination handoff when the scan center has no label.
   - Accumulates unique candidates across fallback passes instead of replacing the prior pool.
   - Expands dense-market discovery when Category Truth leaves the trusted pool below market minimum.
   - Prevents a large raw pool with only eight trusted results from falsely settling the scan.
   - Emits raw-versus-trusted volume diagnostics for every completed scan.

   Load after:
   1. adventure-config.js
   2. adventure-utils.js
   3. adventure-state.js
   4. adventure-events.js
   5. adventure-destination.js
   6. adventure-search.js

   Provider contract:
   DriveIQAdventureScan.registerProvider("primary", function (context) {
     return Promise.resolve({
       results: [],
       preferred: null,
       metadata: {}
     });
   });
*/

(function (window) {
  "use strict";

  if (window.DriveIQAdventureScan) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;
  var state = window.DriveIQAdventureState;
  var events = window.DriveIQAdventureEvents;
  var destinationController = window.DriveIQAdventureDestination;

  if (!config || !utils || !state || !events || !destinationController) {
    /* CLEAN BOOT-SAFE GUARD
       Do not crash the entire Adventure page when a modular dependency
       loads late. No observers, watchdogs, timers, or DOM mutation loops. */
    try {
      console.error(
        "[Drive IQ Adventure Scan] Module skipped because dependencies are not ready.",
        {
          config: !!config,
          utils: !!utils,
          state: !!state,
          events: !!events,
          destination: !!destinationController
        }
      );
    } catch (dependencyError) {}
    return;
  }

  var providers = {};
  var providerOrder = [];
  var PRIMARY_PLACE_PROVIDER = "google";
  var MAP_GEOMETRY_PROVIDER = "mapbox";
  var OSM_FALLBACK_PROVIDER = "geoapify";

  function providerAuthority(name) {
    var key = String(name || "").toLowerCase();

    if (/google|places_new|places-new|google_places/.test(key)) {
      return "primary";
    }

    if (/geoapify|openstreetmap|osm/.test(key)) {
      return "osm-fallback";
    }

    if (/mapbox/.test(key)) {
      return "geometry-fallback";
    }

    return "supplemental";
  }

  function providerAuthorityWeight(name) {
    return {
      primary: 0,
      supplemental: 1,
      "geometry-fallback": 2,
      "osm-fallback": 3
    }[providerAuthority(name)] || 1;
  }

  function outdoorFallbackAllowed(category) {
    var key = scanCategoryKey(category);
    return /outdoor|park|trail|water|waterfront|scenic|nature|ramp|marina|camp|fishing/.test(key);
  }
  var activeExecution = null;
  var timeoutTimer = null;
  var progressTimer = null;
  var shelfPaintTimer = null;
  var lastCommittedResults = [];
  var lastCompletedAt = 0;
  var FIRST_PAINT_DELAY_MS = 0;
  var PHOTO_PREWARM_LIMIT = 8;
  var TYPICAL_CITY_MIN_RESULTS = 12;
  var TYPICAL_CITY_MAX_RESULTS = 18;
  var TOURIST_MIN_RESULTS = 18;
  var TOURIST_MAX_RESULTS = 24;
  var MAJOR_CITY_MIN_RESULTS = 24;
  var MAJOR_CITY_MAX_RESULTS = 36;
  var SMALL_TOWN_MIN_RESULTS = 8;
  var SMALL_TOWN_MAX_RESULTS = 12;
  var VERY_SMALL_TOWN_MAX_RESULTS = 4;

  /*
    Providers must return a substantially larger raw pool than the final
    committed result count so Category Truth, ranking, and diversity can
    filter hard without making dense markets feel empty.
  */
  var STANDARD_PROVIDER_RESULT_LIMIT = 42;
  var TOURIST_PROVIDER_RESULT_LIMIT = 64;
  var MAJOR_PROVIDER_RESULT_LIMIT = 84;
  var RAW_FALLBACK_FLOOR = 16;
  var TOURIST_RAW_FALLBACK_FLOOR = 28;
  var MAJOR_RAW_FALLBACK_FLOOR = 38;
  var PROGRESSIVE_SCORE_THRESHOLDS = [82, 72, 62, 52];
  var PRIMARY_RECOMMENDATION_COUNT = 3;
  var INITIAL_SHELF_MAX_RESULTS = 20;
  var DISCOVERY_ROUND_RESULT_COUNT = 12;
  var MAX_DISCOVERY_ROUNDS = 3;
  var MAX_FALLBACK_PASSES = 4;
  var recommendationPool = [];
  var shelfBatch = [];
  var shelfBatchMode = "top";
  var shelfBatchCursor = 0;
  var shownRecommendationKeys = {};
  var lastRecommendationExecution = null;
  var recommendationSessions = {};
  var freshMarketSeenKeysBySession = {};
  var discoveryRoundsBySession = {};
  var initialDepthBootstrapSessions = {};
  var INITIAL_DEPTH_BOOTSTRAP_THRESHOLD = 3;
  var INITIAL_DEPTH_BOOTSTRAP_DELAY_MS = 90;

  /*
    V600.65 Local Food Authority:
    Discovery Food should surface places users would not already see from the road.
    Explicit brand searches remain allowed.
  */
  var DEFAULT_FOOD_CHAIN_TERMS = [
    "mcdonald","burger king","wendy","taco bell","subway",
    "chick-fil-a","chick fil a","popeyes","kfc","sonic","arbys","arby's",
    "whataburger","five guys","dairy queen","zaxby's","zaxbys",
    "raising cane's","raising canes","wingstop","buffalo wild wings",
    "papa john","papa john's","domino","pizza hut","little caesars",
    "applebee","chili's","chilis","olive garden","red lobster",
    "texas roadhouse","longhorn steakhouse","outback steakhouse",
    "cracker barrel","ihop","denny's","dennys","waffle house",
    "panera","jimmy john","jersey mike","firehouse subs",
    "panda express","chipotle","qdoba","starbucks","dunkin",
    "shrimp basket","the craft bar","craft bar","captain d's","captain ds","long john silver"
  ];
  var activeRecommendationSessionKey = "";
  var recommendationExhausted = false;
  var recommendationSessionServedCount = 0;
  var refillInProgress = false;
  var refillMode = "";
  var refillReference = null;
  var refillAttemptsBySession = {};
  var refillBaselineKeys = {};
  var refillWatchdogTimer = null;
  var refillShelfGuardTimer = null;
  var refillExhaustionTimer = null;
  var refillSettlementGeneration = 0;
  var calmContinuationLoadingActive = false;
  var refillLoadingButtonMode = "";
  var refillLoadingButtonOriginalLabel = "";
  var refillShelfScrollLeft = 0;
  var refillShelfScrollTop = 0;
  var retainedShelfScrollTop = 0;
  var retainedShelfFirstKey = "";
  var refillRequestedAt = 0;
  var lastUsableShelfBatch = [];
  var lastUsableShelfMode = "top";
  var MAX_REFILL_ATTEMPTS = 4;
  var REFILL_TARGET_COUNT = 3;
  var REFILL_WATCHDOG_MS = 18000;
  var REFILL_RETRY_DELAY_MS = 140;
  var BATCH_VARIETY_BONUS = 10;
  var BATCH_DUPLICATE_PENALTY = 16;
  var DISTRICT_ANCHOR_BONUS = 14;
  var EXPERIENCE_SIGNAL_CAP = 28;
  var EVENT_CONFIRMED_TONIGHT_BONUS = 46;
  var EVENT_UPCOMING_BONUS = 31;
  var EVENT_RECURRING_TONIGHT_BONUS = 20;
  var EVENT_KNOWN_VENUE_BONUS = 11;
  var EVENT_OFFICIAL_SOURCE_BONUS = 12;
  var EVENT_SOON_BONUS = 16;
  var EVENT_EXPIRED_PENALTY = 120;
  var DECISION_SIGNAL_CAP = 36;
  var WORTH_DRIVE_MIN_MILES = 8;
  var WORTH_DRIVE_MIN_RATING = 4.4;
  var WORTH_DRIVE_MIN_REVIEWS = 120;
  var MAX_RECOMMENDATION_SESSIONS = 24;
  var shelfState = {
    visible: false,
    minimized: false,
    userClosed: false,
    explicitOpen: false,
    available: false,
    lastReason: "",
    lastChangedAt: 0
  };
  var shelfWatchdogTimer = null;
  var SHELF_WATCHDOG_INTERVAL_MS = 240;
  var destinationCameraTimerIds = [];
  var lastSafeDestinationHandoff = null;
  var lastMapRecoveryAt = 0;
  var lastMapRecoveryReason = "";
  var MAP_RECOVERY_DELAYS = [0, 80, 240, 520];
  var RESULT_CAMERA_DELAYS = [0];
  var RESULT_CAMERA_LOCAL_MAX_MILES = 10;
  var RESULT_CAMERA_DESTINATION_MAX_MILES = 7;
  var RESULT_CAMERA_CORE_LIMIT = 16;
  var RESULT_CAMERA_MIN_ZOOM = 12.6;
  var MAX_RESULT_BOUNDS_LAT_SPAN = 1.35;
  var MAX_RESULT_BOUNDS_LNG_SPAN = 1.75;
  var MIN_RESULT_CAMERA_POINTS = 2;
  /*
    V600.83 Local Market Radius Authority:
    Discovery categories must never widen into a statewide result set.
    Values are in meters and intentionally preserve a wider ceiling for
    Worth the Drive, Waterfront, and Outdoors.
  */
  var DESTINATION_CATEGORY_RADIUS_METERS = {
    wedding_venues: { initial: 16000, wider: 28000, maximum: 40000, hardMiles: 25 },
    food: { initial: 13000, wider: 24000, maximum: 40000, hardMiles: 25 },
    food_iq: { initial: 13000, wider: 24000, maximum: 40000, hardMiles: 25 },
    local_favorites: { initial: 16000, wider: 29000, maximum: 48000, hardMiles: 30 },
    local_faves: { initial: 16000, wider: 29000, maximum: 48000, hardMiles: 30 },
    worth_the_drive: { initial: 19000, wider: 40000, maximum: 56000, hardMiles: 35 },
    worth_drive: { initial: 19000, wider: 40000, maximum: 56000, hardMiles: 35 },
    trending: { initial: 16000, wider: 29000, maximum: 40000, hardMiles: 25 },
    hidden_gems: { initial: 16000, wider: 32000, maximum: 48000, hardMiles: 30 },
    live_music: { initial: 16000, wider: 32000, maximum: 48000, hardMiles: 30 },
    nightlife: { initial: 16000, wider: 32000, maximum: 48000, hardMiles: 30 },
    waterfront: { initial: 19000, wider: 40000, maximum: 56000, hardMiles: 35 },
    outdoors: { initial: 19000, wider: 40000, maximum: 56000, hardMiles: 35 },
    family_iq: { initial: 16000, wider: 29000, maximum: 40000, hardMiles: 25 }
  };

  function destinationRadiusProfile(execution) {
    var key = scanCategoryKey(execution && execution.category);
    var profile =
      LOCAL_CATEGORY_RADIUS_METERS[key] ||
      DESTINATION_CATEGORY_RADIUS_METERS[key] ||
      null;
    var label = marketLabel(execution);

    if (!profile) {
      profile = {
        initial: 16000,
        wider: 32000,
        maximum: 48000,
        hardMiles: 30
      };
    }

    /*
      Townships, counties, regions, and other broad administrative labels are
      not trustworthy city markets. Keep them intentionally tighter until
      Search IQ resolves a more precise locality.
    */
    if (/\b(township|county|parish|borough|region|district)\b/.test(label)) {
      return {
        initial: Math.min(Number(profile.initial || 16000), 13000),
        wider: Math.min(Number(profile.wider || 24000), 24000),
        maximum: Math.min(Number(profile.maximum || 40000), 40000),
        hardMiles: Math.min(Number(profile.hardMiles || 25), 25),
        administrativeGuard: true
      };
    }

    return profile;
  }

  var LOCAL_CATEGORY_RADIUS_METERS = {
    coffee: {
      initial: 6500,
      wider: 11000,
      maximum: 19000,
      rawFloor: 3
    },
    dessert: {
      initial: 6500,
      wider: 11000,
      maximum: 19000,
      rawFloor: 3
    },
    breakfast: {
      initial: 8000,
      wider: 13000,
      maximum: 20000,
      rawFloor: 3
    },
    pharmacy: {
      initial: 6500,
      wider: 10000,
      maximum: 16000,
      rawFloor: 2
    }
  };

  /* V600.11 PLACE CARD TAP AUTHORITY
     The modular UI intentionally hid the legacy shelf, so completed scans
     showed pins and a count but no browsable cards. This controller owns one
     safe, horizontal, map-first results shelf. */
  var RESULT_SHELF_ID = "diqV600ResultShelf";
  var RESULT_STYLE_ID = "diqV600ResultShelfStyles";
  var recommendationOverlayGuardTimer = null;
  var RECOMMENDATION_OVERLAY_GUARD_MS = 180;
  var RESULTS_PILL_ID = "diqV600ResultsPill";
  var resolvedResultPhotoURLs = {};
  var resultPhotoObservers = {};
  var presentationState = {
    ready: false,
    token: "",
    resultCount: 0,
    pinsPainted: false,
    mapVisible: false,
    menuMinimized: false,
    lastReason: "",
    generation: 0
  };
  var PRESENTATION_SETTLE_DELAY_MS = 90;

  function mainAdventureMenuVisible() {
    var menu = document.getElementById("diqAdventureMenu");
    var input =
      document.getElementById("diqAdventureDestinationInput") ||
      document.getElementById("diqAdventureSearchInput") ||
      document.getElementById("diqV500SearchInput");
    var mode = String(state.get("ui.mode", "") || "");

    return !!(
      mode === "menu" ||
      mode === "destination_editing" ||
      (
        document.body &&
        (
          document.body.classList.contains("diq-v600-menu") ||
          document.body.classList.contains("diq-v600-destination-editing")
        )
      ) ||
      (
        menu &&
        menu.style.display !== "none" &&
        menu.getAttribute("aria-hidden") !== "true"
      ) ||
      (
        input &&
        (
          document.activeElement === input ||
          input.offsetParent !== null
        )
      )
    );
  }

  function recommendationOverlayText(node) {
    return String(
      node && node.textContent || ""
    ).replace(/\s+/g, " ").trim().toLowerCase();
  }

  function isProtectedRecommendationSurface(node) {
    return !!(
      !node ||
      node.id === RESULT_SHELF_ID ||
      (
        node.closest &&
        (
          node.closest("#" + RESULT_SHELF_ID) ||
          node.closest("#diqAdventurePlaceCard") ||
          node.closest(".diqPlaceIQCard") ||
          node.closest("[data-diq-place-card]")
        )
      )
    );
  }

  function looksLikeStaleRecommendationOverlay(node) {
    var textValue;

    if (
      !node ||
      isProtectedRecommendationSurface(node)
    ) {
      return false;
    }

    textValue = recommendationOverlayText(node);

    if (!textValue) {
      return false;
    }

    return !!(
      textValue.indexOf(
        "choose a destination for this recommendation"
      ) >= 0 ||
      (
        textValue.indexOf("recommendation") >= 0 &&
        (
          textValue.indexOf("outdoors") >= 0 ||
          textValue.indexOf("food") >= 0 ||
          textValue.indexOf("coffee") >= 0 ||
          textValue.indexOf("nightlife") >= 0 ||
          textValue.indexOf("live music") >= 0
        ) &&
        textValue.length < 260
      )
    );
  }

  function suppressStaleRecommendationOverlays(reason) {
    var candidates = [];
    var selectors = [
      "section",
      "aside",
      "[role='dialog']",
      "[data-recommendation]",
      "[data-diq-recommendation]",
      ".recommendation",
      ".recommendation-card",
      ".diqRecommendation",
      ".diqRecommendationCard"
    ];

    if (!mainAdventureMenuVisible()) {
      return 0;
    }

    selectors.forEach(function (selector) {
      var nodes;

      try {
        nodes = document.querySelectorAll(selector);
      } catch (error) {
        nodes = [];
      }

      Array.prototype.forEach.call(nodes, function (node) {
        if (
          looksLikeStaleRecommendationOverlay(node) &&
          candidates.indexOf(node) < 0
        ) {
          candidates.push(node);
        }
      });
    });

    candidates.forEach(function (node) {
      node.classList.remove("on");
      node.classList.remove("open");
      node.classList.remove("active");
      node.classList.remove("ready");
      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("opacity", "0", "important");
      node.style.setProperty("pointer-events", "none", "important");
      node.setAttribute("aria-hidden", "true");
      node.setAttribute("inert", "");
      node.setAttribute(
        "data-diq-search-authority-suppressed",
        String(reason || "search-visible")
      );
    });

    if (candidates.length) {
      try {
        events.emit("recommendation:overlay-suppressed", {
          reason: String(reason || "search-visible"),
          count: candidates.length
        });
      } catch (error) {}
    }

    return candidates.length;
  }

  function stopRecommendationOverlayGuard() {
    if (recommendationOverlayGuardTimer) {
      window.clearInterval(recommendationOverlayGuardTimer);
      recommendationOverlayGuardTimer = null;
    }

    return true;
  }

  function startRecommendationOverlayGuard(reason) {
    stopRecommendationOverlayGuard();
    suppressStaleRecommendationOverlays(
      reason || "search-authority"
    );

    recommendationOverlayGuardTimer = window.setInterval(function () {
      if (!mainAdventureMenuVisible()) {
        stopRecommendationOverlayGuard();
        return;
      }

      suppressStaleRecommendationOverlays(
        "search-authority-heartbeat"
      );
    }, RECOMMENDATION_OVERLAY_GUARD_MS);

    return true;
  }

  function escapeHTML(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function directResultPhoto(item) {
    return String(
      item && (
        item.diqPhotoUrl ||
        item.photoURL ||
        item.photoUrl ||
        item.heroPhoto ||
        item.heroImage ||
        item.image
      ) || ""
    );
  }

  function rememberResolvedResultPhoto(item, url) {
    var cleanURL = String(url || "").trim();
    var key;

    if (!item || !cleanURL || cleanURL === "none") {
      return "";
    }

    key = resultPhotoKey(item);

    if (key && !resolvedResultPhotoURLs[key]) {
      resolvedResultPhotoURLs[key] = cleanURL;
    }

    /*
      Preserve the first trusted photo directly on the committed item as well.
      Future shelf rebuilds may receive the same object through state, pool, or
      continuation settlement, so every path keeps the already-resolved image.
    */
    if (!item.diqPhotoUrl) {
      item.diqPhotoUrl = cleanURL;
    }

    return resolvedResultPhotoURLs[key] || cleanURL;
  }

  function resultPhoto(item) {
    var direct = directResultPhoto(item);
    var key = resultPhotoKey(item);

    if (direct) {
      return rememberResolvedResultPhoto(item, direct);
    }

    return String(key && resolvedResultPhotoURLs[key] || "");
  }

  function resultPhotoKey(item) {
    try {
      if (
        window.DriveIQAdventurePhoto &&
        typeof window.DriveIQAdventurePhoto.placeKey === "function"
      ) {
        return window.DriveIQAdventurePhoto.placeKey(item);
      }
    } catch (error) {}

    return [
      item && (
        item.placeId ||
        item.place_id ||
        item.googlePlaceId ||
        item.id ||
        item.label ||
        item.name ||
        ""
      ),
      item && (item.lat != null ? item.lat : item.latitude || ""),
      item && (item.lng != null ? item.lng : item.longitude || "")
    ].join("|");
  }

  function backgroundImageURL(element) {
    var value = "";

    if (!element) {
      return "";
    }

    try {
      value = String(
        element.style.backgroundImage ||
        window.getComputedStyle(element).backgroundImage ||
        ""
      );
    } catch (error) {
      value = "";
    }

    if (!value || value === "none") {
      return "";
    }

    value = value.replace(/^url\((['"]?)/, "").replace(/\1\)$/, "");
    return String(value || "").trim();
  }

  function captureHydratedResultPhoto(item, element) {
    var url = backgroundImageURL(element);

    if (!url) {
      return false;
    }

    rememberResolvedResultPhoto(item, url);
    element.setAttribute("data-diq-photo-resolved", "1");
    return true;
  }

  function watchHydratedResultPhoto(item, element) {
    var key = resultPhotoKey(item);
    var attempts = 0;
    var timer;

    if (!item || !element || !key) {
      return;
    }

    if (captureHydratedResultPhoto(item, element)) {
      return;
    }

    if (window.MutationObserver && !resultPhotoObservers[key]) {
      try {
        resultPhotoObservers[key] = new MutationObserver(function () {
          if (captureHydratedResultPhoto(item, element)) {
            try {
              resultPhotoObservers[key].disconnect();
            } catch (disconnectError) {}
            delete resultPhotoObservers[key];
          }
        });

        resultPhotoObservers[key].observe(element, {
          attributes: true,
          attributeFilter: ["style", "class", "data-photo-url"]
        });
      } catch (observerError) {}
    }

    timer = window.setInterval(function () {
      attempts += 1;

      if (
        captureHydratedResultPhoto(item, element) ||
        attempts >= 12 ||
        !document.documentElement.contains(element)
      ) {
        window.clearInterval(timer);

        if (resultPhotoObservers[key]) {
          try {
            resultPhotoObservers[key].disconnect();
          } catch (disconnectError) {}
          delete resultPhotoObservers[key];
        }
      }
    }, 250);
  }

  function hydrateResultThumbnail(item, element) {
    if (!item || !element) {
      return;
    }

    /*
      Paint an already-resolved URL before invoking any asynchronous resolver.
      A rerender can upgrade a placeholder, but it may never downgrade a photo.
    */
    var preserved = resultPhoto(item);

    if (preserved) {
      element.style.backgroundImage = 'url("' +
        String(preserved).replace(/"/g, "%22") +
        '")';
      element.setAttribute("data-diq-photo-resolved", "1");
    }

    try {
      if (
        window.DriveIQAdventurePhoto &&
        typeof window.DriveIQAdventurePhoto.hydrateElement === "function"
      ) {
        window.DriveIQAdventurePhoto.hydrateElement(
          item,
          element,
          {
            source: "results-shelf",
            preserveExisting: true
          }
        );
      }
    } catch (error) {}

    watchHydratedResultPhoto(item, element);
  }

  function ensureResultShelfStyles() {
    var style;

    if (document.getElementById(RESULT_STYLE_ID)) {
      return;
    }

    style = document.createElement("style");
    style.id = RESULT_STYLE_ID;
    style.type = "text/css";
    style.textContent =
      "#" + RESULT_SHELF_ID + "{" +
        "display:none;position:fixed;left:14px;right:14px;" +
        "bottom:calc(82px + env(safe-area-inset-bottom,0px));" +
        "z-index:2147482500;padding:13px;border-radius:22px;" +
        "background:rgba(3,11,20,.95);" +
        "border:1px solid rgba(51,255,200,.34);" +
        "box-shadow:0 18px 44px rgba(0,0,0,.52);" +
        "overflow:hidden;" +
      "}" +
      "#" + RESULT_SHELF_ID + ".on{display:block;animation:diqV600ShelfIn .2s ease both;}" +"@keyframes diqV600ShelfIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}" +
      ".diqV600ShelfHeader{" +
        "display:flex;align-items:center;justify-content:space-between;" +
        "gap:10px;margin:0 2px 9px;color:#dffff5;" +
        "font:900 11px/1.2 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.7px;" +
      "}" +
      ".diqV600ShelfClose{" +
        "border:0;background:rgba(255,255,255,.08);color:#fff;" +
        "width:30px;height:30px;border-radius:10px;font-size:18px;" +
      "}" +
      ".diqV600ShelfTrack{" +
        "display:flex;gap:10px;overflow-x:auto;overflow-y:hidden;" +
        "padding:0 1px 3px;scroll-snap-type:x proximity;" +
        "-webkit-overflow-scrolling:touch;" +
      "}" +
      ".diqV600ShelfCard{" +
        "flex:0 0 min(78vw,330px);display:grid;" +
        "grid-template-columns:82px minmax(0,1fr);gap:11px;" +
        "min-height:108px;padding:10px;border:1px solid rgba(255,255,255,.12);" +
        "border-radius:16px;background:rgba(255,255,255,.055);" +
        "color:#fff;text-align:left;scroll-snap-align:start;" +
        "font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
      "}" +
      ".diqV600ShelfPhoto,.diqV600ResultPhoto{" +
        "width:82px;height:88px;border-radius:13px;" +
        "background:linear-gradient(145deg,rgba(255,226,130,.65),rgba(33,242,193,.45));" +
        "background-size:cover;background-position:center;" +
      "}" +
      ".diqV600ShelfName{" +
        "display:block;font-size:16px;line-height:1.1;font-weight:900;" +
        "white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" +
      "}" +
      ".diqV600ShelfReason{" +
        "display:block;margin-top:5px;color:#bdcbd4;" +
        "font-size:12px;line-height:1.25;max-height:31px;overflow:hidden;" +
      "}" +
      ".diqV600ShelfMeta{" +
        "display:block;margin-top:7px;color:#ffe88f;" +
        "font-size:9px;line-height:1.2;font-weight:900;letter-spacing:.65px;" +
      "}" +
      ".diqV600EventBadge{" +
        "display:inline-flex;align-items:center;width:max-content;max-width:100%;" +
        "padding:6px 8px;border-radius:999px;margin-bottom:7px;" +
        "background:rgba(255,214,120,.11);border:1px solid rgba(255,214,120,.28);" +
        "color:#ffe18a;font-size:9px;line-height:1;font-weight:950;" +
        "letter-spacing:.85px;text-transform:uppercase;" +
      "}" +
      ".diqV600EventBadge.confirmed{" +
        "background:rgba(68,242,195,.11);border-color:rgba(68,242,195,.32);" +
        "color:#76ffd7;" +
      "}" +
      ".diqV600EventTitle{" +
        "display:block;color:#fff;font-size:15px;line-height:1.12;font-weight:950;" +
        "white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" +
      "}" +
      ".diqV600EventVenue{" +
        "display:block;margin-top:4px;color:#c8d6de;font-size:11px;" +
        "line-height:1.2;font-weight:800;white-space:nowrap;" +
        "overflow:hidden;text-overflow:ellipsis;" +
      "}" +
      ".diqV600DecisionBadge{" +
        "display:inline-flex;align-items:center;width:max-content;max-width:100%;" +
        "padding:6px 8px;border-radius:999px;margin-bottom:7px;" +
        "background:rgba(72,241,195,.09);border:1px solid rgba(72,241,195,.26);" +
        "color:#77ffd7;font-size:9px;line-height:1;font-weight:950;" +
        "letter-spacing:.82px;text-transform:uppercase;" +
      "}" +
      ".diqV600DecisionBadge.warm{" +
        "background:rgba(255,214,120,.09);border-color:rgba(255,214,120,.25);" +
        "color:#ffe18a;" +
      "}" +
      ".diqV600ShelfRefineLabel{" +
        "display:block;margin:12px 2px 7px;color:#79ffd9;" +
        "font:950 10px/1 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:1.05px;text-transform:uppercase;" +
      "}" +
      ".diqV600ShelfActions{" +
        "display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;" +
        "padding:0 1px 1px;overflow:visible;pointer-events:auto;" +
      "}" +
      ".diqV600ShelfAction{" +
        "display:flex;align-items:center;justify-content:center;" +
        "width:100%;min-height:44px;border:1px solid rgba(73,245,199,.28);" +
        "border-radius:14px;padding:10px 9px;" +
        "background:rgba(255,255,255,.075);color:#effff9;" +
        "font:900 11px/1.1 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.12px;touch-action:manipulation;pointer-events:auto;" +
        "-webkit-tap-highlight-color:transparent;" +
      "}" +
      ".diqV600ShelfAction:active{" +
        "transform:scale(.97);background:rgba(54,240,195,.16);" +
      "}" +
      ".diqV600ShelfAction[disabled]{" +
        "opacity:.48;pointer-events:none;" +
      "}" +
      ".diqV600ShelfAction[data-refilling='1']{" +
        "opacity:.68;" +
      "}" +
      "#" + RESULTS_PILL_ID + "{" +
        "display:none;position:fixed;left:50%;" +
        "bottom:calc(86px + env(safe-area-inset-bottom,0px));" +
        "transform:translateX(-50%);z-index:2147482000;" +
        "min-width:118px;min-height:44px;padding:0 18px;" +
        "align-items:center;justify-content:center;" +
        "border-radius:999px;border:1px solid rgba(52,255,203,.48);" +
        "background:rgba(2,11,19,.96);color:#dffff4;" +
        "box-shadow:0 14px 34px rgba(0,0,0,.52);" +
        "font:950 11px/1 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
        "letter-spacing:.9px;text-transform:uppercase;" +
        "pointer-events:auto;touch-action:manipulation;-webkit-tap-highlight-color:transparent;" +
      "}" +
      "#" + RESULTS_PILL_ID + ".on{display:flex;}" +
      "#" + RESULTS_PILL_ID + ".shelf-open{" +
        "bottom:calc(292px + env(safe-area-inset-bottom,0px));" +
      "}" +
      "#" + RESULTS_PILL_ID + ":active{transform:translateX(-50%) scale(.97);}";

    (document.head || document.documentElement).appendChild(style);
  }

  function ensureResultShelf() {
    var shelf = document.getElementById(RESULT_SHELF_ID);

    ensureResultShelfStyles();

    if (!shelf) {
      shelf = document.createElement("section");
      shelf.id = RESULT_SHELF_ID;
      shelf.setAttribute("aria-live", "polite");
      shelf.setAttribute("aria-hidden", "true");
      document.body.appendChild(shelf);
    }

    return shelf;
  }


  function resetPresentationState(reason, token) {
    retainedShelfScrollTop = 0;
    retainedShelfFirstKey = "";
    presentationState.ready = false;
    presentationState.token = String(token || "");
    presentationState.resultCount = 0;
    presentationState.pinsPainted = false;
    presentationState.mapVisible = false;
    presentationState.menuMinimized = false;
    presentationState.lastReason = String(reason || "reset");
    presentationState.generation += 1;

    try {
      events.emit("scan:presentation-reset", {
        token: presentationState.token,
        reason: presentationState.lastReason,
        generation: presentationState.generation
      });
    } catch (error) {}

    return presentationState.generation;
  }

  function mapSurfaceAvailable() {
    var map = adventureMapInstance();
    var canvas = null;

    try {
      canvas = document.querySelector(".mapboxgl-canvas");
    } catch (error) {
      canvas = null;
    }

    return !!(
      map ||
      (
        canvas &&
        canvas.offsetParent !== null &&
        window.getComputedStyle(canvas).visibility !== "hidden" &&
        window.getComputedStyle(canvas).opacity !== "0"
      )
    );
  }

  function requestAdventureMenuMinimize(reason) {
    var minimized = false;

    try {
      events.emit("ui:adventure-menu-minimize-requested", {
        reason: String(reason || "scan-presentation"),
        preserveDestination: true,
        preserveResults: true
      });
      minimized = true;
    } catch (error) {}

    try {
      if (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.minimizeMenu === "function"
      ) {
        minimized =
          window.DriveIQAdventureUI.minimizeMenu(
            String(reason || "scan-presentation")
          ) !== false || minimized;
      }
    } catch (uiError) {}

    try {
      if (
        window.DriveIQAdventureControls &&
        typeof window.DriveIQAdventureControls.minimize === "function"
      ) {
        minimized =
          window.DriveIQAdventureControls.minimize(
            String(reason || "scan-presentation")
          ) !== false || minimized;
      }
    } catch (controlsError) {}

    presentationState.menuMinimized = minimized;
    return minimized;
  }

  function autoOpenInitialResultShelf(execution, reason) {
    var pool = state.get(
      "results.items",
      lastCommittedResults
    ) || [];
    var source = String(
      reason || "initial-presentation"
    );

    if (
      !execution ||
      execution.isRefill ||
      activeExecution ||
      destinationEditingActive() ||
      placeModeActive() ||
      !pool.length ||
      presentationState.ready !== true
    ) {
      return false;
    }

    /*
      V600.78 Initial Shelf Authority:
      opening the first compact shelf is presentation settlement, not a user
      continuation and not a replacement scan.
    */
    setShelfState({
      visible: false,
      minimized: true,
      userClosed: false,
      explicitOpen: true,
      available: true
    }, source + "-prepare");

    return restoreCurrentShelf(
      source + "-open",
      {
        forceExplicitOpen: true
      }
    );
  }

  function finalizeScanPresentation(execution, results, reason) {
    var token = execution && execution.token || "";
    var generation = presentationState.generation;
    var items = Array.isArray(results) ? results : [];

    if (
      !items.length ||
      !token ||
      presentationState.token !== token
    ) {
      resetPresentationState(
        reason || "presentation-invalid",
        token
      );
      scheduleResultsPillSync("presentation-invalid");
      return false;
    }

    revealAdventureMapCanvas();
    presentationState.mapVisible = mapSurfaceAvailable();
    repaintPins(items);
    presentationState.pinsPainted = committedResultCount() > 0;
    requestAdventureMenuMinimize(
      reason || "scan-presentation-ready"
    );

    window.setTimeout(function () {
      if (
        generation !== presentationState.generation ||
        presentationState.token !== token ||
        !committedResultCount()
      ) {
        return;
      }

      revealAdventureMapCanvas();
      presentationState.mapVisible = mapSurfaceAvailable();

      presentationState.ready = !!(
        committedResultCount() > 0 &&
        presentationState.mapVisible
      );
      presentationState.resultCount = committedResultCount();
      presentationState.lastReason = String(
        reason || "scan-presentation-ready"
      );

      if (presentationState.ready) {
        autoOpenInitialResultShelf(
          execution,
          "presentation-ready"
        );
      }

      try {
        events.emit(
          presentationState.ready
            ? "scan:presentation-ready"
            : "scan:presentation-blocked",
          {
            token: token,
            count: presentationState.resultCount,
            mapVisible: presentationState.mapVisible,
            pinsPainted: presentationState.pinsPainted,
            menuMinimized: presentationState.menuMinimized,
            shelfAutoOpened:
              presentationState.ready === true &&
              resultShelfVisible(),
            reason: presentationState.lastReason,
            generation: presentationState.generation
          }
        );
      } catch (error) {}

      scheduleResultsPillSync(
        presentationState.ready
          ? "presentation-ready"
          : "presentation-blocked"
      );
    }, PRESENTATION_SETTLE_DELAY_MS);

    window.setTimeout(function () {
      if (
        presentationState.ready ||
        presentationState.token !== token ||
        !committedResultCount()
      ) {
        return;
      }

      revealAdventureMapCanvas();
      presentationState.mapVisible = mapSurfaceAvailable();

      if (presentationState.mapVisible) {
        presentationState.pinsPainted = true;
        presentationState.ready = true;
        presentationState.resultCount = committedResultCount();
        requestAdventureMenuMinimize("presentation-watchdog");
        autoOpenInitialResultShelf(execution, "presentation-watchdog");
        try {
          events.emit("scan:presentation-ready", {
            token: token,
            count: presentationState.resultCount,
            mapVisible: true,
            pinsPainted: true,
            menuMinimized: presentationState.menuMinimized,
            shelfAutoOpened: resultShelfVisible(),
            reason: "presentation-watchdog",
            generation: presentationState.generation
          });
        } catch (e) {}
        scheduleResultsPillSync("presentation-watchdog");
      }
    }, 350);

    return true;
  }

  function committedResultCount() {
    return Array.isArray(lastCommittedResults)
      ? lastCommittedResults.length
      : 0;
  }

  function ensureResultsPill() {
    var pill = document.getElementById(RESULTS_PILL_ID);

    ensureResultShelfStyles();

    if (!pill) {
      pill = document.createElement("button");
      pill.id = RESULTS_PILL_ID;
      pill.type = "button";
      pill.setAttribute("aria-label", "Open committed places");
      pill.setAttribute("aria-hidden", "true");

      pill.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (!committedResultCount()) {
          updateResultsPill("empty-click");
          return false;
        }

        /*
          Never start a new scan from the count pill. It only restores the
          current immutable committed result session.
        */
        if (placeModeActive()) {
          try {
            if (
              window.DriveIQAdventureUI &&
              typeof window.DriveIQAdventureUI.closeSelectedPlace === "function"
            ) {
              window.DriveIQAdventureUI.closeSelectedPlace(
                "results-pill"
              );
            }
          } catch (closeError) {}

          window.setTimeout(function () {
            openResultShelf("results-pill-after-place");
            updateResultsPill("results-pill-after-place");
          }, 120);

          return false;
        }

        toggleResultShelf("results-pill");
        updateResultsPill("results-pill");
        return false;
      }, false);

      document.body.appendChild(pill);
    }

    return pill;
  }

  function updateResultsPill(reason) {
    var pill = ensureResultsPill();
    var count = committedResultCount();
    var shouldShow = !!(
      count > 0 &&
      presentationState.ready === true &&
      presentationState.resultCount === count &&
      mapSurfaceAvailable() &&
      !destinationEditingActive()
    );

    if (!pill) {
      return false;
    }

    pill.textContent =
      String(count) +
      (count === 1 ? " place" : " places");

    pill.setAttribute(
      "aria-label",
      resultShelfVisible()
        ? "Close committed places"
        : "Open committed places"
    );

    pill.setAttribute(
      "data-diq-results-count",
      String(count)
    );
    pill.setAttribute(
      "data-diq-pill-reason",
      String(reason || "sync")
    );
    pill.className = shouldShow
      ? (
          resultShelfVisible()
            ? "on shelf-open"
            : "on"
        )
      : "";
    pill.setAttribute(
      "aria-hidden",
      shouldShow ? "false" : "true"
    );
    pill.style.pointerEvents = shouldShow ? "auto" : "none";

    return shouldShow;
  }

  function scheduleResultsPillSync(reason) {
    updateResultsPill(reason || "sync");

    window.setTimeout(function () {
      updateResultsPill(
        String(reason || "sync") + "-settled"
      );
    }, 0);

    window.setTimeout(function () {
      updateResultsPill(
        String(reason || "sync") + "-late"
      );
    }, 180);

    return true;
  }

  function setShelfState(nextState, reason) {
    var supplied = nextState || {};

    if (typeof supplied.visible === "boolean") {
      shelfState.visible = supplied.visible;
    }

    if (typeof supplied.minimized === "boolean") {
      shelfState.minimized = supplied.minimized;
    }

    if (typeof supplied.userClosed === "boolean") {
      shelfState.userClosed = supplied.userClosed;
    }

    if (typeof supplied.explicitOpen === "boolean") {
      shelfState.explicitOpen = supplied.explicitOpen;
    }

    if (typeof supplied.available === "boolean") {
      shelfState.available = supplied.available;
    }

    shelfState.lastReason = String(reason || "");
    shelfState.lastChangedAt = now();

    scheduleResultsPillSync(
      reason || "shelf-state-changed"
    );

    return shelfState;
  }

  function stopShelfWatchdog() {
    if (shelfWatchdogTimer) {
      window.clearInterval(shelfWatchdogTimer);
      shelfWatchdogTimer = null;
    }

    return true;
  }

  function destinationEditingActive() {
    return !!(
      state.get("ui.mode", "") === "destination_editing" ||
      state.get("destination.editing", false) === true ||
      (
        document.body &&
        document.body.classList.contains(
          "diq-v600-destination-editing"
        )
      ) ||
      (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.status === "function" &&
        window.DriveIQAdventureUI.status().destinationEditingActive === true
      )
    );
  }

  function suspendShelfForDestinationEditing(reason) {
    var shelf = document.getElementById(RESULT_SHELF_ID);

    if (shelfPaintTimer) {
      window.clearTimeout(shelfPaintTimer);
      shelfPaintTimer = null;
    }

    if (shelf) {
      shelf.className = "";
      shelf.style.pointerEvents = "none";
      shelf.setAttribute("aria-hidden", "true");
    }

    setShelfState({
      visible: false,
      minimized: true,
      userClosed: false,
      explicitOpen: false,
      available: lastCommittedResults.length > 0
    }, reason || "destination-editing");

    stopShelfWatchdog();

    try {
      events.emit("results:shelf-suspended", {
        reason: String(reason || "destination-editing"),
        count: Number(state.get("results.count", 0) || 0)
      });
    } catch (error) {}

    return true;
  }

  function shouldRestoreShelf() {
    return !!(
      !destinationEditingActive() &&
      lastCommittedResults.length &&
      !activeExecution &&
      !placeModeActive() &&
      !shelfState.userClosed &&
      shelfState.explicitOpen === true &&
      (
        shelfState.minimized ||
        shelfState.visible
      )
    );
  }

  function restoreCurrentShelf(reason, options) {
    var supplied = options || {};

    if (destinationEditingActive()) {
      suspendShelfForDestinationEditing(
        reason || "restore-blocked-destination-editing"
      );
      return false;
    }

    var pool = state.get(
      "results.items",
      lastCommittedResults
    ) || [];
    var batch;

    if (
      !pool.length ||
      activeExecution ||
      placeModeActive() ||
      shelfState.userClosed ||
      (
        supplied.forceExplicitOpen !== true &&
        shelfState.explicitOpen !== true
      )
    ) {
      return false;
    }

    if (!recommendationPool.length) {
      resetRecommendationSession(
        pool,
        lastRecommendationExecution
      );
    }

    batch = shelfBatch.length
      ? shelfBatch.slice()
      : batchFromPool("top", null, {allowRefill:false, source:"restore"});

    return renderResultShelf(batch, {
      decisionMode: shelfBatchMode,
      totalCount: recommendationPool.length,
      restoreReason: String(reason || "restore")
    });
  }

  function startShelfWatchdog() {
    stopShelfWatchdog();

    shelfWatchdogTimer = window.setInterval(function () {
      if (destinationEditingActive()) {
        suspendShelfForDestinationEditing(
          "watchdog-destination-editing"
        );
        return;
      }

      if (!shouldRestoreShelf()) {
        return;
      }

      if (!resultShelfVisible()) {
        restoreCurrentShelf("watchdog");
      }
    }, SHELF_WATCHDOG_INTERVAL_MS);

    return true;
  }

  function minimizeResultShelf(reason) {
    var shelf = document.getElementById(RESULT_SHELF_ID);

    if (shelfPaintTimer) {
      window.clearTimeout(shelfPaintTimer);
      shelfPaintTimer = null;
    }

    setShelfState({
      visible: false,
      minimized: true,
      userClosed: false,
      explicitOpen: false,
      available: lastCommittedResults.length > 0
    }, reason || "minimized");

    if (shelf) {
      shelf.className = "";
      shelf.style.pointerEvents = "none";
      shelf.setAttribute("aria-hidden", "true");
    }

    try {
      events.emit("results:shelf-minimized", {
        reason: String(reason || "minimized"),
        count: Number(state.get("results.count", 0) || 0)
      });
    } catch (error) {}

    if (!destinationEditingActive()) {
      startShelfWatchdog();
    } else {
      stopShelfWatchdog();
    }

    return true;
  }

  function closeResultShelf(reason) {
    var shelf = document.getElementById(RESULT_SHELF_ID);

    if (shelfPaintTimer) {
      window.clearTimeout(shelfPaintTimer);
      shelfPaintTimer = null;
    }

    setShelfState({
      visible: false,
      minimized: false,
      userClosed: true,
      explicitOpen: false,
      available: lastCommittedResults.length > 0
    }, reason || "user-close");

    if (shelf) {
      shelf.className = "";
      shelf.style.pointerEvents = "none";
      shelf.setAttribute("aria-hidden", "true");
    }

    try {
      events.emit("results:shelf-hidden", {
        reason: String(reason || "user-close"),
        count: Number(state.get("results.count", 0) || 0),
        userClosed: true
      });
    } catch (error) {}

    stopShelfWatchdog();
    return true;
  }

  function openResultShelf(reason) {
    var pool = state.get(
      "results.items",
      lastCommittedResults
    ) || [];

    if (
      destinationEditingActive() ||
      activeExecution ||
      placeModeActive() ||
      !pool.length
    ) {
      return false;
    }

    setShelfState({
      visible: false,
      minimized: true,
      userClosed: false,
      explicitOpen: true,
      available: true
    }, reason || "explicit-open");

    return restoreCurrentShelf(
      reason || "explicit-open",
      {
        forceExplicitOpen: true
      }
    );
  }

  function toggleResultShelf(reason) {
    var source = String(reason || "places-toggle");

    if (resultShelfVisible()) {
      minimizeResultShelf(source + "-close");

      try {
        events.emit("results:shelf-toggled", {
          open: false,
          reason: source,
          count: Number(state.get("results.count", 0) || 0)
        });
      } catch (error) {}

      return false;
    }

    var opened = openResultShelf(source + "-open");

    try {
      events.emit("results:shelf-toggled", {
        open: opened === true,
        reason: source,
        count: Number(state.get("results.count", 0) || 0)
      });
    } catch (error) {}

    return opened;
  }

  function hideResultShelf(reason) {
    var why = String(reason || "hidden");

    if (
      why === "user-close" ||
      why === "new-scan" ||
      why === "scan-cancelled" ||
      why === "scan-failed" ||
      why === "adventure-exit"
    ) {
      return closeResultShelf(why);
    }

    return minimizeResultShelf(why);
  }

  function openResult(item) {
    var opened = false;

    if (!item) {
      return false;
    }

    /*
      Shared Selection Authority:
      result cards and map pins must converge on the same UI-owned Place IQ
      transition. Hide the shelf first so browsing UI never competes with the
      focused destination card.
    */
    minimizeResultShelf("place-selected");

    try {
      if (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.openSelectedPlace === "function"
      ) {
        opened = window.DriveIQAdventureUI.openSelectedPlace(
          item,
          "result-card"
        ) !== false;
      }
    } catch (uiSelectionError) {
      opened = false;
    }

    /*
      Compatibility fallback for builds where the shared UI controller has
      not loaded yet. Preserve the existing direct Place IQ path.
    */
    if (!opened) {
      try {
        state.selectPlace(item, "result-card");
      } catch (stateError) {}

      try {
        if (
          window.DriveIQAdventurePlaceCard &&
          typeof window.DriveIQAdventurePlaceCard.open === "function"
        ) {
          opened = window.DriveIQAdventurePlaceCard.open(item) !== false;
        }
      } catch (placeCardError) {
        opened = false;
      }

      try {
        if (
          window.DriveIQAdventurePins &&
          typeof window.DriveIQAdventurePins.select === "function"
        ) {
          window.DriveIQAdventurePins.select(item, "result-card");
        } else {
          events.emit("pin:selected", {
            item: item,
            place: item,
            result: item,
            source: "result-card"
          });
        }
      } catch (pinError) {
        try {
          events.emit("pin:selected", {
            item: item,
            place: item,
            result: item,
            source: "result-card"
          });
        } catch (eventError) {}
      }
    }

    try {
      if (
        window.DriveIQAdventureMap &&
        typeof window.DriveIQAdventureMap.focusPlace === "function"
      ) {
        window.DriveIQAdventureMap.focusPlace(item, {
          duration: 450,
          source: "result-card"
        });
      }
    } catch (mapError) {}

    if (opened) {
      return true;
    }

    /*
      Recovery Authority:
      only restore the shelf when every Place IQ path failed. This prevents
      users from being stranded with hidden browsing controls.
    */
    restoreCurrentShelf("place-open-failed");

    try {
      if (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.showToast === "function"
      ) {
        window.DriveIQAdventureUI.showToast(
          "Place information could not open"
        );
      }
    } catch (uiError) {}

    return false;
  }

  function placeModeActive() {
    return !!(
      state.get("ui.mode", "") === "place" ||
      (
        document.body &&
        (
          document.body.classList.contains("diq-v600-place") ||
          document.body.classList.contains("diqPlaceIQMode")
        )
      )
    );
  }

  function resultShelfVisible() {
    var shelf = document.getElementById(RESULT_SHELF_ID);

    if (destinationEditingActive()) {
      return false;
    }

    return !!(
      shelf &&
      shelf.classList.contains("on") &&
      shelf.getAttribute("aria-hidden") !== "true"
    );
  }


  function recommendationContextKey(execution) {
    var source = execution || {};
    var center = source.center || {};
    var destination = destinationController.current() || {};
    var identity = String(
      destination.placeId ||
      destination.place_id ||
      destination.id ||
      destination.name ||
      destination.label ||
      center.label ||
      ""
    );
    var lat = Number(center.lat);
    var lng = Number(center.lng);

    if (!identity && isFinite(lat) && isFinite(lng)) {
      identity =
        lat.toFixed(3) + "," +
        lng.toFixed(3);
    }

    return [
      scanCategoryKey(source.category || config.scan.defaultCategory),
      normalizedScope(source.scope || config.scan.defaultScope),
      norm(identity)
    ].join("|");
  }

  function discoveryRoundSessionKey(execution) {
    return recommendationContextKey(
      execution ||
      lastRecommendationExecution ||
      {}
    );
  }

  function currentDiscoveryRound(execution) {
    var key = discoveryRoundSessionKey(execution);

    return Math.max(
      1,
      Number(discoveryRoundsBySession[key] || 1)
    );
  }

  function setDiscoveryRound(value, execution) {
    var key = discoveryRoundSessionKey(execution);

    if (!key) {
      return 1;
    }

    discoveryRoundsBySession[key] = Math.max(
      1,
      Math.min(
        MAX_DISCOVERY_ROUNDS,
        Number(value || 1)
      )
    );

    return discoveryRoundsBySession[key];
  }

  function advanceDiscoveryRound(execution) {
    return setDiscoveryRound(
      currentDiscoveryRound(execution) + 1,
      execution
    );
  }

  function discoveryRoundsComplete(execution) {
    return currentDiscoveryRound(execution) >= MAX_DISCOVERY_ROUNDS;
  }

  function trimRecommendationSessions() {
    var keys = Object.keys(recommendationSessions);

    if (keys.length <= MAX_RECOMMENDATION_SESSIONS) {
      return;
    }

    keys.sort(function (a, b) {
      return Number(
        recommendationSessions[a] &&
        recommendationSessions[a].updatedAt || 0
      ) - Number(
        recommendationSessions[b] &&
        recommendationSessions[b].updatedAt || 0
      );
    });

    while (keys.length > MAX_RECOMMENDATION_SESSIONS) {
      delete recommendationSessions[keys.shift()];
    }
  }

  function recommendationSession(key) {
    return key && recommendationSessions[key]
      ? recommendationSessions[key]
      : null;
  }

  /*
    V600.63 Fresh Market Session Authority:
    A new destination/category scan is a new discovery transaction. It must
    never inherit shown keys, exhaustion, refill attempts, cursor position, or
    the last usable shelf from a previous city.
  */
  function resetFreshMarketRecommendationState(reason) {
    recommendationPool = [];
    shelfBatch = [];
    shelfBatchMode = "top";
    shelfBatchCursor = 0;
    shownRecommendationKeys = {};
    recommendationExhausted = false;
    recommendationSessionServedCount = 0;
    activeRecommendationSessionKey = "";
    lastRecommendationExecution = null;
    lastUsableShelfBatch = [];
    lastUsableShelfMode = "top";
    refillInProgress = false;
    refillMode = "";
    refillReference = null;
    refillBaselineKeys = {};
    refillRequestedAt = 0;
    calmContinuationLoadingActive = false;
    refillSettlementGeneration += 1;
    clearRefillExhaustionTimer();

    /*
      The new execution will establish round 1 under its own destination,
      scope, and category session key.
    */
    discoveryRoundsBySession = {};
    initialDepthBootstrapSessions = {};

    if (refillWatchdogTimer) {
      window.clearTimeout(refillWatchdogTimer);
      refillWatchdogTimer = null;
    }

    setCalmContinuationLoading(false, "");

    try {
      events.emit("recommendations:fresh-session-reset", {
        reason: String(reason || "fresh-market-scan"),
        resetAt: now()
      });
    } catch (error) {}

    return true;
  }

  function mergeRecommendationPools(existing, incoming) {
    var merged = [];
    var seen = {};

    (existing || []).concat(incoming || []).forEach(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      if (!key || seen[key]) {
        return;
      }

      seen[key] = true;
      merged.push(item);
    });

    merged.sort(function (a, b) {
      return Number(b && b.__scanScore || 0) -
        Number(a && a.__scanScore || 0);
    });

    return merged;
  }

  function persistRecommendationSession() {
    if (!activeRecommendationSessionKey) {
      return false;
    }

    recommendationSessions[activeRecommendationSessionKey] = {
      pool: recommendationPool.slice(),
      shownKeys: Object.assign({}, shownRecommendationKeys),
      cursor: shelfBatchCursor,
      exhausted: recommendationExhausted,
      servedCount: recommendationSessionServedCount,
      discoveryRound: currentDiscoveryRound(lastRecommendationExecution),
      execution: lastRecommendationExecution
        ? Object.assign({}, lastRecommendationExecution)
        : null,
      updatedAt: now()
    };

    trimRecommendationSessions();
    return true;
  }

  function resetRecommendationSession(pool, execution, options) {
    var supplied = options || {};
    var sessionKey = supplied.sessionKey ||
      recommendationContextKey(execution);
    var previous = supplied.preserveExisting
      ? recommendationSession(sessionKey)
      : null;

    activeRecommendationSessionKey = sessionKey;
    lastRecommendationExecution = execution || null;

    if (previous) {
      recommendationPool = mergeRecommendationPools(
        previous.pool || [],
        Array.isArray(pool) ? pool : []
      );
      shownRecommendationKeys = Object.assign(
        {},
        previous.shownKeys || {}
      );
      shelfBatchCursor = Number(previous.cursor || 0);
      recommendationSessionServedCount = Number(
        previous.servedCount ||
        Object.keys(previous.shownKeys || {}).length ||
        0
      );
      setDiscoveryRound(
        Number(previous.discoveryRound || 1),
        execution
      );
      recommendationExhausted = false;
    } else {
      recommendationPool = Array.isArray(pool)
        ? pool.slice()
        : [];
      shownRecommendationKeys = {};
      shelfBatchCursor = 0;
      recommendationSessionServedCount = 0;
      setDiscoveryRound(1, execution);
      recommendationExhausted = false;
    }

    shelfBatch = [];
    shelfBatchMode = "top";

    persistRecommendationSession();
    return true;
  }

  function markBatchShown(items) {
    if (Array.isArray(items) && items.length) {
      recommendationSessionServedCount += items.length;
    }

    (items || []).forEach(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      if (key) {
        shownRecommendationKeys[key] = true;
      }
    });

    recommendationExhausted =
      unseenRecommendations(recommendationPool).length === 0;

    persistRecommendationSession();
  }

  function unseenRecommendations(pool) {
    return (pool || []).filter(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      return !!(
        key &&
        !shownRecommendationKeys[key]
      );
    });
  }

  function recommendationRemainingCount() {
    return unseenRecommendations(recommendationPool).length;
  }

  function clearRefillExhaustionTimer() {
    if (refillExhaustionTimer) {
      window.clearTimeout(refillExhaustionTimer);
      refillExhaustionTimer = null;
    }

    return true;
  }

  function continuationLoadingSurfaceActive() {
    var shelf = document.getElementById(RESULT_SHELF_ID);
    var textValue = "";
    var nodes = [];

    if (
      refillInProgress ||
      calmContinuationLoadingActive ||
      refillWatchdogTimer ||
      refillShelfGuardTimer ||
      activeExecution && activeExecution.isRefill
    ) {
      return true;
    }

    if (
      shelf &&
      shelf.getAttribute("data-diq-calm-continuation") === "1"
    ) {
      return true;
    }

    try {
      nodes = document.querySelectorAll(
        "[data-diq-refill-loading='1']," +
        "[data-diq-calm-continuation='1']," +
        "[aria-busy='true']"
      );
    } catch (error) {
      nodes = [];
    }

    if (nodes && nodes.length) {
      return true;
    }

    try {
      textValue = String(
        document.body && document.body.innerText || ""
      ).toLowerCase();

      if (
        textValue.indexOf("finding more trusted places") >= 0 ||
        textValue.indexOf("finding more local picks") >= 0 ||
        textValue.indexOf("finding closer picks") >= 0 ||
        textValue.indexOf("finding similar picks") >= 0
      ) {
        return true;
      }
    } catch (error2) {}

    return false;
  }

  function finalizeRefillExhaustion(mode, reason) {
    var selectedMode = String(mode || "another");
    var generation = ++refillSettlementGeneration;

    clearRefillExhaustionTimer();
    releaseRefillState(
      "final-exhaustion-" + String(reason || "zero-growth")
    );
    restoreLastUsableShelf(
      "final-exhaustion-" + String(reason || "zero-growth")
    );

    refillExhaustionTimer = window.setTimeout(function () {
      if (
        generation !== refillSettlementGeneration ||
        continuationLoadingSurfaceActive()
      ) {
        return;
      }

      refillExhaustionTimer = null;
      emitRecommendationExhausted(selectedMode);
    }, 320);

    return true;
  }

  function emitRecommendationExhausted(mode) {
    var selectedMode = String(mode || "another");

    /*
      V600.67 Exhaustion Authority:
      never tell the user the market is exhausted while the engine is still
      actively searching. Before round 3, exhaustion is only valid after the
      full controlled refill attempt path has returned zero growth.
    */
    if (
      refillInProgress ||
      activeExecution ||
      continuationLoadingSurfaceActive()
    ) {
      return false;
    }

    if (
      selectedMode !== "top" &&
      !discoveryRoundsComplete(lastRecommendationExecution) &&
      refillAttemptCount() < MAX_REFILL_ATTEMPTS
    ) {
      return false;
    }

    /*
      Initial Top Picks settlement is not a continuation request. An empty
      initial pool means "no results yet", not "you explored everything".
      Exhaustion is only truthful after this exact session served at least one
      real recommendation and the user requested continuation.
    */
    if (
      selectedMode === "top" ||
      recommendationSessionServedCount < 1 ||
      recommendationPool.length < 1
    ) {
      recommendationExhausted = false;
      shelfBatch = [];

      try {
        events.emit("recommendations:empty-initial-session", {
          mode: selectedMode,
          sessionKey: activeRecommendationSessionKey,
          totalCount: recommendationPool.length,
          servedCount: recommendationSessionServedCount,
          category: lastRecommendationExecution
            ? lastRecommendationExecution.category
            : "",
          scope: lastRecommendationExecution
            ? lastRecommendationExecution.scope
            : ""
        });
      } catch (emptyEventError) {}

      return false;
    }

    recommendationExhausted = true;
    shelfBatch = [];
    persistRecommendationSession();

    try {
      events.emit("recommendations:exhausted", {
        mode: String(mode || "another"),
        sessionKey: activeRecommendationSessionKey,
        totalCount: recommendationPool.length,
        shownCount: Object.keys(shownRecommendationKeys).length,
        servedCount: recommendationSessionServedCount,
        category: lastRecommendationExecution
          ? lastRecommendationExecution.category
          : "",
        scope: lastRecommendationExecution
          ? lastRecommendationExecution.scope
          : ""
      });
    } catch (error) {}

    try {
      if (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.showToast === "function"
      ) {
        window.DriveIQAdventureUI.showToast(
          "You’ve explored the strongest local picks here"
        );
      }
    } catch (uiError) {}

    return false;
  }



  function requestedCategoryKey() {
    return scanCategoryKey(
      lastRecommendationExecution &&
      lastRecommendationExecution.category ||
      config.scan.defaultCategory
    );
  }

  function eventConfidence(item) {
    return norm(
      item && (
        item.eventConfidence ||
        item.primaryEvent && item.primaryEvent.confidence ||
        ""
      )
    ).replace(/\s+/g, "_");
  }

  function eventRecord(item) {
    if (!item) {
      return null;
    }

    if (item.primaryEvent && typeof item.primaryEvent === "object") {
      return item.primaryEvent;
    }

    if (
      item.eventTitle ||
      item.eventStartTime ||
      item.eventRecurrenceText ||
      item.eventConfidence
    ) {
      return {
        eventTitle: item.eventTitle || "",
        eventType: item.eventType || "",
        startTime: item.eventStartTime || "",
        endTime: item.eventEndTime || "",
        floorOrArea: item.eventFloorOrArea || "",
        sourceType: item.eventSourceType || "",
        sourceURL: item.eventSourceURL || "",
        confirmedAt: item.eventConfirmedAt || "",
        recurring: item.eventRecurring === true,
        recurrenceText: item.eventRecurrenceText || "",
        confidence: item.eventConfidence || ""
      };
    }

    return null;
  }

  function eventDate(value) {
    var date;

    if (!value) {
      return null;
    }

    date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  function eventExpired(item) {
    var event = eventRecord(item);
    var end;
    var start;
    var current = new Date();

    if (!event) {
      return false;
    }

    if (event.expired === true) {
      return true;
    }

    end = eventDate(event.endTime);
    start = eventDate(event.startTime);

    if (end) {
      return end.getTime() < current.getTime();
    }

    return !!(
      start &&
      start.getTime() < current.getTime() - 6 * 60 * 60 * 1000
    );
  }

  function eventFocusedCategory(category) {
    var key = scanCategoryKey(category);

    return (
      key === "live_music" ||
      key === "nightlife" ||
      key === "events" ||
      key === "tonight" ||
      key === "karaoke" ||
      key === "trivia" ||
      key === "open_mic" ||
      key === "watch_party" ||
      key === "happy_hour" ||
      key === "dj"
    );
  }

  function eventSourceTrustScore(item) {
    var event = eventRecord(item);
    var source = norm(
      event && (
        event.sourceType ||
        event.source ||
        ""
      )
    );

    if (/official_website|official website|venue website/.test(source)) {
      return EVENT_OFFICIAL_SOURCE_BONUS;
    }

    if (/official_social|official social|facebook|instagram/.test(source)) {
      return EVENT_OFFICIAL_SOURCE_BONUS - 2;
    }

    if (/ticketmaster|bandsintown|eventbrite|event_platform/.test(source)) {
      return EVENT_OFFICIAL_SOURCE_BONUS - 4;
    }

    if (/provider|google/.test(source)) {
      return 4;
    }

    return 0;
  }

  function eventSoonScore(item) {
    var event = eventRecord(item);
    var start = eventDate(event && event.startTime);
    var hours;

    if (!start) {
      return 0;
    }

    hours = (start.getTime() - Date.now()) / 3600000;

    if (hours < -6) {
      return -EVENT_EXPIRED_PENALTY;
    }

    if (hours >= -1 && hours <= 3) {
      return EVENT_SOON_BONUS;
    }

    if (hours > 3 && hours <= 8) {
      return 11;
    }

    if (hours > 8 && hours <= 24) {
      return 6;
    }

    if (hours > 24 && hours <= 72) {
      return 2;
    }

    return 0;
  }

  function eventIQScore(item, execution) {
    var confidence = eventConfidence(item);
    var score = 0;
    var focused = eventFocusedCategory(
      execution && execution.category
    );

    if (eventExpired(item)) {
      return -EVENT_EXPIRED_PENALTY;
    }

    if (confidence === "confirmed_tonight") {
      score += EVENT_CONFIRMED_TONIGHT_BONUS;
    } else if (confidence === "upcoming_event") {
      score += EVENT_UPCOMING_BONUS;
    } else if (confidence === "usually_happens_tonight") {
      score += EVENT_RECURRING_TONIGHT_BONUS;
    } else if (confidence === "known_music_venue") {
      score += EVENT_KNOWN_VENUE_BONUS;
    }

    score += eventSourceTrustScore(item);
    score += eventSoonScore(item);

    if (!focused) {
      score *= 0.45;
    }

    return score;
  }

  function eventConfidenceLabel(item) {
    var confidence = eventConfidence(item);

    if (confidence === "confirmed_tonight") {
      return "LIVE TONIGHT";
    }
    if (confidence === "upcoming_event") {
      return "UPCOMING EVENT";
    }
    if (confidence === "usually_happens_tonight") {
      return "USUALLY HAPPENS TONIGHT";
    }
    if (confidence === "known_music_venue") {
      return "KNOWN MUSIC VENUE";
    }

    return "";
  }

  function eventTypeLabel(item) {
    var event = eventRecord(item);
    var type = norm(
      event && (
        event.eventType ||
        item.eventType ||
        ""
      )
    ).replace(/\s+/g, "_");

    if (type === "live_music") { return "LIVE MUSIC"; }
    if (type === "karaoke") { return "KARAOKE"; }
    if (type === "trivia") { return "TRIVIA NIGHT"; }
    if (type === "open_mic") { return "OPEN MIC"; }
    if (type === "dj") { return "DJ"; }
    if (type === "watch_party") { return "WATCH PARTY"; }
    if (type === "happy_hour") { return "HAPPY HOUR"; }
    if (type === "festival") { return "FESTIVAL"; }
    if (type === "comedy") { return "COMEDY"; }
    if (type === "show") { return "SHOW"; }

    return "EVENT";
  }

  function eventTimeText(item) {
    var event = eventRecord(item);
    var start = eventDate(event && event.startTime);
    var recurrence = clean(
      event && event.recurrenceText ||
      item && item.eventRecurrenceText ||
      ""
    );
    var options;

    if (start) {
      options = {
        hour: "numeric",
        minute: start.getMinutes() ? "2-digit" : undefined
      };

      try {
        return start.toLocaleTimeString([], options);
      } catch (error) {
        return start.toLocaleTimeString();
      }
    }

    return recurrence;
  }

  function eventShelfData(item) {
    var event = eventRecord(item);
    var confidence = eventConfidenceLabel(item);
    var eventTitle = clean(
      event && event.eventTitle ||
      item && item.eventTitle ||
      ""
    );
    var venue = clean(
      event && event.venueName ||
      item && (item.label || item.name) ||
      ""
    );
    var area = clean(
      event && event.floorOrArea ||
      item && item.eventFloorOrArea ||
      ""
    );
    var time = eventTimeText(item);

    if (!event && !confidence) {
      return null;
    }

    return {
      confidence: confidence,
      eventType: eventTypeLabel(item),
      title: eventTitle || eventTypeLabel(item),
      venue: venue,
      area: area,
      time: time
    };
  }

  function currentLocalHour() {
    return new Date().getHours();
  }

  function recommendationSignals(item, execution) {
    var textValue = itemText(item);
    var category = scanCategoryKey(
      execution && execution.category
    );
    var distance = distanceMilesValue(
      item,
      execution && execution.center
    );
    var rating = ratingValue(item);
    var reviews = reviewValue(item);
    var hour = currentLocalHour();
    var signals = [];

    function add(key, label, score, tone) {
      if (!key || signals.some(function (signal) {
        return signal.key === key;
      })) {
        return;
      }

      signals.push({
        key: key,
        label: label,
        score: Number(score || 0),
        tone: tone || "cool"
      });
    }

    if (
      distance !== null &&
      distance >= WORTH_DRIVE_MIN_MILES &&
      rating !== null &&
      rating >= WORTH_DRIVE_MIN_RATING &&
      reviews >= WORTH_DRIVE_MIN_REVIEWS &&
      (
        containsAny(textValue, [
          "iconic","landmark","destination","waterfront","beach",
          "historic","live music","local favorite","scenic",
          "award winning","must visit","signature"
        ]) ||
        Number(item.__categoryTruth && item.__categoryTruth.confidence || 0) >= 0.86
      )
    ) {
      add("worth_drive","Worth the Drive",18,"warm");
    }

    if (
      containsAny(textValue, [
        "local favorite","locally owned","family owned","family-owned",
        "neighborhood favorite","independent","local institution"
      ]) ||
      item.isLocal === true
    ) {
      add("local_favorite","Local Favorite",16,"cool");
    }

    if (
      containsAny(textValue, [
        "hidden gem","off the beaten path","easy to miss",
        "local secret","under the radar","lesser known"
      ])
    ) {
      add("hidden_gem","Hidden Gem",15,"cool");
    }

    if (
      containsAny(textValue, [
        "waterfront","beachfront","harbor","harbour","marina",
        "pier","boardwalk","water view","riverfront","lakefront"
      ])
    ) {
      add("waterfront","Waterfront",14,"cool");
    }

    if (
      containsAny(textValue, [
        "sunset","golden hour","sunrise","west-facing view"
      ])
    ) {
      add("sunset","Sunset Spot",13,"warm");
    }

    if (
      containsAny(textValue, [
        "romantic","date night","intimate","rooftop",
        "candlelight","couples"
      ])
    ) {
      add("date_night","Date Night",12,"warm");
    }

    if (
      containsAny(textValue, [
        "family friendly","family-friendly","kids","children",
        "playground","arcade","mini golf","aquarium","zoo"
      ])
    ) {
      add("family","Family Friendly",12,"cool");
    }

    if (
      containsAny(textValue, [
        "open late","late night","open until midnight",
        "open until 1","open until 2","after hours"
      ]) ||
      (
        hour >= 20 &&
        (item.openNow === true || item.isOpen === true) &&
        (
          category === "nightlife" ||
          category === "live_music" ||
          category === "dessert"
        )
      )
    ) {
      add("open_late","Open Late",11,"warm");
    }

    if (
      containsAny(textValue, [
        "tourist favorite","popular attraction","must see",
        "must-see","famous","iconic","signature destination"
      ]) ||
      (
        rating !== null &&
        rating >= 4.5 &&
        reviews >= 2500
      )
    ) {
      add("tourist_favorite","Tourist Favorite",10,"warm");
    }

    if (
      containsAny(textValue, [
        "local secret","locals know","locals love","neighborhood gem"
      ]) &&
      reviews < 1200
    ) {
      add("local_secret","Local Secret",14,"cool");
    }

    if (
      containsAny(textValue, [
        "indoor","museum","arcade","bowling","aquarium",
        "covered patio","indoor market"
      ])
    ) {
      add("rainy_day","Rainy Day Pick",8,"cool");
    }

    if (
      item.trending === true ||
      item.popularNow === true ||
      item.busyNow === true ||
      containsAny(textValue, [
        "trending","popular tonight","popular now","buzzing"
      ])
    ) {
      add("trending","Trending",15,"warm");
    }

    if (
      eventConfidence(item) === "confirmed_tonight"
    ) {
      add("live_now","Live Tonight",30,"cool");
    }

    signals.sort(function (a, b) {
      return b.score - a.score;
    });

    return signals;
  }

  function decisionIQScore(item, execution) {
    return Math.min(
      DECISION_SIGNAL_CAP,
      recommendationSignals(item, execution)
        .reduce(function (sum, signal) {
          return sum + Number(signal.score || 0);
        }, 0)
    );
  }

  function strongestDecisionSignal(item, execution) {
    var signals = recommendationSignals(item, execution);

    return signals.length ? signals[0] : null;
  }

  function decisionBadgeData(item, execution) {
    var signal;

    if (eventShelfData(item)) {
      return null;
    }

    signal = strongestDecisionSignal(item, execution);

    if (!signal) {
      return null;
    }

    return {
      label: signal.label,
      className: signal.tone === "warm"
        ? " warm"
        : ""
    };
  }

  function decisionWhyNow(item, execution) {
    var signal = strongestDecisionSignal(item, execution);
    var distance = distanceMilesValue(
      item,
      execution && execution.center
    );

    if (!signal) {
      return "";
    }

    if (signal.key === "worth_drive") {
      return distance !== null
        ? "Strong enough to justify the " + Math.round(distance) + "-mile drive"
        : "Strong enough to justify the extra drive";
    }

    if (signal.key === "local_favorite") {
      return "A stronger local choice than the familiar options";
    }

    if (signal.key === "hidden_gem" || signal.key === "local_secret") {
      return "A less obvious pick with real local character";
    }

    if (signal.key === "waterfront") {
      return "The setting is part of the experience";
    }

    if (signal.key === "sunset") {
      return "Best when the light and views matter";
    }

    if (signal.key === "date_night") {
      return "A better fit when atmosphere matters";
    }

    if (signal.key === "family") {
      return "A practical pick for a family outing";
    }

    if (signal.key === "open_late") {
      return "Still makes sense later in the evening";
    }

    if (signal.key === "tourist_favorite") {
      return "A proven destination experience";
    }

    if (signal.key === "rainy_day") {
      return "A useful indoor option when weather changes plans";
    }

    if (signal.key === "trending") {
      return "Getting attention right now";
    }

    return "";
  }

  function experienceSignals(item) {
    var textValue = itemText(item);
    var signals = [];

    function add(name, terms) {
      if (containsAny(textValue, terms)) {
        signals.push(name);
      }
    }

    add("live_music", [
      "live music","music venue","concert","honky tonk","blues",
      "jazz","band","stage"
    ]);
    add("historic", [
      "historic","landmark","institution","established","since "
    ]);
    add("patio", ["patio","courtyard","outdoor seating"]);
    add("rooftop", ["rooftop","roof top"]);
    add("waterfront", [
      "waterfront","riverfront","harbor","marina","beachfront"
    ]);
    add("local", [
      "local favorite","family owned","family-owned","independent",
      "neighborhood","chef owned","chef-owned"
    ]);
    add("night_energy", [
      "nightlife","late night","dance","cocktail","lounge"
    ]);
    add("scenic", [
      "scenic","overlook","view","sunset","water view"
    ]);
    add("family", [
      "family friendly","family-friendly","kids","children"
    ]);

    return signals;
  }

  function experienceSignalScore(item, category) {
    var signals = experienceSignals(item);
    var score = signals.length * 4;
    var key = scanCategoryKey(category);

    if (
      key === "live_music" &&
      signals.indexOf("live_music") >= 0
    ) {
      score += 12;
    }

    if (
      key === "nightlife" &&
      (
        signals.indexOf("night_energy") >= 0 ||
        signals.indexOf("rooftop") >= 0
      )
    ) {
      score += 10;
    }

    if (
      key === "outdoors" &&
      (
        signals.indexOf("scenic") >= 0 ||
        signals.indexOf("waterfront") >= 0
      )
    ) {
      score += 10;
    }

    if (
      key === "local_faves" &&
      (
        signals.indexOf("local") >= 0 ||
        signals.indexOf("historic") >= 0
      )
    ) {
      score += 10;
    }

    return Math.min(
      EXPERIENCE_SIGNAL_CAP,
      score
    );
  }

  function districtAnchorScore(item, execution) {
    var textValue = itemText(item);
    var centerLabel = norm(
      execution &&
      execution.center &&
      execution.center.label
    );
    var score = 0;

    if (
      item &&
      (
        item.insideDistrict === true ||
        item.districtMatch === true ||
        item.isDistrictAnchor === true
      )
    ) {
      score += DISTRICT_ANCHOR_BONUS;
    }

    if (
      centerLabel &&
      textValue.indexOf(centerLabel) > -1
    ) {
      score += 8;
    }

    if (
      containsAny(textValue, [
        "landmark","historic institution","iconic","flagship",
        "music hall","blues club","honky tonk","market hall"
      ])
    ) {
      score += 7;
    }

    return Math.min(24, score);
  }

  function categoryContextScore(item, category) {
    var key = scanCategoryKey(category);
    var textValue = itemText(item);
    var score = 0;

    if (key === "food") {
      if (containsAny(textValue, [
        "regional specialty","chef owned","chef-owned","local favorite",
        "barbecue","bbq","seafood","soul food","cajun","creole"
      ])) {
        score += 12;
      }

      /*
        Destin Institution Authority:
        recognized local destination restaurants should outrank generic
        regional concepts when the provider successfully returns them.
      */
      if (containsAny(textValue, [
        "boschamps","bo shamps","dewey destin","harbor docks",
        "louisiana lagniappe","brotula","marina cafe",
        "the back porch","pompano joe","camille's at crystal beach",
        "camilles at crystal beach"
      ])) {
        score += 46;
      }
    } else if (key === "coffee") {
      if (containsAny(textValue, [
        "espresso","roaster","roastery","specialty coffee",
        "independent coffee","single origin"
      ])) {
        score += 14;
      }
    } else if (key === "live_music") {
      if (containsAny(textValue, [
        "live music","music venue","blues club","jazz club",
        "honky tonk","concert"
      ])) {
        score += 16;
      }
    } else if (key === "nightlife") {
      if (containsAny(textValue, [
        "cocktail","lounge","rooftop","dance","late night"
      ])) {
        score += 13;
      }
    } else if (key === "waterfront") {
      if (containsAny(textValue, [
        "waterfront","beachfront","harbor","harbour","marina",
        "boardwalk","pier","beach access","water view"
      ])) {
        score += 18;
      }
    } else if (key === "family_iq") {
      if (containsAny(textValue, [
        "aquarium","zoo","mini golf","arcade","playground",
        "children's museum","water park","theme park","bowling"
      ])) {
        score += 16;
      }
    } else if (key === "dessert") {
      if (containsAny(textValue, [
        "ice cream","gelato","bakery","donut","doughnut",
        "cupcake","chocolate","dessert"
      ])) {
        score += 16;
      }
    } else if (key === "outdoors") {
      if (containsAny(textValue, [
        "trail","waterfall","scenic overlook","nature preserve",
        "beach","lake","river"
      ])) {
        score += 14;
      }
    } else if (key === "hidden_gems") {
      if (containsAny(textValue, [
        "hidden","independent","off the beaten path","neighborhood",
        "historic","small batch"
      ])) {
        score += 14;
      }
    }

    return score;
  }

  function categoryTravelTolerance(category) {
    var key = scanCategoryKey(category);

    if (
      key === "coffee" ||
      key === "dessert" ||
      key === "breakfast" ||
      key === "pharmacy"
    ) {
      return 0.70;
    }

    if (
      key === "live_music" ||
      key === "events" ||
      key === "tonight" ||
      key === "karaoke" ||
      key === "trivia" ||
      key === "open_mic" ||
      key === "watch_party" ||
      key === "happy_hour" ||
      key === "dj" ||
      key === "hidden_gems" ||
      key === "outdoors" ||
      key === "waterfront" ||
      key === "family_iq" ||
      key === "nightlife"
    ) {
      return 1.35;
    }

    return 1.0;
  }

  function proximityValueScore(item, execution) {
    var distance = distanceMilesValue(
      item,
      execution && execution.center
    );
    var category = execution && execution.category;
    var tolerance = categoryTravelTolerance(category);
    var quality = 0;
    var rating = ratingValue(item);
    var reviews = reviewValue(item);

    if (distance === null) {
      return 0;
    }

    if (rating !== null) {
      quality += Math.max(0, rating - 3.8) * 8;
    }

    quality += Math.min(8, Math.log(reviews + 1) * 1.2);

    if (distance <= 0.75 * tolerance) {
      return 14 + quality;
    }

    if (distance <= 2.5 * tolerance) {
      return 8 + quality;
    }

    if (distance <= 7 * tolerance) {
      return 2 + quality;
    }

    return Math.max(
      -12,
      quality - (distance / tolerance)
    );
  }

  function experiencePrimaryKey(item, category) {
    var signals = experienceSignals(item);
    var key = scanCategoryKey(category);
    var textValue = itemText(item);
    var event = eventRecord(item);

    if (eventFocusedCategory(key) && event) {
      return "event:" + norm(
        event.eventType ||
        item.eventType ||
        event.eventTitle ||
        "event"
      );
    }

    if (key === "food") {
      if (/bbq|barbecue/.test(textValue)) { return "bbq"; }
      if (/seafood/.test(textValue)) { return "seafood"; }
      if (/pizza|italian/.test(textValue)) { return "italian"; }
      if (/taco|mexican/.test(textValue)) { return "mexican"; }
      if (/burger/.test(textValue)) { return "burger"; }
      if (/breakfast|brunch/.test(textValue)) { return "breakfast"; }
    }

    if (key === "live_music") {
      if (/blues/.test(textValue)) { return "blues"; }
      if (/jazz/.test(textValue)) { return "jazz"; }
      if (/honky tonk/.test(textValue)) { return "honky_tonk"; }
      if (/concert|music hall/.test(textValue)) { return "concert"; }
    }

    return signals.length
      ? signals[0]
      : norm(
          item &&
          (
            item.userFacingCategory ||
            item.category ||
            item.primaryCategory ||
            "place"
          )
        );
  }

  function curatedBatch(candidates, mode, reference, limitOverride) {
    var remaining = (candidates || []).slice();
    var selected = [];
    var category = requestedCategoryKey();
    var batchLimit = Math.max(
      1,
      Number(
        limitOverride ||
        (mode === "top"
          ? Math.min(INITIAL_SHELF_MAX_RESULTS, remaining.length)
          : PRIMARY_RECOMMENDATION_COUNT)
      )
    );

    function baseModeScore(item) {
      if (mode === "closer") {
        var distance = distanceForRecommendation(item);
        return (
          Number(item.__scanScore || 0) -
          Math.min(50, distance * 7)
        );
      }

      if (mode === "local") {
        return localSignalScore(item);
      }

      if (mode === "similar" && reference) {
        return similarityScore(item, reference);
      }

      return Number(item.__scanScore || 0);
    }

    while (
      remaining.length &&
      selected.length < batchLimit
    ) {
      var bestIndex = 0;
      var bestScore = -Infinity;

      remaining.forEach(function (item, index) {
        var score = baseModeScore(item);
        var key = experiencePrimaryKey(item, category);
        var duplicateCount = 0;
        var brand = normalizedNameForSimilarity(item);

        selected.forEach(function (existing) {
          if (
            experiencePrimaryKey(existing, category) === key
          ) {
            duplicateCount += 1;
          }

          if (
            brand &&
            brand === normalizedNameForSimilarity(existing)
          ) {
            duplicateCount += 2;
          }
        });

        score += duplicateCount === 0
          ? BATCH_VARIETY_BONUS
          : -(duplicateCount * BATCH_DUPLICATE_PENALTY);

        /*
          Keep a small exploration lane: a high-confidence item with a
          different experience can beat a slightly higher raw score.
        */
        if (
          duplicateCount === 0 &&
          item.__categoryTruth &&
          Number(item.__categoryTruth.confidence || 0) >= 0.78
        ) {
          score += 5;
        }

        if (score > bestScore) {
          bestScore = score;
          bestIndex = index;
        }
      });

      selected.push(
        remaining.splice(bestIndex, 1)[0]
      );
    }

    return selected;
  }

  function localSignalScore(item) {
    var textValue = itemText(item);
    var score = Number(item && item.__scanScore || 0);

    if (
      containsAny(textValue, [
        "local","independent","family owned","family-owned",
        "neighborhood","historic","institution","landmark",
        "roaster","chef owned","chef-owned"
      ])
    ) {
      score += 30;
    }

    score -= brandPenalty(item) * 1.5;

    return score;
  }

  function distanceForRecommendation(item) {
    var execution = lastRecommendationExecution || {};
    var distance = distanceMilesValue(
      item,
      execution.center || null
    );

    return distance == null
      ? Number.MAX_SAFE_INTEGER
      : distance;
  }

  function similarityScore(item, reference) {
    var score = Number(item && item.__scanScore || 0);
    var itemCategory = norm(
      item && (
        item.userFacingCategory ||
        item.category ||
        item.primaryCategory ||
        ""
      )
    );
    var referenceCategory = norm(
      reference && (
        reference.userFacingCategory ||
        reference.category ||
        reference.primaryCategory ||
        ""
      )
    );

    if (
      referenceCategory &&
      itemCategory === referenceCategory
    ) {
      score += 35;
    }

    if (
      reference &&
      normalizedNameForSimilarity(item) ===
        normalizedNameForSimilarity(reference)
    ) {
      score -= 50;
    }

    return score;
  }

  function normalizedNameForSimilarity(item) {
    return norm(
      item && (
        item.brand ||
        item.chain ||
        item.parentBrand ||
        item.label ||
        item.name ||
        ""
      )
    );
  }


  function setCalmContinuationLoading(active, mode) {
    var shelf = document.getElementById(RESULT_SHELF_ID);
    var selectedMode = String(mode || refillMode || "another");
    var selectedButton;
    var track;
    var loadingLabel =
      selectedMode === "local"
        ? "Finding local picks…"
        : selectedMode === "closer"
          ? "Finding closer picks…"
          : selectedMode === "similar"
            ? "Finding similar picks…"
            : "Finding more…";

    calmContinuationLoadingActive = active === true;

    if (!shelf) {
      return false;
    }

    shelf.setAttribute(
      "data-diq-calm-continuation",
      active ? "1" : "0"
    );
    shelf.setAttribute(
      "aria-busy",
      active ? "true" : "false"
    );

    track = shelf.querySelector(".diqV600ShelfTrack");

    if (active && track) {
      refillShelfScrollLeft = Number(track.scrollLeft || 0);
      refillShelfScrollTop = Number(track.scrollTop || 0);
    }

    if (active) {
      refillLoadingButtonMode = selectedMode;
      selectedButton = shelf.querySelector(
        "[data-diq-batch='" + selectedMode + "']"
      );

      if (!selectedButton) {
        selectedButton = shelf.querySelector(
          "[data-diq-batch='another']"
        );
      }

      if (selectedButton) {
        refillLoadingButtonOriginalLabel =
          selectedButton.textContent || "Show More Picks";
        selectedButton.textContent = loadingLabel;
        selectedButton.disabled = true;
        selectedButton.setAttribute("aria-disabled", "true");
        selectedButton.setAttribute("data-refilling", "1");
      }

      return true;
    }

    selectedButton = shelf.querySelector(
      "[data-diq-batch='" +
      String(refillLoadingButtonMode || selectedMode || "another") +
      "']"
    );

    if (!selectedButton) {
      selectedButton = shelf.querySelector(
        "[data-diq-batch='another']"
      );
    }

    if (selectedButton) {
      selectedButton.textContent =
        refillLoadingButtonOriginalLabel ||
        (
          discoveryRoundsComplete(lastRecommendationExecution)
            ? "Explored"
            : selectedButton.getAttribute("data-diq-batch") === "another"
              ? "Show More Picks"
              : selectedButton.getAttribute("data-diq-batch") === "local"
                ? "More Local"
                : selectedButton.getAttribute("data-diq-batch") === "closer"
                  ? "Closer"
                  : "Similar"
        );

      selectedButton.disabled =
        selectedButton.getAttribute("data-diq-batch") === "another" &&
        discoveryRoundsComplete(lastRecommendationExecution);

      selectedButton.setAttribute(
        "aria-disabled",
        selectedButton.disabled ? "true" : "false"
      );
      selectedButton.removeAttribute("data-refilling");
    }

    if (track && refillShelfScrollLeft > 0) {
      track.scrollLeft = refillShelfScrollLeft;
    }
    if (track && refillShelfScrollTop > 0) {
      track.scrollTop = refillShelfScrollTop;
    }

    refillLoadingButtonMode = "";
    refillLoadingButtonOriginalLabel = "";
    refillShelfScrollLeft = 0;
    refillShelfScrollTop = 0;

    return true;
  }

  function stopRefillShelfGuard() {
    if (refillShelfGuardTimer) {
      window.clearInterval(refillShelfGuardTimer);
      refillShelfGuardTimer = null;
    }

    return true;
  }

  function startRefillShelfGuard() {
    stopRefillShelfGuard();

    refillShelfGuardTimer = window.setInterval(function () {
      if (!refillInProgress) {
        stopRefillShelfGuard();
        return;
      }

      if (
        !destinationEditingActive() &&
        !placeModeActive() &&
        !resultShelfVisible() &&
        lastUsableShelfBatch.length
      ) {
        restoreLastUsableShelf(
          "v600.68-refill-shelf-guard"
        );
        setCalmContinuationLoading(
          true,
          refillMode || "another"
        );
      }
    }, 240);

    return true;
  }

  function clearRefillWatchdog() {
    if (refillWatchdogTimer) {
      window.clearTimeout(refillWatchdogTimer);
      refillWatchdogTimer = null;
    }

    return true;
  }

  function releaseRefillState(reason) {
    var releasedMode = refillMode;

    clearRefillWatchdog();
    stopRefillShelfGuard();
    refillInProgress = false;
    refillRequestedAt = 0;
    calmContinuationLoadingActive = false;
    setCalmContinuationLoading(false, releasedMode);

    if (
      !destinationEditingActive() &&
      !placeModeActive() &&
      lastUsableShelfBatch.length &&
      !resultShelfVisible()
    ) {
      restoreLastUsableShelf(
        "refill-released-" + String(reason || "released")
      );
    }

    try {
      events.emit("recommendations:refill-released", {
        reason: String(reason || "released"),
        mode: refillMode,
        sessionKey: activeRecommendationSessionKey,
        remainingCount: recommendationRemainingCount()
      });
    } catch (error) {}

    return true;
  }

  function recommendationKeySet(items) {
    var keys = {};

    (items || []).forEach(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      if (key) {
        keys[key] = true;
      }
    });

    return keys;
  }

  function newRecommendationCountSinceRefill() {
    var count = 0;

    recommendationPool.forEach(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      if (
        key &&
        !refillBaselineKeys[key] &&
        !shownRecommendationKeys[key]
      ) {
        count += 1;
      }
    });

    return count;
  }

  function rememberUsableShelf(items, mode) {
    if (Array.isArray(items) && items.length) {
      lastUsableShelfBatch = items.slice();
      lastUsableShelfMode = String(mode || shelfBatchMode || "top");
    }
  }

  function restoreLastUsableShelf(reason) {
    if (
      refillInProgress &&
      resultShelfVisible()
    ) {
      setCalmContinuationLoading(
        true,
        refillMode || refillLoadingButtonMode || "another"
      );
      return true;
    }

    if (destinationEditingActive()) {
      suspendShelfForDestinationEditing(
        reason || "last-usable-blocked-destination-editing"
      );
      return false;
    }

    if (!lastUsableShelfBatch.length) {
      return restoreCurrentShelf(reason || "last-usable-fallback");
    }

    return renderResultShelf(lastUsableShelfBatch.slice(), {
      decisionMode: lastUsableShelfMode,
      totalCount: recommendationPool.length,
      remainingCount: recommendationRemainingCount(),
      restoreReason: String(reason || "last-usable-fallback")
    });
  }

  function startRefillWatchdog(executionToken) {
    clearRefillWatchdog();

    refillWatchdogTimer = window.setTimeout(function () {
      if (!refillInProgress) {
        return;
      }

      clearRefillExhaustionTimer();
      refillSettlementGeneration += 1;

      /*
        A refill must never strand the UI. If the provider or scan lifecycle
        fails to release the state, unlock the controls and restore the last
        usable recommendation batch.
      */
      if (
        activeExecution &&
        executionToken &&
        activeExecution.token === executionToken
      ) {
        try {
          cancel("refill-watchdog-timeout");
        } catch (cancelError) {
          activeExecution = null;
          releaseRefillState("watchdog-cancel-error");
        }
      } else {
        releaseRefillState("watchdog-timeout");
      }

      restoreLastUsableShelf("refill-watchdog-timeout");

      try {
        if (
          window.DriveIQAdventureUI &&
          typeof window.DriveIQAdventureUI.showToast === "function"
        ) {
          window.DriveIQAdventureUI.showToast(
            "Couldn’t find more right now. Your current picks are still available."
          );
        }
      } catch (uiError) {}
    }, REFILL_WATCHDOG_MS);
  }

  function refillAttemptCount() {
    return Number(
      refillAttemptsBySession[activeRecommendationSessionKey] || 0
    );
  }

  function setRefillAttemptCount(value) {
    if (!activeRecommendationSessionKey) {
      return;
    }

    refillAttemptsBySession[activeRecommendationSessionKey] =
      Math.max(0, Number(value || 0));
  }

  function refillQueryForMode(mode, execution, reference) {
    var base = clean(
      execution && execution.query ||
      execution && execution.category ||
      ""
    );
    var selectedMode = String(mode || "another");
    var referenceName = clean(
      reference && (
        reference.name ||
        reference.label
      )
    );
    var referenceCategory = clean(
      reference && (
        reference.userFacingCategory ||
        reference.category ||
        reference.primaryCategory
      )
    );

    if (selectedMode === "local") {
      return clean(
        base +
        " local independent family owned neighborhood favorite"
      );
    }

    if (selectedMode === "closer") {
      return clean(
        base +
        " nearest nearby walkable"
      );
    }

    if (selectedMode === "similar") {
      return clean(
        base + " " +
        referenceCategory + " " +
        referenceName
      );
    }

    return clean(
      base +
      " popular iconic local favorite"
    );
  }

  function refillRadiusForAttempt(execution, attempt) {
    var base = Number(
      execution && execution.radiusMeters ||
      config.scan.defaultRadiusMeters
    );

    if (attempt <= 1) {
      return Math.max(base, Number(config.scan.defaultRadiusMeters || base));
    }

    if (attempt === 2) {
      return Math.max(base, Number(config.scan.widerRadiusMeters || base));
    }

    return Math.max(base, Number(config.scan.maximumRadiusMeters || base));
  }

  function initialDepthCategoryEligible(category) {
    var key = scanCategoryKey(category);

    return (
      key === "food" ||
      key === "live_music" ||
      key === "nightlife" ||
      key === "local_faves" ||
      key === "local_favorites" ||
      key === "trending" ||
      key === "hidden_gems" ||
      key === "worth_the_drive"
    );
  }

  function initialDepthSessionKey(execution) {
    return recommendationContextKey(execution || {});
  }

  function shouldRunInitialDepthBootstrap(execution, resultCount) {
    var key;

    if (
      !execution ||
      execution.isRefill === true ||
      execution.initialDepthBootstrap === true ||
      !initialDepthCategoryEligible(execution.category) ||
      Number(resultCount || 0) < 1 ||
      Number(resultCount || 0) > INITIAL_DEPTH_BOOTSTRAP_THRESHOLD
    ) {
      return false;
    }

    key = initialDepthSessionKey(execution);

    return !!(
      key &&
      initialDepthBootstrapSessions[key] !== true
    );
  }

  function startInitialDepthBootstrap(execution) {
    var key;

    if (!shouldRunInitialDepthBootstrap(
      execution,
      recommendationPool.length
    )) {
      return false;
    }

    key = initialDepthSessionKey(execution);
    initialDepthBootstrapSessions[key] = true;

    refillBaselineKeys = recommendationKeySet(
      recommendationPool
    );

    window.setTimeout(function () {
      if (
        activeExecution ||
        !lastRecommendationExecution ||
        recommendationContextKey(
          lastRecommendationExecution
        ) !== key
      ) {
        return;
      }

      start({
        category: execution.category,
        scope: execution.scope,
        center: execution.center,
        radiusMeters: execution.radiusMeters,
        query: canonicalProviderQuery(
          execution.category,
          2
        ),
        refillMode: "initial_depth",
        refillAttempt: 1,
        preserveResults: true,
        calmContinuation: true,
        initialDepthBootstrap: true
      });
    }, INITIAL_DEPTH_BOOTSTRAP_DELAY_MS);

    try {
      events.emit("recommendations:initial-depth-started", {
        sessionKey: key,
        category: execution.category,
        initialCount: recommendationPool.length
      });
    } catch (error) {}

    return true;
  }

  function requestRecommendationRefill(mode, reference) {
    var execution = lastRecommendationExecution;
    var attempt;

    if (
      refillInProgress ||
      !execution ||
      !activeRecommendationSessionKey
    ) {
      return false;
    }

    if (discoveryRoundsComplete(execution)) {
      return emitRecommendationExhausted(mode);
    }

    attempt = refillAttemptCount() + 1;

    if (attempt > MAX_REFILL_ATTEMPTS) {
      return emitRecommendationExhausted(mode);
    }

    clearRefillExhaustionTimer();
    refillSettlementGeneration += 1;
    refillInProgress = true;
    refillMode = String(mode || "another");
    refillReference = reference || currentSelectedResult();
    refillBaselineKeys = recommendationKeySet(recommendationPool);
    refillRequestedAt = now();
    setRefillAttemptCount(attempt);

    try {
      events.emit("recommendations:refill-started", {
        mode: refillMode,
        attempt: attempt,
        sessionKey: activeRecommendationSessionKey,
        shownCount: Object.keys(shownRecommendationKeys).length,
        currentPoolCount: recommendationPool.length
      });
    } catch (error) {}

    /*
      Calm Continuation Authority:
      keep the current cards and map completely stable. The shelf itself
      communicates loading; no global toast or scan overlay is needed.
    */
    if (!resultShelfVisible()) {
      restoreLastUsableShelf("refill-start-visible");
    }

    setCalmContinuationLoading(true, refillMode);
    startRefillShelfGuard();

    var refillToken = start({
      category: execution.category,
      scope: execution.scope,
      center: execution.center,
      radiusMeters: refillRadiusForAttempt(
        execution,
        attempt
      ),
      query: refillQueryForMode(
        refillMode,
        execution,
        refillReference
      ),
      refillMode: refillMode,
      refillAttempt: attempt,
      preserveResults: true,
      calmContinuation: true
    });

    startRefillWatchdog(refillToken);
    return true;
  }

  function batchFromPool(mode, reference, options) {
    var candidates = unseenRecommendations(
      recommendationPool
    );
    var selectedMode = String(mode || "another");
    var supplied = options || {};
    var allowRefill = supplied.allowRefill === true;

    /*
      Continuation Authority:
      Never hide one or two valid unseen recommendations just because the
      ideal batch size is three. Serve every trustworthy unseen place first.
      Refill only when the committed pool has genuinely reached zero unseen.
    */
    if (!candidates.length) {
      if (
        allowRefill &&
        selectedMode !== "top" &&
        refillAttemptCount() < MAX_REFILL_ATTEMPTS
      ) {
        requestRecommendationRefill(
          selectedMode,
          reference
        );
        return [];
      }

      emitRecommendationExhausted(selectedMode);
      return [];
    }

    if (selectedMode === "closer") {
      candidates.sort(function (a, b) {
        var distanceDelta =
          distanceForRecommendation(a) -
          distanceForRecommendation(b);

        if (distanceDelta !== 0) {
          return distanceDelta;
        }

        return Number(b.__scanScore || 0) -
          Number(a.__scanScore || 0);
      });
    } else if (selectedMode === "local") {
      candidates.sort(function (a, b) {
        return localSignalScore(b) -
          localSignalScore(a);
      });
    } else if (selectedMode === "similar" && reference) {
      candidates.sort(function (a, b) {
        return similarityScore(b, reference) -
          similarityScore(a, reference);
      });
    } else {
      candidates.sort(function (a, b) {
        return Number(b.__scanScore || 0) -
          Number(a.__scanScore || 0);
      });
    }

    shelfBatch = curatedBatch(
      candidates,
      selectedMode,
      reference,
      selectedMode === "top"
        ? Math.min(INITIAL_SHELF_MAX_RESULTS, candidates.length)
        : Math.min(DISCOVERY_ROUND_RESULT_COUNT, candidates.length)
    );
    shelfBatchMode = selectedMode;
    shelfBatchCursor += shelfBatch.length;
    recommendationExhausted = false;
    markBatchShown(shelfBatch);
    rememberUsableShelf(shelfBatch, selectedMode);

    return shelfBatch.slice();
  }

  function currentSelectedResult() {
    return (
      state.get("placeCard.item", null) ||
      state.get("selectedPlace", null) ||
      state.get("place.selected", null) ||
      null
    );
  }

  function showRecommendationBatch(mode, reference) {
    if (refillInProgress) {
      setCalmContinuationLoading(
        true,
        refillMode || mode || "another"
      );
      return false;
    }

    if (destinationEditingActive()) {
      suspendShelfForDestinationEditing(
        "recommendation-batch-destination-editing"
      );
      return false;
    }

    var batch = batchFromPool(
      mode,
      reference || currentSelectedResult(),
      {
        allowRefill: true,
        source: "user-action"
      }
    );

    if (!batch.length) {
      if (refillInProgress) {
        return restoreLastUsableShelf("refill-started");
      }

      return false;
    }

    return renderResultShelf(batch, {
      decisionMode: String(mode || "another"),
      totalCount: recommendationPool.length,
      remainingCount: recommendationRemainingCount()
    });
  }

  function renderResultShelf(items, options) {
    var shelf;
    var supplied = options || {};

    if (
      refillInProgress &&
      resultShelfVisible() &&
      supplied.atomicRefillCommit !== true
    ) {
      setCalmContinuationLoading(
        true,
        refillMode || supplied.decisionMode || "another"
      );
      return true;
    }

    if (destinationEditingActive()) {
      suspendShelfForDestinationEditing(
        supplied.restoreReason ||
        supplied.reason ||
        "render-blocked-destination-editing"
      );
      return false;
    }

    if (placeModeActive()) {
      minimizeResultShelf("place-mode");
      return false;
    }

    shelf = ensureResultShelf();
    var list = Array.isArray(items) ? items : [];
    var track;
    var closeButton;

    var oldTrack = shelf.querySelector(".diqV600ShelfTrack");
    var previousScrollTop = oldTrack ? Number(oldTrack.scrollTop || 0) : retainedShelfScrollTop;
    var firstKey = list.length ? resultPhotoKey(list[0]) : "";
    var preservePosition = firstKey && firstKey === retainedShelfFirstKey;
    if (oldTrack) retainedShelfScrollTop = previousScrollTop;
    shelf.innerHTML = "";

    if (!list.length) {
      if (lastUsableShelfBatch.length) {
        return restoreLastUsableShelf("empty-render-guard");
      }

      minimizeResultShelf("empty-render-guard");
      return false;
    }

    var totalCount = Number(
      supplied.totalCount ||
      recommendationPool.length ||
      state.get("results.count", list.length) ||
      list.length
    );
    var modeLabel = String(
      supplied.decisionMode ||
      shelfBatchMode ||
      "top"
    );

    setShelfState({
      visible: true,
      minimized: false,
      userClosed: false,
      explicitOpen: true,
      available: true
    }, supplied.restoreReason || supplied.reason || "rendered");

    shelf.innerHTML =
      '<div class="diqV600ShelfHeader">' +
        '<span>' +
          (modeLabel === "top"
            ? "TOP PICKS"
            : modeLabel === "closer"
              ? "CLOSER PICKS"
              : modeLabel === "local"
                ? "MORE LOCAL"
                : modeLabel === "similar"
                  ? "SIMILAR PICKS"
                  : "FRESH PICKS") +
          ' • ' +
          String(list.length) +
          (modeLabel === "top" ? ' PLACES' : ' NEW') +
        '</span>' +
        '<button class="diqV600ShelfClose" type="button" aria-label="Close results">&times;</button>' +
      '</div>' +
      '<div class="diqV600ShelfTrack"></div>' +
      '<div class="diqV600ShelfRefineLabel">KEEP EXPLORING</div>' +
      '<div class="diqV600ShelfActions" aria-label="Refine Adventure results">' +
        '<button class="diqV600ShelfAction" type="button" data-diq-batch="another"' +
          (
            refillInProgress || discoveryRoundsComplete(lastRecommendationExecution)
              ? ' disabled aria-disabled="true"' +
                (refillInProgress ? ' data-refilling="1"' : '')
              : ''
          ) +
          '>' +
          (
            discoveryRoundsComplete(lastRecommendationExecution)
              ? 'Explored'
              : refillInProgress
                ? 'Finding more…'
                : 'Show More Picks'
          ) +
        '</button>' +
        '<button class="diqV600ShelfAction" type="button" data-diq-batch="closer"' +
          (refillInProgress ? ' disabled aria-disabled="true"' : '') +
          '>Closer</button>' +
        '<button class="diqV600ShelfAction" type="button" data-diq-batch="local"' +
          (refillInProgress ? ' disabled aria-disabled="true"' : '') +
          '>More Local</button>' +
        '<button class="diqV600ShelfAction" type="button" data-diq-batch="similar"' +
          (refillInProgress ? ' disabled aria-disabled="true"' : '') +
          '>Similar</button>' +
      '</div>';

    track = shelf.querySelector(".diqV600ShelfTrack");
    closeButton = shelf.querySelector(".diqV600ShelfClose");

    list.forEach(function (item, index) {
      var card = document.createElement("button");
      var photo = resultPhoto(item);
      var photoKey = resultPhotoKey(item);
      var photoElement;
      var reason = String(
        item.recommendedBecause ||
        item.why ||
        item.reason ||
        "Worth exploring near this destination."
      );
      var category = String(
        item.userFacingCategory ||
        item.category ||
        item.primaryCategory ||
        "PLACE IQ"
      );
      var distance = String(
        item.distanceText ||
        item.distanceLabel ||
        item.distanceMiles ||
        ""
      );

      var eventData = eventShelfData(item);
      var decisionBadge = decisionBadgeData(
        item,
        lastRecommendationExecution || {}
      );
      var badgeClass = eventConfidence(item) === "confirmed_tonight"
        ? " confirmed"
        : "";

      card.type = "button";
      card.className = "diqV600ShelfCard";
      card.setAttribute("data-result-index", String(index));
      card.innerHTML =
        '<span class="diqV600ShelfPhoto diqV600ResultPhoto" ' +
          'data-diq-result-photo="1" ' +
          'data-photo-role="result" ' +
          'data-photo-place-key="' + escapeHTML(photoKey) + '"' +
          (photo
            ? ' style="background-image:url(&quot;' + escapeHTML(photo) + '&quot;)"'
            : "") +
        '></span>' +
        '<span>' +
          (
            eventData
              ? (
                  '<span class="diqV600EventBadge' + badgeClass + '">' +
                    escapeHTML(eventData.confidence || eventData.eventType) +
                  '</span>' +
                  '<span class="diqV600EventTitle">' +
                    escapeHTML(
                      eventData.title +
                      (eventData.time ? " • " + eventData.time : "")
                    ) +
                  '</span>' +
                  '<span class="diqV600EventVenue">' +
                    escapeHTML(
                      eventData.venue +
                      (eventData.area ? " · " + eventData.area : "")
                    ) +
                  '</span>'
                )
              : (
                  (
                    decisionBadge
                      ? '<span class="diqV600DecisionBadge' +
                          decisionBadge.className + '">' +
                          escapeHTML(decisionBadge.label) +
                        '</span>'
                      : ''
                  ) +
                  '<span class="diqV600ShelfName">' +
                    escapeHTML(item.label || item.name || "Adventure Place") +
                  '</span>'
                )
          ) +
          '<span class="diqV600ShelfReason">' +
            escapeHTML(reason) +
          '</span>' +
          '<span class="diqV600ShelfMeta">' +
            escapeHTML(
              eventData
                ? (
                    eventData.eventType +
                    (distance ? " • " + distance : "")
                  )
                : (
                    category.toUpperCase() +
                    (distance ? " • " + distance : "")
                  )
            ) +
          '</span>' +
        '</span>';

      card.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        openResult(item);
      }, false);

      track.appendChild(card);

      photoElement = card.querySelector("[data-diq-result-photo]");
      hydrateResultThumbnail(item, photoElement);
    });

    if (closeButton) {
      closeButton.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        closeResultShelf("user-close");
      }, false);
    }

    /*
      V602.3 Refinement Touch Authority:
      the shelf is rebuilt after every batch, so bind one capture-level handler
      to the current shelf surface. This survives nested tap targets and prevents
      lower bottom-page overlays from swallowing the action.
    */
    if (!shelf.__diqRefinementAuthorityBound) {
      shelf.addEventListener("click", function (event) {
        var button = event.target && event.target.closest
          ? event.target.closest("[data-diq-batch]")
          : null;
        var mode;
        var nowValue;

        if (!button || !shelf.contains(button) || button.disabled) {
          return;
        }

        mode = String(button.getAttribute("data-diq-batch") || "another");
        nowValue = Date.now();

        if (
          shelf.__diqLastRefinementMode === mode &&
          nowValue - Number(shelf.__diqLastRefinementAt || 0) < 450
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        shelf.__diqLastRefinementMode = mode;
        shelf.__diqLastRefinementAt = nowValue;

        event.preventDefault();
        event.stopPropagation();
        showRecommendationBatch(mode);
      }, true);

      shelf.__diqRefinementAuthorityBound = true;
    }

    retainedShelfFirstKey = firstKey;
    retainedShelfScrollTop = preservePosition ? previousScrollTop : 0;
    if (track) {
      track.scrollTop = retainedShelfScrollTop;
      track.addEventListener("scroll", function () {
        retainedShelfScrollTop = Number(track.scrollTop || 0);
      }, { passive: true });
      if (preservePosition && retainedShelfScrollTop > 0) {
        window.requestAnimationFrame(function () {
          if (shelf.contains(track)) track.scrollTop = retainedShelfScrollTop;
        });
      }
    }

    rememberUsableShelf(list, modeLabel);

    shelf.className = "on";
    shelf.style.pointerEvents = "auto";
    shelf.setAttribute("aria-hidden", "false");
    scheduleResultsPillSync("shelf-rendered");

    setShelfState({
      visible: true,
      minimized: false,
      userClosed: false
    }, supplied.restoreReason || "rendered");

    startShelfWatchdog();

    try {
      events.emit("results:rendered", {
        results: list,
        shelfID: RESULT_SHELF_ID,
        decisionMode: modeLabel,
        totalCount: totalCount
      });

      events.emit("results:shelf-shown", {
        results: list,
        shelfID: RESULT_SHELF_ID,
        count: list.length,
        totalCount: totalCount,
        decisionMode: modeLabel
      });
    } catch (eventError) {}

    window.setTimeout(function () {
      try {
        if (
          resultShelfVisible() &&
          window.DriveIQAdventurePhoto &&
          typeof window.DriveIQAdventurePhoto.hydrateResultShelf === "function"
        ) {
          window.DriveIQAdventurePhoto.hydrateResultShelf();
        }
      } catch (photoError) {}
    }, 180);

    return true;
  }

  function prewarmPhotos(items) {
    try {
      if (
        window.DriveIQAdventurePhoto &&
        typeof window.DriveIQAdventurePhoto.prewarm === "function"
      ) {
        window.DriveIQAdventurePhoto.prewarm(
          Array.isArray(items) ? items : [],
          PHOTO_PREWARM_LIMIT
        );
      }
    } catch (error) {}
  }

  function repaintPins(items) {
    try {
      if (
        window.DriveIQAdventurePins &&
        typeof window.DriveIQAdventurePins.render === "function"
      ) {
        window.DriveIQAdventurePins.render(items || []);
        return true;
      }

      if (
        window.DriveIQAdventurePins &&
        typeof window.DriveIQAdventurePins.repaint === "function"
      ) {
        window.DriveIQAdventurePins.repaint();
        return true;
      }
    } catch (error) {}

    return false;
  }

  function paintCommittedResults(results) {
    var items = Array.isArray(results) ? results : [];
    var primary;

    repaintPins(items);
    prewarmPhotos(items);

    /*
      Decision Compression:
      commit and paint every trusted result as a map pin, but lead the shelf
      with only the strongest three recommendations.
    */
    if (!recommendationPool.length) {
      resetRecommendationSession(
        items,
        lastRecommendationExecution,
        {
          sessionKey: recommendationContextKey(
            lastRecommendationExecution
          ),
          preserveExisting: true
        }
      );
    }

    var paintMode =
      lastRecommendationExecution &&
      lastRecommendationExecution.isRefill === true &&
      lastRecommendationExecution.refillMode
        ? lastRecommendationExecution.refillMode
        : "top";

    /*
      Initial settlement authority:
      a completed first scan always paints Top Picks. It must never infer
      "another" from cursor state or launch an automatic refill.
    */
    if (paintMode === "top") {
      /*
        V600.66 Full Pool Shelf Authority:
        the first Places shelf represents the whole trusted result pool,
        not an artificial three-card teaser.
      */
      primary = recommendationPool
        .slice()
        .sort(function (a, b) {
          return Number(b && b.__scanScore || 0) -
            Number(a && a.__scanScore || 0);
        })
        .slice(0, INITIAL_SHELF_MAX_RESULTS);

      shelfBatch = primary.slice();
      shelfBatchMode = "top";
      shelfBatchCursor = primary.length;
      recommendationExhausted = primary.length >= recommendationPool.length;
      markBatchShown(primary);
      rememberUsableShelf(primary, "top");
    } else {
      primary = batchFromPool(
        paintMode,
        refillReference,
        {
          allowRefill: false,
          source: "refill-settlement"
        }
      );
    }

    if (primary.length) {
      rememberUsableShelf(primary, paintMode);

      if (paintMode === "top") {
        /*
          Map-First Authority:
          initial scan completion commits every pin and preserves the first
          recommendation batch, but does not force Top Picks over the map.
          The Places control is the only authority allowed to open it.
        */
        setShelfState({
          visible: false,
          minimized: true,
          userClosed: false,
          explicitOpen: false,
          available: true
        }, "initial-results-available");

        var shelf = document.getElementById(RESULT_SHELF_ID);

        if (shelf) {
          shelf.className = "";
          shelf.style.pointerEvents = "none";
          shelf.setAttribute("aria-hidden", "true");
        }

        stopShelfWatchdog();

        try {
          events.emit("results:shelf-available", {
            count: items.length,
            batchCount: primary.length,
            reason: "scan-completed-map-first"
          });
        } catch (error) {}
      } else {
        /*
          Refill actions are explicit user actions taken from an already-open
          shelf, so their new batch remains visible.
        */
        renderResultShelf(primary, {
          decisionMode: paintMode,
          totalCount: items.length,
          remainingCount: recommendationRemainingCount(),
          reason: "explicit-refill"
        });
      }
    } else if (paintMode !== "top") {
      restoreLastUsableShelf("refill-no-new-results");
    }

    window.setTimeout(function () {
      repaintPins(items);
    }, 120);

    window.setTimeout(function () {
      try {
        if (
          window.DriveIQAdventurePhoto &&
          typeof window.DriveIQAdventurePhoto.hydrateResultShelf === "function"
        ) {
          window.DriveIQAdventurePhoto.hydrateResultShelf();
        }
      } catch (error) {}
    }, 220);
  }

  function log() {
    if (
      !config.logging.enabled ||
      !window.console ||
      !window.console.log
    ) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift(config.logging.prefix, "[Scan]");
    window.console.log.apply(window.console, args);
  }

  function warn() {
    if (!window.console || !window.console.warn) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift(config.logging.prefix, "[Scan]");
    window.console.warn.apply(window.console, args);
  }

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }


  /* =========================================================
     DRIVE IQ — SCAN ENGINE 6.0
     CATEGORY TRUTH + RECOMMENDATION AUTHORITY
  ========================================================= */

  var CATEGORY_PROFILES = {
    wedding_venues: {
      label: "Wedding Venues",
      allow: [
        "wedding venue","wedding venues","wedding reception",
        "wedding chapel","wedding barn","bridal venue",
        "event venue","event space","event center","event centre",
        "event hall","banquet hall","reception hall","wedding estate",
        "wedding garden","wedding pavilion","wedding location",
        "special event venue","marriage venue"
      ],
      strong: [
        "wedding venue","wedding reception","wedding chapel",
        "wedding barn","wedding estate","bridal venue",
        "banquet hall","reception hall","event venue"
      ],
      block: [
        "restaurant","seafood restaurant","fast food","coffee shop",
        "grocery store","gas station","car wash","auto repair",
        "apartment complex","government office","storage",
        "wedding photographer","wedding planner","wedding dress",
        "bridal shop","florist","dj service","catering service",
        "equipment rental","party supply"
      ]
    },
    coffee: {
      label: "Coffee",
      allow: [
        "coffee","cafe","café","espresso","roaster","roastery",
        "tea house","coffee shop","bakery cafe","bakery café"
      ],
      strong: [
        "coffee","espresso","roaster","roastery","cafe","café"
      ],
      block: [
        "hard rock","restaurant","bar & grill","bar and grill",
        "steakhouse","seafood","sports bar","nightclub","hotel",
        "casino","gas station","convenience","grocery","supermarket",
        "pharmacy","hospital","church","school","office"
      ]
    },
    food: {
      label: "Food",
      allow: [
        "restaurant","grill","kitchen","bistro","diner","eatery",
        "bbq","barbecue","burger","pizza","taco","seafood","steakhouse",
        "cafe","café","bakery","food","deli","sandwich"
      ],
      strong: [
        "restaurant","grill","kitchen","bistro","diner","bbq",
        "barbecue","burger","pizza","taco","seafood","steakhouse"
      ],
      block: [
        "best buy","walmart","target","office depot","hotel","hospital",
        "church","school","bank","insurance","auto repair","car wash",
        "apartment","storage","parking"
      ]
    },
    dessert: {
      label: "Dessert",
      allow: [
        "dessert","ice cream","gelato","bakery","donut","doughnut",
        "cupcake","pastry","chocolate","candy","sweet shop","milkshake"
      ],
      strong: [
        "ice cream","gelato","dessert","bakery","donut","doughnut",
        "cupcake","chocolate"
      ],
      block: [
        "restaurant supply","office","school","church","bank","hospital",
        "storage","parking","gas station","convenience store"
      ]
    },
    local_faves: {
      label: "Local Favorite",
      allow: [
        "restaurant","cafe","café","coffee","bakery","bar","brewery",
        "market","public market","food hall","park","recreation",
        "pickleball","bowling","arcade","music","music venue",
        "theater","theatre","museum","district","tourism",
        "tourist attraction","visitor attraction","attraction",
        "historic site","historic district","heritage","cultural center",
        "performing arts","gallery","art gallery","arts center",
        "garden","botanical garden","landmark","monument",
        "waterfront","harbor","harbour","pier","boardwalk",
        "shop","boutique","independent retail","specialty shop"
      ],
      strong: [
        "local","family owned","independent","neighborhood","historic",
        "heritage","favorite","institution","landmark","iconic",
        "beloved","community favorite","longtime","signature"
      ],
      block: [
        "radio station","television station","corporate office",
        "warehouse","distribution center","storage","apartment",
        "government office","school district"
      ]
    },
    nightlife: {
      label: "Nightlife",
      allow: [
        "bar","pub","lounge","nightclub","club","brewery","taproom",
        "cocktail","live music","music venue","rooftop","dance"
      ],
      strong: [
        "nightclub","cocktail","live music","music venue","rooftop",
        "dance","late night"
      ],
      block: [
        "coffee","breakfast","daycare","school","church","hospital",
        "pharmacy","grocery","hotel lobby"
      ]
    },
    events: {
      label: "Events",
      allow: [
        "event","live music","concert","karaoke","trivia","open mic",
        "dj","watch party","happy hour","festival","comedy","show",
        "music venue","nightlife","theater","theatre"
      ],
      strong: [
        "confirmed tonight","upcoming event","live music","concert",
        "karaoke","trivia","open mic","dj","watch party","festival"
      ],
      block: [
        "music store","record store","radio station","school",
        "private studio","government office"
      ]
    },
    tonight: {
      label: "Tonight",
      allow: [
        "tonight","live music","concert","karaoke","trivia","open mic",
        "dj","watch party","happy hour","nightlife","bar","music venue"
      ],
      strong: [
        "confirmed tonight","live tonight","starts tonight","tonight at",
        "karaoke tonight","trivia tonight","dj tonight","watch party"
      ],
      block: [
        "music store","record store","radio station","school",
        "private studio","government office"
      ]
    },
    karaoke: {
      label: "Karaoke",
      allow: ["karaoke","bar","pub","lounge","nightlife","music venue"],
      strong: ["karaoke tonight","karaoke night","karaoke"],
      block: ["music store","record store","school","private studio"]
    },
    trivia: {
      label: "Trivia",
      allow: ["trivia","bar","pub","brewery","taproom","restaurant"],
      strong: ["trivia tonight","trivia night","pub quiz"],
      block: ["school","library program","private event"]
    },
    open_mic: {
      label: "Open Mic",
      allow: ["open mic","music venue","bar","pub","coffee","theater","theatre"],
      strong: ["open mic tonight","open mic night","open mic"],
      block: ["music store","school","private studio"]
    },
    watch_party: {
      label: "Watch Party",
      allow: ["watch party","sports bar","bar","pub","brewery","restaurant"],
      strong: ["watch party","game day","gameday","big screen"],
      block: ["electronics store","private residence","school"]
    },
    happy_hour: {
      label: "Happy Hour",
      allow: ["happy hour","bar","pub","cocktail","restaurant","rooftop"],
      strong: ["happy hour today","happy hour tonight","happy hour"],
      block: ["liquor store","grocery","private club"]
    },
    dj: {
      label: "DJ",
      allow: ["dj","dance","nightclub","club","bar","rooftop","lounge"],
      strong: ["dj tonight","dj set","dance party"],
      block: ["dj service","wedding service","equipment rental"]
    },
    live_music: {
      label: "Live Music",
      allow: [
        "live music","music venue","concert","bar","pub","brewery",
        "honky tonk","jazz","blues","rooftop","theater","theatre"
      ],
      strong: [
        "live music","music venue","concert","honky tonk","jazz","blues"
      ],
      block: [
        "music store","record store","radio station","school",
        "church","private studio"
      ]
    },
    waterfront: {
      label: "Water",
      allow: [
        "waterfront","beachfront","oceanfront","riverfront","lakefront",
        "harbor","harbour","marina","boardwalk","pier","beach access",
        "boat ramp","fishing pier","water view","beach","bay","gulf",
        "river","lake","island","water park"
      ],
      strong: [
        "waterfront","beachfront","oceanfront","riverfront","lakefront",
        "harbor","harbour","marina","boardwalk","pier","beach access",
        "boat ramp","fishing pier"
      ],
      block: [
        "retention pond","drainage pond","wastewater","water treatment",
        "water tower","utility","public works","office","apartment",
        "parking","police","sheriff","jail"
      ]
    },
    family_iq: {
      label: "Family Fun",
      allow: [
        "aquarium","zoo","mini golf","arcade","playground","museum",
        "family attraction","water park","theme park","bowling","park",
        "ice cream","beach","boardwalk","family friendly","kid friendly"
      ],
      strong: [
        "aquarium","zoo","mini golf","arcade","playground",
        "children's museum","family attraction","water park",
        "theme park","bowling"
      ],
      block: [
        "nightclub","adult entertainment","casino","sports bar",
        "office","warehouse","jail","police","sheriff"
      ]
    },
    outdoors: {
      label: "Outdoors",
      allow: [
        "park","trail","nature","preserve","lake","beach","waterfall",
        "scenic","overlook","garden","campground","camping","river",
        "marina","boardwalk"
      ],
      strong: [
        "trail","nature","preserve","lake","beach","waterfall",
        "scenic","overlook","campground"
      ],
      block: [
        "indoor","office","shopping mall","hotel","restaurant",
        "apartment","storage"
      ]
    },
    hidden_gems: {
      label: "Hidden Gem",
      allow: [
        "restaurant","cafe","café","park","trail","museum","gallery",
        "market","shop","bakery","bar","historic","garden","overlook"
      ],
      strong: [
        "hidden","local","independent","historic","off the beaten path",
        "neighborhood","family owned"
      ],
      block: [
        "walmart","target","mcdonald","starbucks","subway","taco bell",
        "burger king","chick-fil-a","chick fil a","best buy",
        "hotel","airport","hospital"
      ]
    }
  };

  var CATEGORY_ALIASES = {
    wedding_venue: "wedding_venues",
    wedding: "wedding_venues",
    wedding_locations: "wedding_venues",
    reception_venue: "wedding_venues",
    reception_venues: "wedding_venues",
    banquet_hall: "wedding_venues",
    banquet_halls: "wedding_venues",
    event_venue: "wedding_venues",
    event_venues: "wedding_venues",
    coffee_iq: "coffee",
    cafe: "coffee",
    cafes: "coffee",
    coffees: "coffee",
    food_iq: "food",
    restaurants: "food",
    restaurant: "food",
    dining: "food",
    dessert_iq: "dessert",
    desserts: "dessert",
    treats: "dessert",
    ice_cream: "dessert",
    local_favorites: "local_faves",
    local_favorite: "local_faves",
    local_hangout: "local_faves",
    worth_the_drive: "local_faves",
    worth_drive: "local_faves",
    worth_it: "local_faves",
    adventure_iq: "local_faves",
    live_music_iq: "live_music",
    music: "live_music",
    event: "events",
    events_iq: "events",
    things_to_do_tonight: "tonight",
    tonight_iq: "tonight",
    karaoke_iq: "karaoke",
    trivia_iq: "trivia",
    open_mic_iq: "open_mic",
    watch_party_iq: "watch_party",
    happy_hour_iq: "happy_hour",
    dj_iq: "dj",
    water: "waterfront",
    water_iq: "waterfront",
    waterfront_iq: "waterfront",
    beaches: "waterfront",
    beach_access: "waterfront",
    family: "family_iq",
    family_fun: "family_iq",
    kids: "family_iq",
    outdoor: "outdoors",
    scenic: "outdoors",
    hidden_gem: "hidden_gems"
  };

  var TOURIST_MARKETS = [
    "destin","nashville","gatlinburg","gulf shores","orange beach",
    "miami beach","las vegas","new york","boston","new orleans",
    "panama city beach","orlando","savannah","charleston"
  ];

  var MAJOR_MARKETS = [
    "tampa","tampa bay","st. petersburg","saint petersburg","clearwater",
    "nashville","memphis","atlanta","dallas","miami","boston",
    "new york","las vegas","san diego","los angeles","chicago",
    "houston","phoenix","philadelphia","seattle",
    "detroit","denver","baltimore"
  ];

  var VERY_SMALL_MARKETS = [
    "como","coldwater","cherokee village"
  ];

  function norm(value) {
    return utils.normText(value || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function clean(value) {
    return String(value == null ? "" : value)
      .replace(/\s+/g, " ")
      .trim();
  }

  function scanCategoryKey(value) {
    var key = normalizedCategory(value);

    return CATEGORY_ALIASES[key] || key;
  }

  function itemText(item) {
    function taxonomyText(value) {
      if (Array.isArray(value)) {
        value = value.join(" ");
      }

      return String(value == null ? "" : value)
        .replace(/[_\-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    return norm([
      item && item.label,
      item && item.name,
      item && item.category,
      item && item.primaryCategory,
      item && item.userFacingCategory,
      item && item.poiClass,
      item && item.entityType,
      item && taxonomyText(item.primaryType),
      item && taxonomyText(
        item.primaryTypeDisplayName &&
        (
          item.primaryTypeDisplayName.text ||
          item.primaryTypeDisplayName
        )
      ),
      item && taxonomyText(item.types),
      item && item.tags && item.tags.join
        ? item.tags.join(" ")
        : "",
      item && item.address,
      item && item.full,
      item && item.description,
      item && item.summary,
      item && item.eventTitle,
      item && item.eventType,
      item && item.eventRecurrenceText,
      item && item.eventFloorOrArea,
      item && item.eventConfidence
    ].join(" "));
  }

  function containsAny(textValue, terms) {
    var i;

    for (i = 0; i < (terms || []).length; i += 1) {
      if (textValue.indexOf(norm(terms[i])) > -1) {
        return true;
      }
    }

    return false;
  }

  function providerEntityType(item) {
    return norm([
      item && item.entityType,
      item && item.featureType,
      item && item.resultType,
      item && item.placeType,
      item && item.poiType,
      item && item.providerType,
      item && item.types && item.types.join
        ? item.types.join(" ")
        : ""
    ].join(" "));
  }

  function businessIdentityEvidence(item) {
    var evidence = 0;
    var categoryText = norm(
      item && (
        item.category ||
        item.primaryCategory ||
        item.userFacingCategory ||
        item.poiClass ||
        ""
      )
    );

    if (item && (
      item.placeId ||
      item.place_id ||
      item.googlePlaceId ||
      item.providerPlaceId
    )) {
      evidence += 3;
    }

    if (clean(
      item && (
        item.address ||
        item.formattedAddress ||
        item.full
      )
    )) {
      evidence += 2;
    }

    if (categoryText && containsAny(categoryText, [
      "restaurant","food","cafe","café","coffee","bar","pub",
      "nightlife","music venue","museum","park","shop","store",
      "attraction","tourism","tourist attraction","visitor attraction",
      "historic site","historic district","heritage","gallery",
      "art gallery","arts center","cultural center","performing arts",
      "garden","botanical garden","landmark","monument","market",
      "public market","food hall","waterfront","harbor","harbour",
      "pier","boardwalk","boutique","specialty shop","recreation",
      "bakery","brewery","theater","theatre"
    ])) {
      evidence += 2;
    }

    if (ratingValue(item) !== null || reviewValue(item) > 0) {
      evidence += 1;
    }

    if (item && (
      item.website ||
      item.phone ||
      item.openNow !== undefined ||
      item.isOpen !== undefined
    )) {
      evidence += 1;
    }

    return evidence;
  }

  function isDistrictEntity(item) {
    var entityType = providerEntityType(item);
    var textValue = itemText(item);

    return !!(
      item && (
        item.isDistrict === true ||
        item.generatedDistrict === true ||
        item.districtCard === true ||
        item.entityType === "district" ||
        item.type === "district" ||
        item.poiClass === "district"
      ) ||
      containsAny(entityType, [
        "district","neighborhood","neighbourhood"
      ]) ||
      /\bdistrict\b|\bdowntown\b|\btown square\b|\bvillage\b/.test(textValue)
    );
  }

  function districtAllowedForCategory(category) {
    var key = scanCategoryKey(category);

    return (
      key === "local_faves" ||
      key === "waterfront" ||
      key === "outdoors" ||
      key === "live_music" ||
      key === "nightlife" ||
      key === "hidden_gems"
    );
  }

  function civicEmergencyEntity(item) {
    var textValue = itemText(item);
    var entityType = providerEntityType(item);

    return containsAny(textValue + " " + entityType, [
      "police","sheriff","constable","law enforcement","patrol",
      "courthouse","court house","county jail","jail","detention",
      "fire department","fire station","fire rescue","ambulance","ems",
      "government office","city hall","county office","municipal",
      "public works","utility","water treatment","wastewater",
      "parking garage","parking lot","impound","tow yard"
    ]);
  }

  function hardBlockedEntity(item) {
    var textValue = itemText(item);
    var entityType = providerEntityType(item);
    var name = norm(
      item && (
        item.label ||
        item.name ||
        ""
      )
    );
    var evidence = businessIdentityEvidence(item);
    var explicitGeography = containsAny(entityType, [
      "city","locality","county","state","postcode","postal code",
      "administrative","admin area","region","country",
      "neighborhood boundary","neighbourhood boundary"
    ]);
    var explicitNonPlace = containsAny(textValue, [
      "city hall","county government","government office",
      "postal code","school district","radio station",
      "television station","warehouse","distribution center",
      "storage facility","apartment complex","residential building",
      "parking lot"
    ]);

    if (
      explicitNonPlace ||
      explicitGeography ||
      civicEmergencyEntity(item)
    ) {
      return true;
    }

    /*
      Generic city nicknames and locality labels are rejected only when the
      record lacks the evidence expected from a real destination. This blocks
      malformed records such as a bare "Bluff City" locality while preserving
      businesses such as Blues City Cafe.
    */
    if (
      evidence < 2 &&
      (
        containsAny(entityType, [
          "district","neighborhood","neighbourhood","place"
        ]) ||
        /^(bluff city|music city|the big apple|sin city|motor city)$/.test(name)
      )
    ) {
      return true;
    }

    return false;
  }


  function categoryTruth(item, category) {
    var key = scanCategoryKey(category);
    var profile = CATEGORY_PROFILES[key];
    var textValue = itemText(item);
    var providerCategory = norm([
      item && item.category,
      item && item.primaryCategory,
      item && item.userFacingCategory,
      item && item.poiClass,
      item && item.primaryType &&
        String(item.primaryType).replace(/[_\-]+/g, " "),
      item && item.primaryTypeDisplayName &&
        String(
          item.primaryTypeDisplayName.text ||
          item.primaryTypeDisplayName
        ).replace(/[_\-]+/g, " "),
      item && item.types && item.types.join
        ? item.types.join(" ").replace(/[_\-]+/g, " ")
        : ""
    ].join(" "));
    var allowMatch;
    var strongMatch;
    var hardBlocked;
    var softMismatch;

    hardBlocked = hardBlockedEntity(item);

    /*
      Entity Truth:
      A generated district can be a discovery experience, but it is not a
      restaurant, coffee shop, dessert stop, pharmacy, or other place-only
      result. This prevents Food IQ from returning cards such as "Destin
      District" when the user is trying to find Boshamps or another venue.
    */
    if (
      !hardBlocked &&
      isDistrictEntity(item) &&
      !districtAllowedForCategory(key)
    ) {
      hardBlocked = true;
    }

    if (!profile) {
      return {
        accepted: !hardBlocked,
        hardBlocked: hardBlocked,
        confidence: hardBlocked ? 0 : 0.55,
        label: clean(
          item.userFacingCategory ||
          item.category ||
          item.primaryCategory ||
          "Place IQ"
        ),
        reason: hardBlocked
          ? "blocked-generic-entity"
          : "generic-profile"
      };
    }

    allowMatch = containsAny(textValue, profile.allow);
    strongMatch = containsAny(textValue, profile.strong);

    /*
      Category blocks are confidence signals, not automatic rejection, unless
      the provider identity itself clearly conflicts with the requested type.
      This preserves real district businesses with incomplete metadata.
    */
    if (
      !hardBlocked &&
      containsAny(providerCategory, profile.block) &&
      !allowMatch &&
      !strongMatch
    ) {
      hardBlocked = true;
    }

    softMismatch = !allowMatch;

    /* Wedding venue searches are STRICT: unlike general Adventure discovery,
       plausible-but-unverified restaurants, coffee shops and suppliers are
       not acceptable substitutes. Google may classify a legitimate venue as
       an event_venue or banquet_hall; normalize those taxonomy labels. */
    if (key === "wedding_venues") {
      var venueEvidence = textValue + " " + providerCategory;
      var venueMatch = /\b(wedding\s+(venue|venues|reception|chapel|barn|estate|garden|pavilion|location|hall)|bridal\s+venue|reception\s+(venue|hall)|banquet\s+hall|event\s+(venue|venues|space|center|centre|hall)|special\s+event\s+venue|marriage\s+venue)\b/.test(venueEvidence);
      var supplierOnly = /\b(wedding\s+(photograph|planner|dress|cake|supply|supplier|service)|bridal\s+(shop|boutique)|florist|caterer|catering\s+service|dj\s+service|party\s+supply|equipment\s+rental)\b/.test(venueEvidence) && !venueMatch;
      var restaurantOnly = /\b(restaurant|seafood|steakhouse|coffee shop|fast food|pizzeria|burger)\b/.test(providerCategory) && !venueMatch;
      if (!venueMatch || supplierOnly || restaurantOnly) {
        hardBlocked = true;
        allowMatch = false;
        strongMatch = false;
      } else {
        // A mixed-use restaurant or hotel may genuinely host weddings when
        // authoritative venue metadata explicitly identifies the service.
        // Keep prior non-category entity/geography hard blocks authoritative.
        hardBlocked = hardBlockedEntity(item) ||
          (isDistrictEntity(item) && !districtAllowedForCategory(key));
        allowMatch = true;
        strongMatch = true;
      }
      softMismatch = !allowMatch;
    }

    /*
      Coffee Truth remains strict. Obvious restaurant identities that only
      happen to contain "Cafe" are never allowed into Coffee.
    */
    if (key === "coffee") {
      if (
        /hard rock cafe|rainforest cafe|sports cafe|internet cafe/.test(
          textValue
        )
      ) {
        hardBlocked = true;
      }

      if (
        /restaurant|bar and grill|bar & grill|steakhouse|seafood/.test(
          providerCategory
        ) &&
        !/coffee|espresso|roaster|coffee shop/.test(textValue)
      ) {
        hardBlocked = true;
      }
    }

    if (key === "waterfront") {
      if (
        containsAny(textValue, [
          "retention pond","drainage pond","wastewater",
          "water treatment","water tower","utility"
        ])
      ) {
        hardBlocked = true;
      }

      if (
        !hardBlocked &&
        !allowMatch &&
        containsAny(textValue, [
          "beach","harbor","harbour","marina","boardwalk",
          "pier","bay","gulf","river","lake","waterfront"
        ])
      ) {
        allowMatch = true;
        softMismatch = false;
      }
    }

    if (key === "family_iq") {
      if (
        containsAny(textValue, [
          "nightclub","adult entertainment","casino"
        ])
      ) {
        hardBlocked = true;
      }

      /*
        V602.5 Family Provider Metadata Rescue:
        Google Places frequently describes legitimate family destinations with
        taxonomy such as tourist_attraction, amusement_center, amusement_park,
        recreation_center, museum, aquarium, zoo, bowling_alley, park, beach,
        or visitor attraction. Accept those identities when the record has real
        place evidence and has not triggered a hard safety/category block.
      */
      if (
        !hardBlocked &&
        !allowMatch &&
        businessIdentityEvidence(item) >= 3 &&
        containsAny(textValue + " " + providerCategory, [
          "tourist attraction","visitor attraction","amusement center",
          "amusement park","recreation center","family entertainment center",
          "aquarium","zoo","museum","children museum","children's museum",
          "mini golf","miniature golf","bowling alley","arcade","playground",
          "water park","theme park","park","beach","boardwalk",
          "boat tour","dolphin cruise","wildlife experience"
        ])
      ) {
        allowMatch = true;
        softMismatch = false;
      }
    }

    /*
      Destination Local Favorites Truth:
      Geoapify frequently labels valid historic, cultural, waterfront, arts,
      market, garden, landmark, and visitor experiences with broad tourism
      metadata. Treat those records as category matches when they still carry
      credible place identity. Hard blocks remain authoritative.
    */
    if (
      key === "local_faves" &&
      !hardBlocked &&
      !allowMatch &&
      businessIdentityEvidence(item) >= 3 &&
      containsAny(textValue + " " + providerCategory, [
        "tourism","attraction","historic","heritage","gallery","arts",
        "cultural","garden","landmark","monument","market","food hall",
        "waterfront","harbor","harbour","pier","boardwalk","boutique",
        "specialty shop","recreation","visitor"
      ])
    ) {
      allowMatch = true;
      softMismatch = false;
    }

    /*
      Broad Food scans should not discard plausible restaurants merely
      because a provider omitted a clean category string.
    */
    if (
      key === "food" &&
      !hardBlocked &&
      !allowMatch &&
      /cuisine|dining|eat|menu|brunch|breakfast|lunch|dinner/.test(textValue)
    ) {
      allowMatch = true;
      softMismatch = false;
    }

    return {
      accepted: !hardBlocked,
      hardBlocked: hardBlocked,
      softMismatch: softMismatch,
      allowMatch: allowMatch,
      strongMatch: strongMatch,
      confidence: hardBlocked
        ? 0
        : strongMatch
          ? 0.96
          : allowMatch
            ? 0.80
            : businessIdentityEvidence(item) >= 3
              ? 0.60
              : 0.46,
      label: profile.label,
      reason: hardBlocked
        ? "category-blocked"
        : strongMatch
          ? "strong-category-match"
          : allowMatch
            ? "category-match"
            : "soft-category-mismatch"
    };
  }

  function ratingValue(item) {
    var value = Number(
      item && (
        item.rating ||
        item.googleRating ||
        item.averageRating
      )
    );

    return isFinite(value) ? value : null;
  }

  function reviewValue(item) {
    var value = Number(
      item && (
        item.reviewCount ||
        item.userRatingsTotal ||
        item.reviews ||
        item.ratingCount
      )
    );

    return isFinite(value) ? Math.max(0, value) : 0;
  }

  function distanceMilesValue(item, center) {
    var supplied = Number(
      item && (
        item.distanceMiles ||
        item.distance_miles
      )
    );
    var lat1;
    var lat2;
    var dLat;
    var dLng;
    var a;

    if (isFinite(supplied)) {
      return Math.max(0, supplied);
    }

    if (
      !item ||
      !center ||
      !isFinite(Number(item.lat)) ||
      !isFinite(Number(item.lng)) ||
      !isFinite(Number(center.lat)) ||
      !isFinite(Number(center.lng))
    ) {
      return null;
    }

    lat1 = Number(center.lat) * Math.PI / 180;
    lat2 = Number(item.lat) * Math.PI / 180;
    dLat = (Number(item.lat) - Number(center.lat)) * Math.PI / 180;
    dLng = (Number(item.lng) - Number(center.lng)) * Math.PI / 180;

    a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);

    return 3958.7613 * 2 *
      Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function brandPenalty(item) {
    var textValue = itemText(item);

    if (containsAny(textValue, [
      "mcdonald","burger king","taco bell","subway","wendy",
      "starbucks","dunkin","applebee","chili's","olive garden"
    ])) {
      return 18;
    }

    return 0;
  }

  function scoreResult(item, execution, truth) {
    var rating = ratingValue(item);
    var reviews = reviewValue(item);
    var distance = distanceMilesValue(item, execution.center);
    var textValue = itemText(item);
    var score = 0;

    score += truth.confidence * 50;

    if (truth.softMismatch) {
      score -= 8;
    }

    if (rating !== null) {
      score += Math.max(0, rating - 3.5) * 18;
    }

    score += Math.min(22, Math.log(reviews + 1) * 3.2);

    if (distance !== null) {
      if (distance <= 1) {
        score -= distance * 1.0;
      } else if (distance <= 5) {
        score -= 1 + ((distance - 1) * 0.45);
      } else {
        score -= 2.8 + ((distance - 5) * 0.15);
      }
    }

    if (item.openNow === true || item.isOpen === true) {
      score += 8;
    }

    if (
      containsAny(textValue, [
        "local","independent","family owned","historic",
        "neighborhood","roaster","award winning"
      ])
    ) {
      score += 10;
    }

    if (
      item.diqPhotoUrl ||
      item.photoURL ||
      item.photoUrl ||
      item.heroPhoto ||
      item.image
    ) {
      score += 4;
    }

    score += districtAnchorScore(
      item,
      execution
    );

    score += categoryContextScore(
      item,
      execution.category
    );

    score += experienceSignalScore(
      item,
      execution.category
    );

    score += eventIQScore(
      item,
      execution
    );

    score += decisionIQScore(
      item,
      execution
    );

    score += proximityValueScore(
      item,
      execution
    );

    score -= brandPenalty(item);

    return score;
  }

  function honestCategory(item, requestedCategory, truth) {
    var textValue = itemText(item);

    if (truth && truth.label) {
      return truth.label;
    }

    if (/coffee|espresso|roaster/.test(textValue)) {
      return "Coffee";
    }

    if (/restaurant|grill|kitchen|diner|bbq|pizza|taco|burger/.test(textValue)) {
      return "Food";
    }

    if (/bar|pub|nightclub|lounge/.test(textValue)) {
      return "Nightlife";
    }

    if (/park|trail|lake|beach|scenic/.test(textValue)) {
      return "Outdoors";
    }

    return clean(
      item.userFacingCategory ||
      item.category ||
      item.primaryCategory ||
      requestedCategory ||
      "Place IQ"
    );
  }

  function recommendedBecause(item, execution, truth) {
    var eventData = eventShelfData(item);
    var confidence = eventConfidence(item);
    var rating = ratingValue(item);
    var reviews = reviewValue(item);
    var distance = distanceMilesValue(item, execution.center);
    var textValue = itemText(item);
    var parts = [];

    if (eventData) {
      if (confidence === "confirmed_tonight") {
        parts.push(
          eventData.time
            ? eventData.eventType + " starts " + eventData.time
            : eventData.eventType + " tonight"
        );
      } else if (confidence === "upcoming_event") {
        parts.push(
          eventData.time
            ? eventData.eventType + " at " + eventData.time
            : "Upcoming " + eventData.eventType.toLowerCase()
        );
      } else if (confidence === "usually_happens_tonight") {
        parts.push(
          eventData.time
            ? "Usually " + eventData.time
            : "Usually happens tonight"
        );
      } else if (confidence === "known_music_venue") {
        parts.push("Known live music venue");
      }

      if (eventData.area) {
        parts.push(eventData.area);
      }
    }

    var whyNow = decisionWhyNow(item, execution);

    if (whyNow && parts.indexOf(whyNow) < 0) {
      parts.push(whyNow);
    }

    if (item.openNow === true || item.isOpen === true) {
      parts.push("Open now");
    }

    if (
      containsAny(textValue, [
        "local","independent","family owned","family-owned",
        "neighborhood","historic","institution"
      ])
    ) {
      parts.push("Local favorite");
    }

    if (rating !== null && rating >= 4.6) {
      parts.push("Highly rated");
    } else if (rating !== null && rating >= 4.3) {
      parts.push("Strong reviews");
    }

    if (reviews >= 500) {
      parts.push("Well established");
    }

    if (distance !== null && distance <= 1) {
      parts.push("Very close");
    } else if (distance !== null && distance <= 4) {
      parts.push("Nearby");
    }

    if (
      containsAny(textValue, [
        "parking","garage","easy access"
      ])
    ) {
      parts.push("Easy parking");
    }

    if (
      containsAny(textValue, [
        "live music","music venue","concert"
      ])
    ) {
      parts.push("Live music");
    }

    if (
      containsAny(textValue, [
        "patio","rooftop","waterfront","riverfront"
      ])
    ) {
      parts.push(
        containsAny(textValue, ["waterfront","riverfront"])
          ? "Waterfront"
          : containsAny(textValue, ["rooftop"])
            ? "Rooftop"
            : "Patio"
      );
    }

    if (
      districtAnchorScore(item, execution) >= DISTRICT_ANCHOR_BONUS
    ) {
      parts.push("District standout");
    }

    if (
      experienceSignalScore(item, execution.category) >= 18
    ) {
      parts.push("Strong experience match");
    }

    if (!parts.length && truth && truth.confidence >= 0.9) {
      parts.push(
        "Strong " +
        String(truth.label || "category").toLowerCase() +
        " match"
      );
    }

    if (!parts.length) {
      parts.push("Worth exploring nearby");
    }

    return parts.slice(0, 3).join(" • ");
  }


  function marketLabel(execution) {
    var source = execution || {};
    var center = source.center || {};
    var handoff = source.destinationHandoff || {};
    var current = {};

    try {
      current = destinationController.current() || {};
    } catch (error) {
      current = {};
    }

    return norm([
      center.label,
      center.name,
      center.city,
      center.marketLabel,
      handoff.marketLabel,
      handoff.label,
      handoff.name,
      handoff.city,
      handoff.formattedAddress,
      handoff.address,
      current.marketLabel,
      current.label,
      current.name,
      current.city,
      current.formattedAddress,
      current.address
    ].filter(Boolean).join(" "));
  }

  function touristMarket(execution) {
    return containsAny(
      marketLabel(execution),
      TOURIST_MARKETS
    );
  }

  function majorCityMarket(execution) {
    return containsAny(
      marketLabel(execution),
      MAJOR_MARKETS
    );
  }

  function majorMarket(execution) {
    return (
      majorCityMarket(execution) ||
      touristMarket(execution)
    );
  }

  function localRadiusProfile(execution) {
    var key = scanCategoryKey(
      execution && execution.category
    );

    return LOCAL_CATEGORY_RADIUS_METERS[key] || null;
  }

  function marketVolumeProfile(execution, trustedCount) {
    var label = marketLabel(execution);
    var count = Math.max(0, Number(trustedCount || 0));

    if (containsAny(label, VERY_SMALL_MARKETS)) {
      return {
        marketClass: "very_small_town",
        minimum: Math.min(2, count || 2),
        target: VERY_SMALL_TOWN_MAX_RESULTS,
        providerLimit: 20,
        rawFloor: 6
      };
    }

    /*
      Major-city identity wins over tourist identity when a destination exists
      in both registries. Dense metros need the broadest trusted pool.
    */
    if (majorCityMarket(execution)) {
      return {
        marketClass: "major_metro",
        minimum: MAJOR_CITY_MIN_RESULTS,
        target: MAJOR_CITY_MAX_RESULTS,
        providerLimit: MAJOR_PROVIDER_RESULT_LIMIT,
        rawFloor: MAJOR_RAW_FALLBACK_FLOOR
      };
    }

    if (touristMarket(execution)) {
      return {
        marketClass: "tourist_destination",
        minimum: TOURIST_MIN_RESULTS,
        target: TOURIST_MAX_RESULTS,
        providerLimit: TOURIST_PROVIDER_RESULT_LIMIT,
        rawFloor: TOURIST_RAW_FALLBACK_FLOOR
      };
    }

    /*
      Candidate density is the safest fallback classifier when a market is not
      in the registry. Sparse pools behave like small towns; healthy pools
      behave like typical cities.
    */
    if (count > 0 && count <= SMALL_TOWN_MAX_RESULTS) {
      return {
        marketClass: "small_town",
        minimum: Math.min(SMALL_TOWN_MIN_RESULTS, count),
        target: SMALL_TOWN_MAX_RESULTS,
        providerLimit: 30,
        rawFloor: 10
      };
    }

    return {
      marketClass: "typical_city",
      minimum: TYPICAL_CITY_MIN_RESULTS,
      target: TYPICAL_CITY_MAX_RESULTS,
      providerLimit: STANDARD_PROVIDER_RESULT_LIMIT,
      rawFloor: RAW_FALLBACK_FLOOR
    };
  }

  function providerResultLimit(execution) {
    var profile = marketVolumeProfile(execution, 0);
    var configured = Number(
      config.scan.maximumResults || 0
    );

    /*
      The legacy global cap may be smaller than the market needs. Treat it as
      a floor for compatibility, never as a ceiling on dense-market discovery.
    */
    return Math.max(
      profile.providerLimit,
      configured
    );
  }

  function rawFallbackFloor(execution) {
    var localProfile = localRadiusProfile(execution);

    if (localProfile) {
      return Number(localProfile.rawFloor || 3);
    }

    return marketVolumeProfile(
      execution,
      0
    ).rawFloor;
  }

  function trustedFallbackMinimum(execution) {
    var localProfile = localRadiusProfile(execution);
    var profile;

    /*
      Quick-stop categories remain intentionally local. They may settle with a
      smaller trusted set instead of widening across an entire metro.
    */
    if (localProfile) {
      return Math.max(
        1,
        Number(localProfile.rawFloor || 3)
      );
    }

    profile = marketVolumeProfile(execution, 0);

    return Math.max(
      1,
      Number(profile.minimum || 0)
    );
  }

  function visibleResultTarget(execution, count) {
    var trustedCount = Math.max(0, Number(count || 0));
    var profile = marketVolumeProfile(
      execution,
      trustedCount
    );

    /*
      Never fabricate volume. Commit up to the market target, but only from
      places that survived identity, Category Truth, scoring, and diversity.
    */
    return Math.max(
      1,
      Math.min(
        profile.target,
        trustedCount
      )
    );
  }

  function diversityKey(item) {
    return norm(
      item && (
        item.brand ||
        item.chain ||
        item.parentBrand ||
        item.label ||
        item.name ||
        ""
      )
    ).replace(/\b\d+\b/g, "").trim();
  }

  function applyDiversity(items, limit) {
    var output = [];
    var brandCounts = {};
    var index;
    var item;
    var key;

    for (index = 0; index < items.length; index += 1) {
      item = items[index];
      key = diversityKey(item);

      if (
        key &&
        brandCounts[key] &&
        brandCounts[key] >= 2
      ) {
        continue;
      }

      if (key) {
        brandCounts[key] = (brandCounts[key] || 0) + 1;
      }

      output.push(item);

      if (output.length >= limit) {
        break;
      }
    }

    return output;
  }

  function explicitDiscoveryQuery(execution) {
    return norm(
      execution && (
        execution.originalQuery ||
        execution.query ||
        ""
      )
    );
  }

  function matchedDefaultFoodChain(item) {
    var textValue = itemText(item);
    var index;

    for (index = 0; index < DEFAULT_FOOD_CHAIN_TERMS.length; index += 1) {
      if (textValue.indexOf(norm(DEFAULT_FOOD_CHAIN_TERMS[index])) > -1) {
        return DEFAULT_FOOD_CHAIN_TERMS[index];
      }
    }

    return "";
  }

  function explicitFoodBrandSearchAllows(item, execution) {
    var query = explicitDiscoveryQuery(execution);
    var chain = matchedDefaultFoodChain(item);

    if (!query || !chain) {
      return false;
    }

    return (
      query.indexOf(norm(chain)) > -1 ||
      norm(chain).indexOf(query) > -1
    );
  }

  function shouldExcludeDefaultFoodChain(item, execution) {
    var category = scanCategoryKey(
      execution && execution.category
    );
    var chain;

    if (category !== "food") {
      return false;
    }

    if (explicitFoodBrandSearchAllows(item, execution)) {
      return false;
    }

    chain = matchedDefaultFoodChain(item);

    if (chain) {
      item.__foodChainBlocked = true;
      item.__foodChainName = chain;
      return true;
    }

    return !!(
      item &&
      item.isChain === true &&
      (
        item.isNationalChain === true ||
        item.nationalChain === true ||
        item.corporateChain === true ||
        item.chainScope === "national"
      )
    );
  }

  function freshMarketHistoryKey(execution) {
    return recommendationContextKey(execution || {});
  }

  function freshMarketSeenSet(execution) {
    var key = freshMarketHistoryKey(execution);

    if (!freshMarketSeenKeysBySession[key]) {
      freshMarketSeenKeysBySession[key] = {};
    }

    return freshMarketSeenKeysBySession[key];
  }

  function alreadyShownInPriorMarketScan(item, execution) {
    var key;
    var seen;

    if (!execution || execution.isRefill === true) {
      return false;
    }

    key = item && (
      item.__adventureKey ||
      itemKey(item)
    );

    if (!key) {
      return false;
    }

    seen = freshMarketSeenSet(execution);
    return seen[key] === true;
  }

  function rememberFreshMarketResults(items, execution) {
    var seen;

    if (!execution || execution.isRefill === true) {
      return false;
    }

    seen = freshMarketSeenSet(execution);

    (items || []).forEach(function (item) {
      var key = item && (
        item.__adventureKey ||
        itemKey(item)
      );

      if (key) {
        seen[key] = true;
      }
    });

    return true;
  }

  function processRecommendations(items, execution) {
    var requestedCategory = normalizedCategory(execution.category);
    var category = scanCategoryKey(execution.category);

    if (
      requestedCategory === "worth_the_drive" ||
      requestedCategory === "worth_drive"
    ) {
      try {
        events.emit("scan:category-alias-applied", {
          requestedCategory: requestedCategory,
          effectiveCategory: category,
          token: execution.token,
          center: execution.center
        });
      } catch (categoryAliasEventError) {}
    }
    var accepted = [];
    var rejected = [];
    var normalized = deduplicate(items || []);
    var index;
    var item;
    var truth;
    var output = [];
    var target;
    var thresholdIndex;
    var threshold;
    var thresholdPool;
    var seen = {};
    var key;
    var historyExcluded = [];
    var historyRescueApplied = false;

    for (index = 0; index < normalized.length; index += 1) {
      item = normalized[index];

      if (shouldExcludeDefaultFoodChain(item, execution)) {
        rejected.push(item);
        continue;
      }

      if (alreadyShownInPriorMarketScan(item, execution)) {
        historyExcluded.push(item);
        rejected.push(item);
        continue;
      }

      truth = categoryTruth(item, category);

      item.__categoryTruth = truth;
      item.__scanScore = scoreResult(item, execution, truth);

      if (truth.hardBlocked) {
        rejected.push(item);
        continue;
      }

      item.userFacingCategory = honestCategory(
        item,
        category,
        truth
      );

      item.recommendedBecause = recommendedBecause(
        item,
        execution,
        truth
      );

      accepted.push(item);
    }

    /*
      Zero-Result History Rescue:
      Prior-session no-repeat memory is useful only while fresh unseen places
      exist. It must never turn a populated destination into a false empty
      market. When history exclusion is the only remaining barrier, restore
      previously seen candidates and still apply every hard Category Truth block.
    */
    if (
      accepted.length === 0 &&
      historyExcluded.length > 0 &&
      execution &&
      execution.isRefill !== true
    ) {
      for (index = 0; index < historyExcluded.length; index += 1) {
        item = historyExcluded[index];
        truth = categoryTruth(item, category);

        item.__categoryTruth = truth;
        item.__scanScore = scoreResult(item, execution, truth);

        if (truth.hardBlocked) {
          continue;
        }

        item.userFacingCategory = honestCategory(
          item,
          category,
          truth
        );

        item.recommendedBecause = recommendedBecause(
          item,
          execution,
          truth
        );

        item.__historyRescued = true;
        accepted.push(item);
      }

      historyRescueApplied = accepted.length > 0;

      if (historyRescueApplied) {
        try {
          events.emit("scan:history-rescue-applied", {
            token: execution.token,
            category: category,
            destination: execution.destination || null,
            rescuedCount: accepted.length,
            historyExcludedCount: historyExcluded.length,
            rawCount: normalized.length
          });
        } catch (historyRescueEventError) {}
      }
    }

    accepted.sort(function (a, b) {
      return Number(b.__scanScore || 0) -
        Number(a.__scanScore || 0);
    });

    target = visibleResultTarget(
      execution,
      accepted.length
    );

    /*
      Progressive Ranking:
      fill from the same candidate pool by lowering the ranking threshold.
      Hard blocks never relax. Coffee's explicit restaurant exclusions remain
      absolute because those records were removed above.
    */
    for (
      thresholdIndex = 0;
      thresholdIndex < PROGRESSIVE_SCORE_THRESHOLDS.length;
      thresholdIndex += 1
    ) {
      threshold = PROGRESSIVE_SCORE_THRESHOLDS[thresholdIndex];

      thresholdPool = accepted.filter(function (candidate) {
        return Number(candidate.__scanScore || 0) >= threshold;
      });

      thresholdPool = applyDiversity(
        thresholdPool,
        target
      );

      for (index = 0; index < thresholdPool.length; index += 1) {
        item = thresholdPool[index];
        key = item.__adventureKey || itemKey(item);

        if (!key || seen[key]) {
          continue;
        }

        seen[key] = true;
        output.push(item);

        if (output.length >= target) {
          break;
        }
      }

      if (output.length >= target) {
        break;
      }
    }

    /*
      Last safe fill:
      if thresholds still underfill, use the remaining non-hard-blocked
      candidates in score order. This is rank relaxation, not truth relaxation.
    */
    if (output.length < target) {
      for (index = 0; index < accepted.length; index += 1) {
        item = accepted[index];
        key = item.__adventureKey || itemKey(item);

        if (!key || seen[key]) {
          continue;
        }

        seen[key] = true;
        output.push(item);

        if (output.length >= target) {
          break;
        }
      }
    }

    return {
      results: output,
      topRecommendations: output.slice(
        0,
        PRIMARY_RECOMMENDATION_COUNT
      ),
      rejectedCount: rejected.length,
      acceptedCount: accepted.length,
      rawCount: normalized.length,
      historyExcludedCount: historyExcluded.length,
      historyRescueApplied: historyRescueApplied,
      historyRescuedCount: accepted.filter(function (candidate) {
        return candidate.__historyRescued === true;
      }).length,
      targetCount: target,
      marketVolumeProfile: marketVolumeProfile(
        execution,
        accepted.length
      ),
      category: category,
      districtAnchoredCount: output.filter(function (candidate) {
        return districtAnchorScore(candidate, execution) > 0;
      }).length,
      experienceRichCount: output.filter(function (candidate) {
        return experienceSignals(candidate).length >= 2;
      }).length,
      eventIQCount: output.filter(function (candidate) {
        return !!eventRecord(candidate);
      }).length,
      confirmedTonightCount: output.filter(function (candidate) {
        return eventConfidence(candidate) === "confirmed_tonight";
      }).length,
      recurringTonightCount: output.filter(function (candidate) {
        return eventConfidence(candidate) === "usually_happens_tonight";
      }).length,
      knownMusicVenueCount: output.filter(function (candidate) {
        return eventConfidence(candidate) === "known_music_venue";
      }).length,
      decisionIQCount: output.filter(function (candidate) {
        return recommendationSignals(candidate, execution).length > 0;
      }).length,
      worthTheDriveCount: output.filter(function (candidate) {
        return recommendationSignals(candidate, execution).some(function (signal) {
          return signal.key === "worth_drive";
        });
      }).length,
      localFavoriteCount: output.filter(function (candidate) {
        return recommendationSignals(candidate, execution).some(function (signal) {
          return signal.key === "local_favorite";
        });
      }).length,
      lowestIncludedScore: output.length
        ? Number(output[output.length - 1].__scanScore || 0)
        : null
    };
  }

  function validCoordinate(value) {
    var lat;
    var lng;
    var location;

    if (!value) {
      return null;
    }

    location = value.location ||
      (value.geometry && value.geometry.location) ||
      value.position ||
      value.center ||
      value;

    lat = Number(
      typeof location.lat !== "undefined"
        ? location.lat
        : location.latitude
    );

    lng = Number(
      typeof location.lng !== "undefined"
        ? location.lng
        : (typeof location.lon !== "undefined"
            ? location.lon
            : location.longitude)
    );

    if (
      !isFinite(lat) ||
      !isFinite(lng) ||
      Math.abs(lat) > 90 ||
      Math.abs(lng) > 180
    ) {
      return null;
    }

    return {
      lat: lat,
      lng: lng,
      label: String(
        value.label ||
        value.name ||
        value.city ||
        ""
      )
    };
  }

  function cleanText(value) {
    return String(value == null ? "" : value)
      .replace(/\s+/g, " ")
      .trim();
  }

  function validBounds(value) {
    var source = value || {};
    var west;
    var south;
    var east;
    var north;
    var spanLng;
    var spanLat;

    if (Array.isArray(source) && source.length >= 2) {
      if (
        Array.isArray(source[0]) &&
        Array.isArray(source[1])
      ) {
        west = Number(source[0][0]);
        south = Number(source[0][1]);
        east = Number(source[1][0]);
        north = Number(source[1][1]);
      } else if (source.length >= 4) {
        west = Number(source[0]);
        south = Number(source[1]);
        east = Number(source[2]);
        north = Number(source[3]);
      }
    } else {
      west = Number(
        source.west != null ? source.west :
        source.minLng != null ? source.minLng :
        source.minLon != null ? source.minLon :
        source.minLongitude
      );
      south = Number(
        source.south != null ? source.south :
        source.minLat != null ? source.minLat :
        source.minLatitude
      );
      east = Number(
        source.east != null ? source.east :
        source.maxLng != null ? source.maxLng :
        source.maxLon != null ? source.maxLon :
        source.maxLongitude
      );
      north = Number(
        source.north != null ? source.north :
        source.maxLat != null ? source.maxLat :
        source.maxLatitude
      );
    }

    if (
      !isFinite(west) ||
      !isFinite(south) ||
      !isFinite(east) ||
      !isFinite(north) ||
      Math.abs(west) > 180 ||
      Math.abs(east) > 180 ||
      Math.abs(south) > 90 ||
      Math.abs(north) > 90
    ) {
      return null;
    }

    if (west > east) {
      var lngSwap = west;
      west = east;
      east = lngSwap;
    }

    if (south > north) {
      var latSwap = south;
      south = north;
      north = latSwap;
    }

    spanLng = east - west;
    spanLat = north - south;

    /*
      City/district bounds should never span half the planet. Reject unsafe
      provider boxes and let the center/radius camera fallback take authority.
    */
    if (
      spanLng <= 0 ||
      spanLat <= 0 ||
      spanLng > 8 ||
      spanLat > 8
    ) {
      return null;
    }

    return {
      west: west,
      south: south,
      east: east,
      north: north,
      array: [
        [west, south],
        [east, north]
      ]
    };
  }

  function destinationSource(options) {
    var supplied = options || {};
    var direct = supplied.destination ||
      supplied.destinationEntity ||
      supplied.selectedDestination ||
      null;
    var current = null;

    try {
      current = destinationController.current();
    } catch (error) {
      current = null;
    }

    return direct || current || supplied.center || null;
  }

  function normalizeDestinationHandoff(value, fallbackCenter) {
    var source = value || {};
    var coordinate = validCoordinate(source) ||
      validCoordinate(fallbackCenter);
    var bounds = validBounds(
      source.bounds ||
      source.bbox ||
      source.boundingBox ||
      source.viewport ||
      null
    );
    var radius = Number(
      source.radiusMeters ||
      source.searchRadiusMeters ||
      source.radius ||
      0
    );
    var output;
    var key;

    if (!coordinate) {
      return null;
    }

    output = {};

    for (key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        output[key] = source[key];
      }
    }

    output.id = cleanText(
      source.id ||
      source.placeId ||
      source.place_id ||
      source.canonicalEntityId ||
      ""
    );
    output.placeId = cleanText(
      source.placeId ||
      source.place_id ||
      source.id ||
      ""
    );
    output.name = cleanText(
      source.name ||
      source.canonicalName ||
      source.label ||
      coordinate.label ||
      "Destination"
    );
    output.label = cleanText(
      source.label ||
      source.marketLabel ||
      source.name ||
      coordinate.label ||
      "Destination"
    );
    output.marketLabel = cleanText(
      source.marketLabel ||
      source.label ||
      source.name ||
      coordinate.label ||
      ""
    );
    output.entityType = cleanText(
      source.entityType ||
      source.destinationType ||
      source.type ||
      "destination"
    );
    output.destinationType = cleanText(
      source.destinationType ||
      source.entityType ||
      source.type ||
      "destination"
    );

    /*
      V600.63 Safe Market Bounds:
      discard only clearly regional/statewide provider viewports while keeping
      the exact selected destination center.
    */
    if (bounds) {
      var safeEntityType = (
        output.entityType + " " + output.destinationType
      ).toLowerCase();
      var safeMaxLatSpan =
        /district|neighborhood|neighbourhood|beach|park/.test(safeEntityType)
          ? 0.55
          : /city|metro|town|village|locality/.test(safeEntityType)
            ? 1.25
            : 2.25;
      var safeLatSpan = Number(bounds.north) - Number(bounds.south);
      var safeLngSpan = Number(bounds.east) - Number(bounds.west);
      var safeCenterInside = (
        coordinate.lat >= Number(bounds.south) - 0.15 &&
        coordinate.lat <= Number(bounds.north) + 0.15 &&
        coordinate.lng >= Number(bounds.west) - 0.15 &&
        coordinate.lng <= Number(bounds.east) + 0.15
      );

      if (
        !safeCenterInside ||
        safeLatSpan > safeMaxLatSpan ||
        safeLngSpan > safeMaxLatSpan * 1.45
      ) {
        bounds = null;
      }
    }

    output.lat = coordinate.lat;
    output.lng = coordinate.lng;
    output.latitude = coordinate.lat;
    output.longitude = coordinate.lng;
    output.center = {
      lat: coordinate.lat,
      lng: coordinate.lng,
      label: output.label
    };
    output.bounds = bounds
      ? {
          west: bounds.west,
          south: bounds.south,
          east: bounds.east,
          north: bounds.north
        }
      : null;
    output.radiusMeters = isFinite(radius) && radius > 0
      ? radius
      : Number(config.scan.defaultRadiusMeters || 18000);
    output.canonicalEntity =
      source.canonicalEntity === true;
    output.providerCityAuthority =
      source.providerCityAuthority === true;
    output.destinationEntity =
      source.destinationEntity === true ||
      output.canonicalEntity ||
      output.providerCityAuthority ||
      /city|district|lake|beach|corridor|park|region|destination/i.test(
        output.entityType
      );
    output.destinationProfile =
      source.destinationProfile &&
      typeof source.destinationProfile === "object"
        ? source.destinationProfile
        : null;
    output.destinationConfidence = Number(
      source.destinationConfidence ||
      source.confidence ||
      0
    );
    output.parentCity = cleanText(source.parentCity || "");
    output.parentEntityId = cleanText(source.parentEntityId || "");
    output.submarkets = Array.isArray(source.submarkets)
      ? source.submarkets.slice()
      : [];
    output.__mapSafe = true;
    output.__mapBoundsValid = !!bounds;
    output.__normalizedBy = "scan-v600.53";

    lastSafeDestinationHandoff = output;
    return output;
  }

  function rawAdventureMap() {
    var controller = window.DriveIQAdventureMap;
    var candidate = null;

    try {
      if (
        controller &&
        typeof controller.getMap === "function"
      ) {
        candidate = controller.getMap();
      }
    } catch (errorA) {}

    if (!candidate) {
      try {
        if (
          controller &&
          typeof controller.map === "function"
        ) {
          candidate = controller.map();
        } else if (controller && controller.map) {
          candidate = controller.map;
        }
      } catch (errorB) {}
    }

    return candidate ||
      window.DRIVE_IQ_ADVENTURE_MAP ||
      window.DriveIQAdventureMapboxMap ||
      window.adventureMap ||
      null;
  }

  function revealAdventureMapCanvas() {
    var selectors = [
      "#map",
      "#adventureMap",
      "#diqAdventureMap",
      "#diqAdventureMapCanvas",
      ".diqAdventureMap",
      ".mapboxgl-map",
      ".mapboxgl-canvas-container",
      ".mapboxgl-canvas"
    ];
    var nodes = [];
    var index;
    var found;
    var node;

    try {
      nodes = document.querySelectorAll(selectors.join(","));
    } catch (queryError) {
      nodes = [];
    }

    for (index = 0; index < nodes.length; index += 1) {
      node = nodes[index];

      if (!node || !node.style) {
        continue;
      }

      /*
        Do not force geometry or z-index. Only undo accidental hidden/blank
        states caused by panel transitions or a failed destination camera.
      */
      if (node.style.display === "none") {
        node.style.display = "";
      }
      if (node.style.visibility === "hidden") {
        node.style.visibility = "visible";
      }
      if (node.style.opacity === "0") {
        node.style.opacity = "1";
      }
    }

    try {
      found = document.querySelector(".mapboxgl-canvas");
      if (found) {
        found.setAttribute("aria-hidden", "false");
      }
    } catch (canvasError) {}

    return true;
  }

  function committedResultCoordinates(results) {
    var seen = {};
    var coordinates = [];

    (Array.isArray(results) ? results : []).forEach(function (item) {
      var coordinate = validCoordinate(item);
      var key;

      if (!coordinate) {
        return;
      }

      key =
        coordinate.lat.toFixed(5) + "," +
        coordinate.lng.toFixed(5);

      if (seen[key]) {
        return;
      }

      seen[key] = true;
      coordinates.push(coordinate);
    });

    return coordinates;
  }

  function resultDerivedBounds(results, center, category) {
    var coordinates = committedResultCoordinates(results);
    var categoryKey = scanCategoryKey(category || "");
    var maxMiles = (
      categoryKey === "food" ||
      categoryKey === "coffee" ||
      categoryKey === "dessert" ||
      categoryKey === "breakfast"
    )
      ? RESULT_CAMERA_LOCAL_MAX_MILES
      : RESULT_CAMERA_DESTINATION_MAX_MILES;

    if (
      center &&
      isFinite(Number(center.lat)) &&
      isFinite(Number(center.lng))
    ) {
      coordinates = coordinates
        .map(function (coordinate) {
          return {
            lat: coordinate.lat,
            lng: coordinate.lng,
            __cameraMiles: distanceMilesValue(
              {
                lat: coordinate.lat,
                lng: coordinate.lng
              },
              center
            )
          };
        })
        .filter(function (coordinate) {
          return (
            coordinate.__cameraMiles !== null &&
            coordinate.__cameraMiles <= maxMiles
          );
        })
        .sort(function (a, b) {
          return a.__cameraMiles - b.__cameraMiles;
        });

      if (
        categoryKey !== "food" &&
        categoryKey !== "coffee" &&
        categoryKey !== "dessert" &&
        categoryKey !== "breakfast" &&
        coordinates.length > RESULT_CAMERA_CORE_LIMIT
      ) {
        coordinates = coordinates.slice(0, RESULT_CAMERA_CORE_LIMIT);
      }
    }
    var west = Infinity;
    var south = Infinity;
    var east = -Infinity;
    var north = -Infinity;
    var latSpan;
    var lngSpan;

    if (coordinates.length < MIN_RESULT_CAMERA_POINTS) {
      return null;
    }

    coordinates.forEach(function (coordinate) {
      west = Math.min(west, coordinate.lng);
      south = Math.min(south, coordinate.lat);
      east = Math.max(east, coordinate.lng);
      north = Math.max(north, coordinate.lat);
    });

    latSpan = north - south;
    lngSpan = east - west;

    if (
      !isFinite(latSpan) ||
      !isFinite(lngSpan) ||
      latSpan <= 0 ||
      lngSpan <= 0 ||
      latSpan > MAX_RESULT_BOUNDS_LAT_SPAN ||
      lngSpan > MAX_RESULT_BOUNDS_LNG_SPAN
    ) {
      return null;
    }

    return {
      west: west,
      south: south,
      east: east,
      north: north,
      latSpan: latSpan,
      lngSpan: lngSpan,
      count: coordinates.length,
      coordinates: coordinates.slice()
    };
  }

  function recoverMapSurface(reason, center) {
    var controller = window.DriveIQAdventureMap;

    revealAdventureMapCanvas();

    try {
      if (
        controller &&
        typeof controller.scheduleSurfaceRecovery === "function"
      ) {
        controller.scheduleSurfaceRecovery(
          String(reason || "scan-result-camera"),
          {
            center: center || null
          }
        );
        return true;
      }

      if (
        controller &&
        typeof controller.recoverSurface === "function"
      ) {
        controller.recoverSurface(
          String(reason || "scan-result-camera"),
          {
            center: center || null
          }
        );
        return true;
      }

      if (
        controller &&
        typeof controller.restore === "function"
      ) {
        controller.restore(
          String(reason || "scan-result-camera")
        );
        return true;
      }
    } catch (error) {}

    return false;
  }

  function safeResultCamera(results, destination, reason) {
    var controller = window.DriveIQAdventureMap;
    var map = rawAdventureMap();
    var handoff = normalizeDestinationHandoff(
      destination,
      currentCenter({})
    );
    var center = handoff
      ? {lat: handoff.lat, lng: handoff.lng}
      : validCoordinate(currentCenter({}));
    var bounds = resultDerivedBounds(
      results,
      center,
      lastRecommendationExecution &&
      lastRecommendationExecution.category
    );
    var moved = false;
    var cameraOptions = {
      padding: {
        top: 130,
        right: 46,
        bottom: 250,
        left: 46
      },
      maxZoom: 14.2,
      duration: 520,
      essential: true
    };

    recoverMapSurface(
      String(reason || "result-camera") + "-before",
      center
    );

    /*
      Result Geometry Authority:
      after a scan completes, destination/provider bounds are no longer used.
      Only the coordinates that survived validation and actually became pins
      may define the final camera.
    */
    if (bounds && controller) {
      try {
        if (
          typeof controller.fitResults === "function"
        ) {
          moved = controller.fitResults(
            bounds.coordinates || committedResultCoordinates(results),
            cameraOptions
          ) !== false;
        }
      } catch (controllerFitError) {
        moved = false;
      }
    }

    if (!moved && bounds && map) {
      try {
        if (typeof map.fitBounds === "function") {
          map.stop();
          map.fitBounds(
            [
              [bounds.west, bounds.south],
              [bounds.east, bounds.north]
            ],
            cameraOptions
          );
          moved = true;
        }
      } catch (fitError) {
        moved = false;
      }
    }

    /*
      Guaranteed fallback:
      malformed, overly broad, or coastal-sprawl bounds can never block the
      map. A simple destination-center jump/ease is always valid.
    */
    if (moved && map && typeof map.getZoom === "function") {
      try {
        if (
          map.getZoom() < RESULT_CAMERA_MIN_ZOOM &&
          typeof map.easeTo === "function"
        ) {
          map.easeTo({
            center: center
              ? [center.lng, center.lat]
              : undefined,
            zoom: RESULT_CAMERA_MIN_ZOOM,
            duration: 360,
            essential: true
          });
        }
      } catch (minimumZoomError) {}
    }

    if (!moved && center) {
      try {
        if (
          controller &&
          typeof controller.setCenter === "function"
        ) {
          moved = controller.setCenter(center, {
            zoom: 11.8,
            duration: 420
          }) !== false;
        }
      } catch (controllerCenterError) {
        moved = false;
      }

      if (!moved && map) {
        try {
          map.stop();

          if (typeof map.jumpTo === "function") {
            map.jumpTo({
              center: [center.lng, center.lat],
              zoom: 11.8,
              bearing: 0,
              pitch: 0
            });
            moved = true;
          } else if (typeof map.flyTo === "function") {
            map.flyTo({
              center: [center.lng, center.lat],
              zoom: 11.8,
              duration: 420,
              essential: true
            });
            moved = true;
          }
        } catch (centerError) {
          moved = false;
        }
      }
    }

    try {
      if (map && typeof map.resize === "function") {
        map.resize();
      }
      if (map && typeof map.triggerRepaint === "function") {
        map.triggerRepaint();
      }
    } catch (resizeError) {}

    recoverMapSurface(
      String(reason || "result-camera") + "-after",
      center
    );

    lastMapRecoveryAt = now();
    lastMapRecoveryReason = String(reason || "result-camera");

    try {
      events.emit("map:result-camera-applied", {
        reason: lastMapRecoveryReason,
        moved: moved,
        resultCount: Array.isArray(results) ? results.length : 0,
        usedResultBounds: !!bounds,
        bounds: bounds,
        center: center
      });
    } catch (eventError) {}

    return moved;
  }

  function scheduleResultMapRecovery(results, destination, reason) {
    var resultList = Array.isArray(results)
      ? results.slice()
      : [];
    var handoff = normalizeDestinationHandoff(
      destination,
      currentCenter({})
    );

    clearDestinationCameraTimers();

    RESULT_CAMERA_DELAYS.forEach(function (delay) {
      destinationCameraTimerIds.push(
        window.setTimeout(function () {
          safeResultCamera(
            resultList,
            handoff || destination,
            String(reason || "scheduled-result-camera") +
              ":" + String(delay)
          );
        }, delay)
      );
    });

    return true;
  }

  function clearDestinationCameraTimers() {
    destinationCameraTimerIds.forEach(function (timerId) {
      window.clearTimeout(timerId);
    });
    destinationCameraTimerIds = [];
  }

  function safeDestinationCamera(destination, reason) {
    var handoff = normalizeDestinationHandoff(
      destination,
      currentCenter({})
    );
    var controller = window.DriveIQAdventureMap;
    var map = rawAdventureMap();
    var moved = false;
    var cameraOptions;

    revealAdventureMapCanvas();

    if (!handoff) {
      return false;
    }

    cameraOptions = {
      duration: 520,
      source: "scan-destination-recovery",
      reason: String(reason || "destination-camera"),
      padding: {
        top: 120,
        right: 42,
        bottom: 260,
        left: 42
      }
    };

    try {
      if (
        controller &&
        typeof controller.focusDestination === "function"
      ) {
        moved = controller.focusDestination(
          handoff,
          cameraOptions
        ) !== false;
      }
    } catch (focusError) {
      moved = false;
    }

    if (!moved && map) {
      try {
        if (
          handoff.bounds &&
          typeof map.fitBounds === "function"
        ) {
          map.fitBounds(
            [
              [handoff.bounds.west, handoff.bounds.south],
              [handoff.bounds.east, handoff.bounds.north]
            ],
            cameraOptions
          );
          moved = true;
        }
      } catch (boundsError) {
        moved = false;
      }
    }

    if (!moved && map) {
      try {
        if (typeof map.flyTo === "function") {
          map.flyTo({
            center: [handoff.lng, handoff.lat],
            zoom: /city|region|lake|corridor/i.test(handoff.entityType)
              ? 10.6
              : /district|beach|park/i.test(handoff.entityType)
                ? 12.2
                : 13.2,
            duration: 520,
            essential: true
          });
          moved = true;
        } else if (typeof map.setCenter === "function") {
          map.setCenter([handoff.lng, handoff.lat]);
          moved = true;
        }
      } catch (flyError) {
        moved = false;
      }
    }

    try {
      if (map && typeof map.resize === "function") {
        map.resize();
      }
    } catch (resizeError) {}

    lastMapRecoveryAt = now();
    lastMapRecoveryReason = String(reason || "destination-camera");

    return moved;
  }

  function scheduleDestinationMapRecovery(destination, reason) {
    var handoff = normalizeDestinationHandoff(
      destination,
      currentCenter({})
    );

    if (!handoff) {
      return false;
    }

    clearDestinationCameraTimers();

    MAP_RECOVERY_DELAYS.forEach(function (delay) {
      destinationCameraTimerIds.push(
        window.setTimeout(function () {
          revealAdventureMapCanvas();
          safeDestinationCamera(
            handoff,
            String(reason || "scheduled-recovery") +
              ":" + String(delay)
          );
        }, delay)
      );
    });

    return true;
  }

  function currentCenter(options) {
    var supplied = options || {};
    var destination = destinationSource(supplied);
    var lastFix = state.get("location.lastFix", null);
    var fallback = (
      validCoordinate(supplied.center) ||
      validCoordinate(lastFix) ||
      {
        lat: config.map.fallbackCenter.lat,
        lng: config.map.fallbackCenter.lng,
        label: "Current map area"
      }
    );
    var normalized = normalizeDestinationHandoff(
      destination,
      fallback
    );

    if (normalized) {
      return {
        lat: normalized.lat,
        lng: normalized.lng,
        label: normalized.label,
        bounds: normalized.bounds,
        radiusMeters: normalized.radiusMeters,
        entityType: normalized.entityType,
        destinationType: normalized.destinationType,
        destinationId: normalized.id,
        canonicalEntity: normalized.canonicalEntity,
        providerCityAuthority: normalized.providerCityAuthority,
        destinationProfile: normalized.destinationProfile,
        marketLabel: normalized.marketLabel,
        __destinationHandoff: normalized
      };
    }

    return fallback;
  }

  function normalizedCategory(value) {
    var category = utils.normText(
      value || config.scan.defaultCategory
    )
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return category || config.scan.defaultCategory;
  }

  function normalizedScope(value) {
    var scope = utils.normText(
      value || config.scan.defaultScope
    )
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return scope || config.scan.defaultScope;
  }

  function googleTextValue(value) {
    if (value == null) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    return String(value.text || value.name || value.value || "");
  }

  function googlePlaceIdFor(item) {
    var id = String(
      item && (
        item.googlePlaceId ||
        item.google_place_id ||
        item.placeId ||
        item.place_id ||
        item.id ||
        item.name ||
        ""
      ) || ""
    ).trim();

    if (id.indexOf("places/") === 0) {
      id = id.slice(7);
    }

    return /^ChI[A-Za-z0-9_-]+/.test(id) ? id : "";
  }

  function googlePhotoReferences(item) {
    var photos = Array.isArray(item && item.photos) ? item.photos : [];

    return photos.map(function (photo) {
      if (typeof photo === "string") {
        return { name: photo };
      }

      return {
        name: String(photo && (photo.name || photo.photoReference || photo.photo_reference) || ""),
        widthPx: Number(photo && (photo.widthPx || photo.width || 0) || 0),
        heightPx: Number(photo && (photo.heightPx || photo.height || 0) || 0),
        authorAttributions: photo && photo.authorAttributions || []
      };
    }).filter(function (photo) {
      return !!photo.name;
    });
  }

  function itemKey(item) {
    var coordinate = validCoordinate(item);
    var googlePlaceId = googlePlaceIdFor(item);

    if (!item) {
      return "";
    }

    return String(
      googlePlaceId ||
      item.providerPlaceId ||
      item.mapboxId ||
      item.id ||
      item.label ||
      item.name ||
      ""
    ) + "|" +
    String(coordinate ? coordinate.lat : "") + "|" +
    String(coordinate ? coordinate.lng : "");
  }

  function validResult(item) {
    var coordinate = validCoordinate(item);
    var label = googleTextValue(
      item && (item.displayName || item.label || item.name)
    );

    return !!(item && coordinate && String(label || "").trim());
  }

  function normalizeResult(item, index) {
    var coordinate = validCoordinate(item);
    var output = {};
    var key;
    var googlePlaceId;
    var displayName;
    var formattedAddress;
    var photos;

    if (!coordinate) {
      return null;
    }

    for (key in item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        output[key] = item[key];
      }
    }

    var providerText = String(
      output.provider ||
      output.sourceProvider ||
      output.inventoryAuthority ||
      output.providerAuthority ||
      ""
    ).toLowerCase();
    var explicitGoogleIdentity = !!(
      output.googlePlaceId ||
      output.google_place_id ||
      /google/.test(providerText)
    );

    googlePlaceId = explicitGoogleIdentity
      ? googlePlaceIdFor(item)
      : "";
    displayName = googleTextValue(
      output.displayName || output.label || output.name
    ) || "Adventure Place";
    formattedAddress = String(
      output.formattedAddress ||
      output.formatted_address ||
      output.shortFormattedAddress ||
      output.address ||
      output.vicinity ||
      ""
    );
    photos = googlePhotoReferences(output);

    output.label = displayName;
    output.name = displayName;
    output.displayName = displayName;
    output.formattedAddress = formattedAddress;
    output.address = output.address || formattedAddress;
    output.lat = coordinate.lat;
    output.lng = coordinate.lng;

    if (googlePlaceId) {
      output.googlePlaceId = googlePlaceId;
      output.placeId = googlePlaceId;
      output.providerPlaceId = googlePlaceId;
      output.provider = "google";
      output.sourceProvider = "google";
      output.primaryType = String(output.primaryType || output.primary_type || "");
      output.primaryTypeDisplayName = googleTextValue(output.primaryTypeDisplayName);
      output.types = Array.isArray(output.types) ? output.types.slice() : [];
      output.rating = Number(output.rating || output.googleRating || 0) || null;
      var rawRatingCount =
        output.userRatingCount ||
        output.user_ratings_total ||
        output.reviewCount ||
        output.ratingCount ||
        0;
      output.userRatingCount = Array.isArray(rawRatingCount)
        ? 0
        : (Number(rawRatingCount) || 0);
      output.businessStatus = String(output.businessStatus || output.business_status || "");
      output.regularOpeningHours = output.regularOpeningHours || output.opening_hours || null;
      output.currentOpeningHours = output.currentOpeningHours || null;
      output.websiteUri = String(output.websiteUri || output.website || "");
      output.googleMapsUri = String(output.googleMapsUri || output.url || "");
      output.nationalPhoneNumber = String(output.nationalPhoneNumber || output.formatted_phone_number || "");
      output.internationalPhoneNumber = String(output.internationalPhoneNumber || output.international_phone_number || "");
      output.photos = photos;
      output.googlePhotoReferences = photos;
      output.providerAuthority = "google-primary";
    } else {
      output.providerAuthority = providerAuthority(
        output.provider || output.sourceProvider || ""
      );
    }

    output.__adventureIndex = index;
    output.__adventureKey = itemKey(output);

    return output;
  }

  function deduplicate(items) {
    var output = [];
    var seen = {};
    var index;
    var normalized;
    var key;

    for (index = 0; index < (items || []).length; index += 1) {
      if (!validResult(items[index])) {
        continue;
      }

      normalized = normalizeResult(items[index], output.length);

      if (!normalized) {
        continue;
      }

      key = normalized.__adventureKey;

      if (!key || seen[key]) {
        continue;
      }

      seen[key] = true;
      output.push(normalized);
    }

    return output;
  }

  function choosePreferred(results, suppliedPreferred) {
    var suppliedKey = itemKey(suppliedPreferred);
    var index;

    if (suppliedKey) {
      for (index = 0; index < results.length; index += 1) {
        if (itemKey(results[index]) === suppliedKey) {
          return results[index];
        }
      }
    }

    return results.length ? results[0] : null;
  }

  function clearTimers() {
    clearRefillWatchdog();

    if (timeoutTimer) {
      window.clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }

    if (progressTimer) {
      window.clearInterval(progressTimer);
      progressTimer = null;
    }
  }

  function isCurrentToken(token) {
    return !!(
      token &&
      activeExecution &&
      activeExecution.token === token &&
      state.get("scan.token", "") === token
    );
  }

  function emitProgress(token, stage, detail, progress) {
    if (!isCurrentToken(token)) {
      return false;
    }

    events.emit("scan:progress", {
      token: token,
      stage: String(stage || "scanning"),
      detail: String(detail || ""),
      progress: typeof progress === "number"
        ? Math.max(0, Math.min(1, progress))
        : null,
      category: activeExecution.category,
      scope: activeExecution.scope,
      center: activeExecution.center,
      fallbackPass: activeExecution.fallbackPass,
      timestamp: now()
    });

    return true;
  }

  function beginProgressHeartbeat(token) {
    var steps = [
      {
        stage: "discovering",
        detail: eventFocusedCategory(activeExecution && activeExecution.category)
          ? "Finding live experiences and real venues"
          : "Finding places worth exploring",
        progress: 0.18
      },
      {
        stage: "checking",
        detail: eventFocusedCategory(activeExecution && activeExecution.category)
          ? "Checking event freshness and confidence"
          : "Checking local confidence",
        progress: 0.38
      },
      {
        stage: "expanding",
        detail: "Expanding the search where needed",
        progress: 0.58
      },
      {
        stage: "ranking",
        detail: eventFocusedCategory(activeExecution && activeExecution.category)
          ? "Ranking what is happening now"
          : "Ranking the strongest places",
        progress: 0.78
      },
      {
        stage: "committing",
        detail: "Dropping the best pins",
        progress: 0.92
      }
    ];

    var index = 0;

    emitProgress(
      token,
      steps[0].stage,
      steps[0].detail,
      steps[0].progress
    );

    progressTimer = window.setInterval(function () {
      if (!isCurrentToken(token)) {
        clearTimers();
        return;
      }

      index = Math.min(index + 1, steps.length - 1);

      emitProgress(
        token,
        steps[index].stage,
        steps[index].detail,
        steps[index].progress
      );
    }, 900);
  }

  function registerProvider(name, provider, options) {
    var providerName = String(name || "").trim();

    if (!providerName) {
      throw new Error("Scan provider name is required.");
    }

    if (typeof provider !== "function") {
      throw new Error(
        "Scan provider " + providerName + " must be a function."
      );
    }

    providers[providerName] = {
      name: providerName,
      run: provider,
      priority: Number(
        options && options.priority
          ? options.priority
          : 100
      ),
      enabled: !(options && options.enabled === false),
      authority: String(
        options && options.authority || providerAuthority(providerName)
      )
    };

    if (providerOrder.indexOf(providerName) < 0) {
      providerOrder.push(providerName);
    }

    providerOrder.sort(function (a, b) {
      var authorityDelta =
        providerAuthorityWeight(a) - providerAuthorityWeight(b);

      if (authorityDelta !== 0) {
        return authorityDelta;
      }

      return providers[a].priority - providers[b].priority;
    });

    log("Provider registered", providerName);

    return function unregisterProvider() {
      unregisterProviderByName(providerName);
    };
  }

  function unregisterProviderByName(name) {
    var providerName = String(name || "");
    var index = providerOrder.indexOf(providerName);

    delete providers[providerName];

    if (index >= 0) {
      providerOrder.splice(index, 1);
    }
  }

  function enabledProviders() {
    return providerOrder.filter(function (name) {
      return providers[name] && providers[name].enabled;
    });
  }

  function canonicalProviderQuery(category, fallbackPass) {
    var key = scanCategoryKey(category);
    var pass = Number(fallbackPass || 0);
    var queries = {
      wedding_venues: pass >= 2
        ? "wedding venues reception venues banquet halls event venues wedding chapels"
        : "wedding venues reception venues banquet halls",
      coffee: pass >= 2
        ? "coffee cafe espresso roaster roastery bakery"
        : "coffee cafe espresso",
      food: pass >= 2
        ? "restaurants food dining breakfast lunch dinner"
        : "restaurants food",
      dessert: pass >= 2
        ? "dessert ice cream gelato bakery donuts sweets"
        : "dessert ice cream bakery",
      waterfront: pass >= 2
        ? "waterfront beach harbor marina boardwalk pier waterfront dining"
        : "waterfront beach harbor marina",
      family_iq: pass >= 2
        ? "family friendly kids attractions aquarium zoo mini golf arcade museum amusement water park playground bowling beach boardwalk boat tours dolphin cruises"
        : "family friendly attractions kids activities aquarium mini golf arcade museum amusement park playground",
      live_music: pass >= 2
        ? "live music tonight concerts bands music venues nightlife"
        : "live music tonight",
      events: pass >= 2
        ? "events tonight concerts live entertainment festivals comedy"
        : "events tonight",
      tonight: pass >= 2
        ? "things to do tonight live music events nightlife karaoke trivia"
        : "things to do tonight",
      karaoke: "karaoke tonight",
      trivia: "trivia night tonight",
      open_mic: "open mic tonight",
      watch_party: "watch party tonight sports bar",
      happy_hour: "happy hour today tonight",
      dj: "dj tonight dance nightlife",
      nightlife: pass >= 2
        ? "nightlife bars cocktail lounge rooftop"
        : "nightlife bars",
      outdoors: pass >= 2
        ? "parks trails nature lake scenic outdoors"
        : "parks trails outdoors",
      hidden_gems: pass >= 2
        ? "hidden gems unique local places independent"
        : "hidden gems local",
      local_faves: pass >= 2
        ? "local favorites independent neighborhood places"
        : "local favorites"
    };

    return clean(
      queries[key] ||
      String(key || category || "").replace(/_/g, " ")
    );
  }

  function queryForPass(execution) {
    var original = clean(execution && execution.originalQuery);
    var canonical = canonicalProviderQuery(
      execution && execution.category,
      execution && execution.fallbackPass
    );

    if (Number(execution && execution.fallbackPass || 0) <= 0) {
      return original || canonical;
    }

    /*
      Fallback passes deliberately stop carrying preference-heavy language.
      Providers receive a clean category query so "coffee near me" cannot
      become an empty or over-constrained discovery request.
    */
    return canonical || original;
  }

  function providerContext(execution, providerName) {
    return {
      token: execution.token,
      category: execution.category,
      scope: execution.scope,
      center: execution.center,
      radiusMeters: execution.radiusMeters,
      fallbackPass: execution.fallbackPass,
      query: execution.query,
      originalQuery: execution.originalQuery || execution.query,
      canonicalQuery: canonicalProviderQuery(
        execution.category,
        execution.fallbackPass
      ),
      resetDiscovery: execution.resetDiscovery === true,
      forceFreshMarket: execution.forceFreshMarket === true,
      intent: execution.intent || null,
      destination: execution.forceFreshMarket
        ? null
        : (
            execution.destinationHandoff ||
            normalizeDestinationHandoff(
              destinationController.current(),
              execution.center
            )
          ),
      lastFix: state.get("location.lastFix", null),
      maximumResults: providerResultLimit(execution),
      marketVolumeProfile: marketVolumeProfile(execution, 0),
      destinationIQ: (
        destinationController &&
        typeof destinationController.intelligence === "function"
      ) ? destinationController.intelligence() : null,
      targetResults: (
        destinationController &&
        typeof destinationController.expectedFor === "function"
      ) ? Number(destinationController.expectedFor(execution.category) || 0) : 0,
      recoveryDecision: (
        destinationController &&
        typeof destinationController.recoveryDecision === "function"
      ) ? destinationController.recoveryDecision(execution.category, 0) : null,
      providerName: providerName,
      providerAuthority: providerAuthority(providerName),
      primaryPlaceProvider: PRIMARY_PLACE_PROVIDER,
      mapGeometryProvider: MAP_GEOMETRY_PROVIDER,
      osmFallbackProvider: OSM_FALLBACK_PROVIDER,
      googlePlacesPrimary: true,
      startedAt: execution.startedAt,
      refillMode: execution.refillMode || "",
      refillAttempt: Number(execution.refillAttempt || 0),
      shownPlaceKeys: Object.keys(shownRecommendationKeys),
      existingPoolCount: recommendationPool.length,
      isCurrent: function () {
        return isCurrentToken(execution.token);
      },
      progress: function (stage, detail, progress) {
        if (execution.calmContinuation === true) {
          try {
            events.emit("recommendations:refill-progress", {
              token: execution.token,
              mode: execution.refillMode || "another",
              stage: stage,
              detail: detail,
              progress: progress
            });
          } catch (error) {}
          return;
        }

        emitProgress(
          execution.token,
          stage,
          detail,
          progress
        );
      }
    };
  }

  function normalizeProviderResponse(response) {
    if (Array.isArray(response)) {
      return {
        results: response,
        preferred: null,
        metadata: {}
      };
    }

    response = response || {};

    return {
      results: Array.isArray(response.results)
        ? response.results
        : [],
      preferred: response.preferred || null,
      metadata: response.metadata || {}
    };
  }

  function runProvider(execution, providerName) {
    var provider = providers[providerName];
    var context;

    if (!provider || !provider.enabled) {
      return Promise.resolve({
        provider: providerName,
        results: [],
        preferred: null,
        metadata: {
          skipped: true
        }
      });
    }

    context = providerContext(execution, providerName);

    if (execution.calmContinuation !== true) {
      emitProgress(
        execution.token,
        "provider",
        "Checking " + providerName,
        null
      );
    }

    return Promise.resolve()
      .then(function () {
        return provider.run(context);
      })
      .then(function (response) {
        var normalized = normalizeProviderResponse(response);

        return {
          provider: providerName,
          results: normalized.results,
          preferred: normalized.preferred,
          metadata: normalized.metadata
        };
      })
      .catch(function (providerError) {
        warn("Provider failed", providerName, providerError);

        return {
          provider: providerName,
          results: [],
          preferred: null,
          metadata: {
            failed: true,
            error: String(
              providerError && providerError.message
                ? providerError.message
                : providerError || "Provider failed"
            )
          }
        };
      });
  }

  function runProvidersSequentially(execution, names) {
    var collected = [];
    var preferred = null;
    var metadata = {};
    var chain = Promise.resolve();
    var orderedNames = (names || []).slice().sort(function (a, b) {
      var authorityDelta =
        providerAuthorityWeight(a) - providerAuthorityWeight(b);

      if (authorityDelta !== 0) {
        return authorityDelta;
      }

      return Number(providers[a] && providers[a].priority || 100) -
        Number(providers[b] && providers[b].priority || 100);
    });

    orderedNames.forEach(function (providerName) {
      chain = chain.then(function () {
        var authority = providerAuthority(providerName);
        var primaryFloor = Math.max(
          6,
          Math.min(18, Number(providerResultLimit(execution) || 18))
        );

        if (!isCurrentToken(execution.token)) {
          return null;
        }

        if (collected.length >= providerResultLimit(execution)) {
          return null;
        }

        /*
          Geoapify/OSM is no longer a normal Adventure inventory source. It is
          permitted only for outdoor/rural categories or when Google and other
          higher-authority sources did not produce a minimally useful pool.
        */
        if (
          authority === "osm-fallback" &&
          !outdoorFallbackAllowed(execution.category) &&
          collected.length >= primaryFloor
        ) {
          metadata[providerName] = {
            skipped: true,
            reason: "google-primary-sufficient",
            authority: authority
          };
          return null;
        }

        return runProvider(execution, providerName)
          .then(function (response) {
            if (!response || !isCurrentToken(execution.token)) {
              return;
            }

            metadata[providerName] = response.metadata || {};
            metadata[providerName].authority = authority;
            collected = collected.concat(response.results || []);

            /*
              V602.4 Partial Result Authority:
              Preserve provider successes immediately instead of waiting for the
              entire sequential provider chain and every fallback pass to finish.
              The global watchdog can safely settle from this pool if a later
              provider or wider-radius pass stalls.
            */
            execution.accumulatedCandidates = deduplicate(
              (execution.accumulatedCandidates || []).concat(
                response.results || []
              )
            );

            if (!preferred && response.preferred) {
              preferred = response.preferred;
            }
          });
      });
    });

    return chain.then(function () {
      return {
        results: collected,
        preferred: preferred,
        metadata: metadata,
        providerAuthority: {
          primary: PRIMARY_PLACE_PROVIDER,
          geometry: MAP_GEOMETRY_PROVIDER,
          osmFallback: OSM_FALLBACK_PROVIDER
        }
      };
    });
  }

  function fallbackRadius(pass, execution) {
    var profile = destinationRadiusProfile(execution);
    var requested = Number(
      execution && execution.requestedRadiusMeters || 0
    );

    if (pass <= 0) {
      return Math.min(
        Number(profile.initial),
        requested > 0
          ? requested
          : Number(profile.initial)
      );
    }

    if (pass === 1) {
      return Number(profile.wider);
    }

    /*
      Every later fallback pass remains at the same honest market ceiling.
      Additional passes may change the query, but may not expand statewide.
    */
    return Number(profile.maximum);
  }

  function localMarketGuard(results, execution) {
    var items = Array.isArray(results) ? results : [];
    var profile = destinationRadiusProfile(execution);
    var maximumMiles = Math.max(
      1,
      Number(profile && profile.hardMiles || 30)
    );
    var kept = [];
    var rejected = [];

    items.forEach(function (item) {
      var miles = distanceMilesValue(item, execution && execution.center);

      /*
        Missing distance evidence is preserved because coordinate validation
        has already occurred elsewhere. Known out-of-market results are not.
      */
      if (miles == null || miles <= maximumMiles) {
        kept.push(item);
      } else {
        rejected.push({
          name: String(item && (item.name || item.label) || ""),
          distanceMiles: Math.round(miles * 10) / 10,
          maximumMiles: maximumMiles
        });
      }
    });

    /*
      Never replace a usable local set with distant filler. When every result
      is outside the market, return an honest empty set rather than six pins
      scattered across a state.
    */
    try {
      events.emit("scan:local-market-guard", {
        token: execution && execution.token || "",
        category: execution && execution.category || "",
        marketLabel: marketLabel(execution),
        administrativeGuard: !!(
          profile && profile.administrativeGuard
        ),
        maximumMiles: maximumMiles,
        inputCount: items.length,
        keptCount: kept.length,
        rejectedCount: rejected.length,
        rejected: rejected
      });
    } catch (error) {}

    return {
      results: kept,
      rejected: rejected,
      maximumMiles: maximumMiles,
      administrativeGuard: !!(
        profile && profile.administrativeGuard
      )
    };
  }

  function hardSettleCompletedScanUI(execution, results, preferred, reason) {
    var items = Array.isArray(results) ? results : [];
    var source = String(reason || "hard-completion-settlement");

    try {
      state.merge("scan", {
        active: false,
        status: "completed",
        completedAt: lastCompletedAt || now(),
        error: null
      }, {
        action: "scan:hard-completed"
      });
    } catch (stateError) {}

    try {
      if (
        window.DriveIQAdventureUI &&
        typeof window.DriveIQAdventureUI.showMapResultsShell === "function"
      ) {
        window.DriveIQAdventureUI.showMapResultsShell(source);
      }
    } catch (uiError) {}

    try {
      events.emit("results:committed", {
        token: execution && execution.token || "",
        results: items,
        count: items.length,
        preferred: preferred || null,
        destination:
          execution && (
            execution.destinationHandoff ||
            execution.center
          ) || null,
        reason: source
      });
    } catch (commitEventError) {}

    try {
      events.emit("scan:settled", {
        token: execution && execution.token || "",
        status: "completed",
        count: items.length,
        reason: source
      });
    } catch (settledEventError) {}

    return true;
  }


  function complete(execution, providerResponse) {
    var results;
    var preferred;
    var committed;

    if (!isCurrentToken(execution.token)) {
      if (execution && execution.isRefill) {
        activeExecution = null;
        releaseRefillState("obsolete-refill-token");
        restoreLastUsableShelf("obsolete-refill-token");
      }

      return false;
    }

    clearTimers();

    var processed = processRecommendations(
      providerResponse && providerResponse.results
        ? providerResponse.results
        : [],
      execution
    );

    var localMarket = localMarketGuard(
      processed.results,
      execution
    );

    results = localMarket.results;
    processed.results = results;
    processed.acceptedCount = results.length;
    processed.rejectedCount =
      Number(processed.rejectedCount || 0) +
      localMarket.rejected.length;
    processed.localMarketRejectedCount =
      localMarket.rejected.length;
    processed.localMarketMaximumMiles =
      localMarket.maximumMiles;
    processed.administrativeMarketGuard =
      localMarket.administrativeGuard;

    lastRecommendationExecution = {
      category: execution.category,
      scope: execution.scope,
      center: execution.center,
      radiusMeters: execution.radiusMeters,
      query: execution.query,
      originalQuery: execution.originalQuery || execution.query,
      resetDiscovery: execution.resetDiscovery === true,
      forceFreshMarket: execution.forceFreshMarket === true,
      intent: execution.intent || null,
      isRefill:
        execution.initialDepthBootstrap === true
          ? false
          : execution.isRefill === true,
      refillMode:
        execution.initialDepthBootstrap === true
          ? ""
          : execution.refillMode || "",
      refillAttempt: Number(execution.refillAttempt || 0),
      calmContinuation: execution.calmContinuation === true,
      initialDepthBootstrap:
        execution.initialDepthBootstrap === true
    };

    resetRecommendationSession(
      results,
      lastRecommendationExecution,
      {
        sessionKey: recommendationContextKey(
          lastRecommendationExecution
        ),
        preserveExisting:
          execution.isRefill === true &&
          execution.forceFreshMarket !== true &&
          execution.resetDiscovery !== true
      }
    );

    results = recommendationPool.slice();

    preferred = choosePreferred(
      results,
      providerResponse
        ? providerResponse.preferred
        : null
    );

    committed = state.commitScanResults(
      execution.token,
      results,
      preferred
    );

    if (!committed) {
      if (execution.isRefill) {
        activeExecution = null;
        releaseRefillState("commit-rejected");
        restoreLastUsableShelf("commit-rejected");

        try {
          events.emit("recommendations:continuation-settled", {
            token: execution.token,
            mode: execution.refillMode || refillMode || "another",
            poolCount: recommendationPool.length,
            batchCount: 0,
            remainingCount: recommendationRemainingCount(),
            settlement: "commit-rejected"
          });
        } catch (commitRejectedEventError) {}
      }

      return false;
    }

    lastCommittedResults = results.slice();
    lastCompletedAt = now();
    presentationState.token = execution.token;
    presentationState.resultCount = results.length;
    presentationState.ready = false;
    presentationState.pinsPainted = false;
    presentationState.mapVisible = false;
    scheduleResultsPillSync("results-committed-pending-presentation");
    rememberFreshMarketResults(results, execution);
    activeExecution = null;

    if (execution.isRefill) {
      var newUnseenCount = newRecommendationCountSinceRefill();
      refillMode = execution.refillMode || refillMode;
      releaseRefillState("completed");

      if (newUnseenCount > 0) {
        /*
          A successful refill may add one, two, or three places. Any positive
          growth is useful and must be shown immediately.
        */
        setRefillAttemptCount(0);
      }

      try {
        events.emit("recommendations:refill-completed", {
          mode: refillMode,
          attempt: Number(execution.refillAttempt || 0),
          poolCount: recommendationPool.length,
          remainingCount: recommendationRemainingCount(),
          newUnseenCount: newUnseenCount,
          sessionKey: activeRecommendationSessionKey
        });
      } catch (refillEventError) {}

      /*
        Zero-growth refill:
        do not repaint an empty shelf or declare exhaustion after one duplicate
        provider response. Continue the controlled expansion until a new place
        appears or the session reaches the honest attempt limit.
      */
      if (
        execution.initialDepthBootstrap !== true &&
        newUnseenCount === 0 &&
        recommendationRemainingCount() === 0 &&
        refillAttemptCount() < MAX_REFILL_ATTEMPTS
      ) {
        clearRefillExhaustionTimer();

        window.setTimeout(function () {
          if (!continuationLoadingSurfaceActive()) {
            requestRecommendationRefill(
              refillMode || "another",
              refillReference
            );
          } else if (!refillInProgress && !activeExecution) {
            requestRecommendationRefill(
              refillMode || "another",
              refillReference
            );
          }
        }, REFILL_RETRY_DELAY_MS);
      } else if (
        execution.initialDepthBootstrap !== true &&
        newUnseenCount === 0 &&
        recommendationRemainingCount() === 0 &&
        refillAttemptCount() >= MAX_REFILL_ATTEMPTS
      ) {
        finalizeRefillExhaustion(
          refillMode || execution.refillMode || "another",
          "attempt-limit-zero-growth"
        );
      }
    }

    /*
      Calm refill completion:
      do not enter the global completion lifecycle, do not move the camera,
      and do not tear down the shelf. Merge the new pool, paint pins once,
      then replace the visible batch once.
    */
    if (
      execution.isRefill &&
      execution.calmContinuation === true &&
      execution.initialDepthBootstrap === true
    ) {
      stopRefillShelfGuard();
      releaseRefillState("initial-depth-completed");
      advanceDiscoveryRound(execution);

      setShelfState({
        visible: false,
        minimized: true,
        userClosed: false,
        explicitOpen: false,
        available: recommendationPool.length > 0
      }, "initial-depth-completed");

      try {
        events.emit("recommendations:initial-depth-settled", {
          token: execution.token,
          sessionKey: activeRecommendationSessionKey,
          poolCount: recommendationPool.length,
          newUnseenCount: newRecommendationCountSinceRefill(),
          category: execution.category
        });
      } catch (error) {}

      window.setTimeout(function () {
        try {
          revealAdventureMapCanvas();
          paintCommittedResults(results);
        } catch (paintError) {}
      }, 0);

      return true;
    }

    if (
      execution.isRefill &&
      execution.calmContinuation === true
    ) {
      var continuationBatch = [];

      stopRefillShelfGuard();

      if (recommendationRemainingCount() > 0) {
        continuationBatch = batchFromPool(
          refillMode || execution.refillMode || "another",
          refillReference,
          {
            allowRefill: false,
            source: "calm-refill-completed"
          }
        );
      }

      if (continuationBatch.length) {
        advanceDiscoveryRound(execution);

        renderResultShelf(continuationBatch, {
          decisionMode:
            refillMode ||
            execution.refillMode ||
            "another",
          totalCount: recommendationPool.length,
          remainingCount: recommendationRemainingCount(),
          reason: "calm-refill-completed",
          atomicRefillCommit: true
        });
      } else {
        restoreLastUsableShelf(
          "calm-refill-no-new-batch"
        );
      }

      window.setTimeout(function () {
        try {
          revealAdventureMapCanvas();
          paintCommittedResults(results);
        } catch (paintError) {}
      }, 0);

      try {
        events.emit("recommendations:continuation-settled", {
          token: execution.token,
          mode:
            refillMode ||
            execution.refillMode ||
            "another",
          poolCount: recommendationPool.length,
          batchCount: continuationBatch.length,
          remainingCount: recommendationRemainingCount(),
          marketClass: marketVolumeProfile(
            execution,
            recommendationPool.length
          ).marketClass
        });
      } catch (settledError) {}

      log(
        "Calm continuation completed",
        recommendationPool.length,
        "pool results",
        execution.refillMode
      );

      return true;
    }

    setShelfState({
      visible: false,
      minimized: false,
      userClosed: false
    }, "scan-completed");

    hardSettleCompletedScanUI(
      execution,
      results,
      preferred,
      "results-committed"
    );

    try {
      events.emit("scan:completed", {
      token: execution.token,
      category: execution.category,
      scope: execution.scope,
      center: execution.center,
      destination: execution.destinationHandoff ||
        normalizeDestinationHandoff(
          destinationController.current(),
          execution.center
        ),
      mapCameraSafe: true,
      radiusMeters: execution.radiusMeters,
      fallbackPass: execution.fallbackPass,
      results: results,
      preferred: preferred,
      metadata: {
        providers: providerResponse
          ? providerResponse.metadata || {}
          : {},
        categoryTruth: {
          category: processed.category,
          acceptedCount: processed.acceptedCount,
          rejectedCount: processed.rejectedCount,
          targetCount: processed.targetCount,
          rawCount: processed.rawCount,
          districtAnchoredCount: processed.districtAnchoredCount,
          experienceRichCount: processed.experienceRichCount,
          lowestIncludedScore: processed.lowestIncludedScore,
          primaryRecommendationCount: Math.min(
            PRIMARY_RECOMMENDATION_COUNT,
            processed.results.length
          ),
          recommendationSessionKey: activeRecommendationSessionKey,
          recommendationPoolCount: recommendationPool.length,
          unseenRecommendationCount: recommendationRemainingCount(),
          preservesShownHistory: true,
          localMarketRejectedCount:
            Number(processed.localMarketRejectedCount || 0),
          localMarketMaximumMiles:
            Number(processed.localMarketMaximumMiles || 0),
          administrativeMarketGuard:
            processed.administrativeMarketGuard === true
        }
      },
      completedAt: lastCompletedAt
      });
    } catch (scanCompletedEventError) {
      warn(
        "scan:completed listener failed after results were committed",
        scanCompletedEventError
      );
    }

    hardSettleCompletedScanUI(
      execution,
      results,
      preferred,
      "scan-completed-delivered"
    );

    scheduleResultMapRecovery(
      results,
      execution.destinationHandoff ||
      normalizeDestinationHandoff(
        destinationController.current(),
        execution.center
      ) ||
      execution.center,
      "scan-completed"
    );

    shelfPaintTimer = window.setTimeout(function () {
      shelfPaintTimer = null;

      /*
        V600.77 Scan Presentation Authority:
        one ordered settlement path owns the map, pins, menu, and Places pill.
        The shelf remains closed until the user opens it from the Places pill.
      */
      revealAdventureMapCanvas();
      paintCommittedResults(results);
      scheduleResultMapRecovery(
        results,
        execution.destinationHandoff ||
        execution.center,
        "results-painted"
      );
      finalizeScanPresentation(
        execution,
        results,
        "results-painted"
      );
    }, FIRST_PAINT_DELAY_MS);

    log(
      "Completed",
      results.length,
      "results",
      execution.category,
      execution.scope
    );

    /*
      V600.80 Single-Scan Lifecycle Authority:
      Do not launch an automatic second scan after the initial result session has
      completed. That hidden beginScan lifecycle can reactivate the global
      SCANNING surface while the first session's pins are already visible.

      Sparse-market expansion will be rebuilt inside the original provider pass.
      User-driven continuation actions remain available after presentation.
    */
    try {
      events.emit("recommendations:initial-depth-deferred", {
        token: execution.token,
        category: execution.category,
        committedCount: results.length,
        reason: "single-scan-lifecycle"
      });
    } catch (depthDeferredError) {}

    return true;
  }

  function fail(execution, scanError) {
    var message;

    if (!execution || !isCurrentToken(execution.token)) {
      return false;
    }

    clearTimers();

    if (
      execution.isRefill &&
      execution.calmContinuation === true
    ) {
      activeExecution = null;
      releaseRefillState("calm-continuation-failed");
      restoreLastUsableShelf(
        "calm-continuation-failed"
      );

      try {
        events.emit("recommendations:continuation-failed", {
          token: execution.token,
          mode: execution.refillMode || "another",
          message: String(
            scanError && scanError.message
              ? scanError.message
              : scanError || "Continuation failed"
          )
        });
      } catch (error) {}

      return false;
    }

    message = String(
      scanError && scanError.message
        ? scanError.message
        : scanError || "Adventure scan failed"
    );

    state.failScan(execution.token, message);
    resetPresentationState("scan-failed", execution.token);
    scheduleResultsPillSync("scan-failed");
    activeExecution = null;

    if (execution.isRefill) {
      releaseRefillState("failed");
      restoreLastUsableShelf("refill-failed");
    } else {
      stopShelfWatchdog();
      closeResultShelf("scan-failed");
    }

    events.emit("scan:failed", {
      token: execution.token,
      category: execution.category,
      scope: execution.scope,
      center: execution.center,
      message: message,
      failedAt: now()
    });

    warn("Failed", message);

    return true;
  }

  function executePass(execution) {
    var names = enabledProviders();

    if (!names.length) {
      return Promise.reject(
        new Error(
          "No Adventure scan provider has been registered."
        )
      );
    }

    execution.radiusMeters = fallbackRadius(
      execution.fallbackPass,
      execution
    );
    execution.query = queryForPass(execution);

    state.merge("scan", {
      fallbackPass: execution.fallbackPass,
      radiusMeters: execution.radiusMeters,
      query: execution.query,
      forceFreshMarket: execution.forceFreshMarket === true,
      resetDiscovery: execution.resetDiscovery === true,
      status: execution.fallbackPass > 0
        ? "expanding"
        : "scanning"
    }, {
      action: "scan:pass:" + execution.fallbackPass
    });

    return runProvidersSequentially(
      execution,
      names
    ).then(function (response) {
      var passResults = deduplicate(response.results || []);
      var unique;
      var floor = rawFallbackFloor(execution);
      var trustedMinimum = trustedFallbackMinimum(execution);
      var preview;
      var trustedCount;
      var rawSparse;
      var trustedSparse;
      var shouldExpand;

      if (!isCurrentToken(execution.token)) {
        return response;
      }

      /*
        Preserve every unique candidate gathered by earlier radius passes.
        A wider pass supplements the local pool instead of replacing it.
      */
      execution.accumulatedCandidates = deduplicate(
        (execution.accumulatedCandidates || []).concat(passResults)
      );
      unique = execution.accumulatedCandidates.slice();

      /*
        Raw volume alone is not enough. A provider can return dozens of loose
        category matches while Category Truth leaves only eight real places.
        Preview the trusted pool before deciding that a dense market is done.
      */
      preview = processRecommendations(
        unique.slice(),
        execution
      );
      trustedCount = preview && Array.isArray(preview.results)
        ? preview.results.length
        : 0;

      rawSparse = unique.length < floor;
      trustedSparse = trustedCount < trustedMinimum;

      shouldExpand = !!(
        execution.fallbackPass < MAX_FALLBACK_PASSES &&
        (
          rawSparse ||
          trustedSparse
        ) &&
        !(
          localRadiusProfile(execution) &&
          unique.length > 0 &&
          trustedCount >= trustedMinimum
        )
      );

      if (shouldExpand) {
        execution.fallbackPass += 1;

        emitProgress(
          execution.token,
          "expanding",
          trustedSparse
            ? "Finding more trusted places"
            : "Expanding the search radius",
          0.62 + execution.fallbackPass * 0.10
        );

        return new Promise(function (resolve) {
          window.setTimeout(
            resolve,
            Math.min(
              250,
              Number(config.scan.fallbackDelayMs || 0)
            )
          );
        }).then(function () {
          return executePass(execution);
        });
      }

      response.results = unique;
      response.metadata = response.metadata || {};
      response.metadata.scanStrategy = {
        rawCandidateCount: unique.length,
        trustedPreviewCount: trustedCount,
        trustedFallbackMinimum: trustedMinimum,
        rawFallbackFloor: floor,
        rawSparse: rawSparse,
        trustedSparse: trustedSparse,
        fallbackPass: execution.fallbackPass,
        providerQuery: execution.query,
        originalQuery: execution.originalQuery || "",
        forceFreshMarket: execution.forceFreshMarket === true,
        resetDiscovery: execution.resetDiscovery === true,
        categoryRadiusProfile: localRadiusProfile(execution),
        providerResultLimit: providerResultLimit(execution),
        marketLabel: marketLabel(execution),
        marketVolumeProfile: marketVolumeProfile(
          execution,
          trustedCount
        ),
        settledLocally: !!(
          localRadiusProfile(execution) &&
          unique.length > 0
        ),
        marketClass: marketVolumeProfile(
          execution,
          trustedCount
        ).marketClass
      };

      return response;
    });
  }

  function start(options) {
    var supplied = options || {};
    var isRefill = supplied.preserveResults === true ||
      !!supplied.refillMode;
    var calmContinuation = !!(
      isRefill &&
      supplied.calmContinuation === true
    );

    if (!isRefill) {
      resetPresentationState(
        "new-scan",
        ""
      );
      scheduleResultsPillSync("new-scan");
      resetFreshMarketRecommendationState(
        supplied.forceFreshMarket === true
          ? "force-fresh-market"
          : supplied.resetDiscovery === true
            ? "reset-discovery"
            : "new-destination-scan"
      );
      closeResultShelf("new-scan");
    }

    if (!calmContinuation) {
      setShelfState({
        visible: false,
        minimized: false,
        userClosed: false
      }, "new-scan-pending");
    }

    if (!isRefill) {
      try {
        if (
          window.DriveIQAdventurePins &&
          typeof window.DriveIQAdventurePins.clear === "function"
        ) {
          window.DriveIQAdventurePins.clear();
        }
      } catch (pinClearError) {}
    }

    var category = normalizedCategory(supplied.category);
    var scope = normalizedScope(supplied.scope);
    var center = currentCenter(supplied);
    var destinationHandoff = normalizeDestinationHandoff(
      destinationSource(supplied),
      center
    );
    var token;

    revealAdventureMapCanvas();

    if (!calmContinuation) {
      scheduleDestinationMapRecovery(
        destinationHandoff || center,
        "scan-start"
      );
    }

    cancel("replaced-by-new-scan");

    token = state.beginScan({
      category: category,
      scope: scope,
      center: center,
      radiusMeters: Number(
        (
          LOCAL_CATEGORY_RADIUS_METERS[
            scanCategoryKey(category)
          ] &&
          LOCAL_CATEGORY_RADIUS_METERS[
            scanCategoryKey(category)
          ].initial
        ) ||
        supplied.radiusMeters ||
        config.scan.defaultRadiusMeters
      )
    });

    if (!isRefill) {
      resetPresentationState("scan-token-created", token);
    }

    activeExecution = {
      token: token,
      category: category,
      scope: scope,
      center: center,
      destinationHandoff: destinationHandoff,
      radiusMeters: Number(
        supplied.radiusMeters ||
        destinationHandoff && destinationHandoff.radiusMeters ||
        config.scan.defaultRadiusMeters
      ),
      requestedRadiusMeters: Number(
        supplied.radiusMeters || 0
      ),
      fallbackPass: 0,
      query: clean(
        supplied.query ||
        canonicalProviderQuery(category, 0)
      ),
      originalQuery: clean(
        supplied.query ||
        canonicalProviderQuery(category, 0)
      ),
      resetDiscovery: supplied.resetDiscovery === true,
      forceFreshMarket: supplied.forceFreshMarket === true,
      intent: supplied.intent || null,
      startedAt: now(),
      isRefill: isRefill,
      refillMode: String(supplied.refillMode || ""),
      refillAttempt: Number(supplied.refillAttempt || 0),
      calmContinuation: calmContinuation,
      initialDepthBootstrap:
        supplied.initialDepthBootstrap === true,
      accumulatedCandidates: []
    };

    if (
      activeExecution.forceFreshMarket ||
      activeExecution.resetDiscovery
    ) {
      recommendationPool = [];
      shelfBatch = [];
      shelfBatchCursor = 0;
      shownRecommendationKeys = {};
      recommendationExhausted = false;
      refillInProgress = false;
      refillMode = "";
      refillReference = null;
      refillBaselineKeys = {};
      lastUsableShelfBatch = [];
      lastUsableShelfMode = "top";
      releaseRefillState("fresh-session-reset");

      try {
        delete refillAttemptsBySession[
          recommendationContextKey(activeExecution)
        ];
      } catch (resetError) {}
    }

    resetRecommendationSession(
      [],
      activeExecution,
      {
        sessionKey: recommendationContextKey(activeExecution),
        preserveExisting:
          !activeExecution.forceFreshMarket &&
          !activeExecution.resetDiscovery
      }
    );

    if (isRefill) {
      shelfBatchMode = activeExecution.refillMode || "another";
    }

    if (!calmContinuation) {
      events.emit("scan:started", {
        token: token,
        category: category,
        scope: scope,
        center: center,
        radiusMeters: activeExecution.radiusMeters,
        query: activeExecution.query,
        originalQuery: activeExecution.originalQuery,
        resetDiscovery: activeExecution.resetDiscovery,
        forceFreshMarket: activeExecution.forceFreshMarket,
        startedAt: activeExecution.startedAt
      });

      beginProgressHeartbeat(token);
    } else {
      try {
        events.emit("recommendations:continuation-started", {
          token: token,
          mode: activeExecution.refillMode || "another",
          category: category,
          scope: scope,
          center: center,
          radiusMeters: activeExecution.radiusMeters,
          startedAt: activeExecution.startedAt
        });
      } catch (error) {}
    }

    timeoutTimer = window.setTimeout(function () {
      var execution;
      var partial;

      if (!isCurrentToken(token)) {
        return;
      }

      execution = activeExecution;
      partial = execution && Array.isArray(execution.accumulatedCandidates)
        ? deduplicate(execution.accumulatedCandidates)
        : [];

      /*
        V602.4 Timeout Rescue Authority:
        A slow later provider/fallback must never erase places that a successful
        earlier provider already found. Settle the current scan from the strongest
        accumulated pool. The existing complete() path still owns Category Truth,
        ranking, local-market filtering, result commit, pins, and presentation.
      */
      if (execution && partial.length > 0) {
        complete(execution, {
          results: partial,
          preferred: null,
          metadata: {
            timeoutRescued: true,
            partialCommit: true,
            rescuedCandidateCount: partial.length,
            fallbackPass: Number(execution.fallbackPass || 0),
            providerQuery: String(execution.query || "")
          }
        });
        return;
      }

      fail(
        execution,
        new Error("Adventure scan timed out.")
      );
    }, Math.max(20000, Number(config.scan.requestTimeoutMs || 30000)));

    executePass(activeExecution)
      .then(function (response) {
        if (isCurrentToken(token)) {
          complete(activeExecution, response);
        }
      })
      .catch(function (scanError) {
        if (isCurrentToken(token)) {
          fail(activeExecution, scanError);
        }
      });

    return token;
  }

  function cancel(reason) {
    var execution = activeExecution;

    if (!execution) {
      clearTimers();
      return false;
    }

    clearTimers();

    activeExecution = null;

    if (execution.isRefill) {
      releaseRefillState(
        execution.calmContinuation === true
          ? "calm-continuation-cancelled"
          : "refill-cancelled"
      );
      restoreCurrentShelf("refill-cancelled");
    } else {
      stopShelfWatchdog();
      closeResultShelf("scan-cancelled");
    }

    if (state.get("scan.token", "") === execution.token) {
      state.merge("scan", {
        active: false,
        completedAt: now(),
        status: "cancelled",
        error: null
      }, {
        action: "scan:cancel"
      });
    }

    if (execution.calmContinuation !== true) {
      events.emit("scan:cancelled", {
        token: execution.token,
        reason: String(reason || "cancelled"),
        cancelledAt: now()
      });
    } else {
      try {
        events.emit("recommendations:continuation-cancelled", {
          token: execution.token,
          mode: execution.refillMode || "another",
          reason: String(reason || "cancelled"),
          cancelledAt: now()
        });
      } catch (error) {}
    }

    return true;
  }

  function current() {
    if (!activeExecution) {
      return null;
    }

    return {
      token: activeExecution.token,
      category: activeExecution.category,
      scope: activeExecution.scope,
      center: activeExecution.center,
      destination: activeExecution.destinationHandoff,
      mapCameraSafe: !!activeExecution.destinationHandoff,
      radiusMeters: activeExecution.radiusMeters,
      fallbackPass: activeExecution.fallbackPass,
      query: activeExecution.query,
      startedAt: activeExecution.startedAt
    };
  }

  function ingestLegacyResults(results, options) {
    var supplied = options || {};
    var execution = activeExecution;
    var token;

    if (!execution) {
      token = state.beginScan({
        category: normalizedCategory(supplied.category),
        scope: normalizedScope(supplied.scope),
        center: currentCenter(supplied),
        destinationHandoff: normalizeDestinationHandoff(
          destinationSource(supplied),
          currentCenter(supplied)
        ),
        radiusMeters: Number(
          supplied.radiusMeters ||
          config.scan.defaultRadiusMeters
        )
      });

      execution = {
        token: token,
        category: normalizedCategory(supplied.category),
        scope: normalizedScope(supplied.scope),
        center: currentCenter(supplied),
        radiusMeters: Number(
          supplied.radiusMeters ||
          config.scan.defaultRadiusMeters
        ),
        fallbackPass: Number(supplied.fallbackPass || 0),
        query: String(supplied.query || ""),
        startedAt: now()
      };

      activeExecution = execution;

      events.emit("scan:started", {
        token: token,
        category: execution.category,
        scope: execution.scope,
        center: execution.center,
        radiusMeters: execution.radiusMeters,
        query: execution.query,
        source: "legacy-ingest",
        startedAt: execution.startedAt
      });
    }

    return complete(execution, {
      results: Array.isArray(results) ? results : [],
      preferred: supplied.preferred || null,
      metadata: {
        source: "legacy-ingest"
      }
    });
  }

  events.on("pin:selected", function () {
    minimizeResultShelf("pin-selected");
  });

  events.on("place:opened", function () {
    minimizeResultShelf("place-opened");
  });

  events.on("place:closed", function () {
    if (
      shelfState.minimized &&
      !shelfState.userClosed
    ) {
      window.setTimeout(function () {
        restoreCurrentShelf("place-closed");
      }, 40);
    }
  });

  events.on("map:ready", function () {
    if (shouldRestoreShelf()) {
      window.setTimeout(function () {
        restoreCurrentShelf("map-ready");
      }, 80);
    }
  });

  events.on("map:style-ready", function () {
    if (shouldRestoreShelf()) {
      window.setTimeout(function () {
        restoreCurrentShelf("map-style-ready");
      }, 120);
    }
  });

  events.on("destination:committed", function (event) {
    var destination = event && event.payload
      ? event.payload
      : null;
    var handoff;

    if (!destination) {
      return;
    }

    handoff = normalizeDestinationHandoff(
      destination,
      state.get("location.lastFix", null)
    );

    if (!handoff) {
      return;
    }

    lastSafeDestinationHandoff = handoff;

    state.merge("scan", {
      center: {
        lat: handoff.lat,
        lng: handoff.lng,
        label: handoff.label,
        bounds: handoff.bounds,
        radiusMeters: handoff.radiusMeters,
        entityType: handoff.entityType,
        destinationId: handoff.id,
        __destinationHandoff: handoff
      },
      scope: "near_destination"
    }, {
      action: "scan:destination-center-v600.53"
    });

    scheduleDestinationMapRecovery(
      handoff,
      "destination-committed"
    );
  });

  try {
    events.on("ui:destination-editing-started", function () {
      suspendShelfForDestinationEditing(
        "ui-destination-editing-started"
      );
    });

    events.on("ui:destination-editing-ended", function () {
      stopShelfWatchdog();
    });

    events.on("destination:committed", function () {
      if (!destinationEditingActive()) {
        startShelfWatchdog();
      }
    });

    events.on("destination:edit-cancelled", function () {
      if (!destinationEditingActive()) {
        startShelfWatchdog();
      }
    });

    events.on("destination:cleared", function () {
      lastCommittedResults = [];
      scheduleResultsPillSync("destination-cleared");
      recommendationPool = [];
      shelfBatch = [];
      lastUsableShelfBatch = [];
      activeRecommendationSessionKey = "";
      shownRecommendationKeys = {};
      recommendationExhausted = false;

      setShelfState({
        visible: false,
        minimized: false,
        userClosed: true,
        explicitOpen: false,
        available: false
      }, "destination-cleared");

      closeResultShelf("destination-cleared");

      try {
        state.merge("results", {
          items: [],
          count: 0,
          committed: false
        }, {
          action: "scan:destination-cleared"
        });
      } catch (error) {}
    });
  } catch (eventBindingError) {}

  try {
    [
      "ui:destination-editing-started",
      "destination:edit-started",
      "destination:edit-query-changed",
      "ui:mode-changed",
      "destination:cleared",
      "ui:map-results-shell-shown"
    ].forEach(function (eventName) {
      events.on(eventName, function (event) {
        var payload = event && event.payload || {};
        var mode = String(payload.mode || "");

        if (
          eventName === "ui:mode-changed" &&
          mode &&
          mode !== "menu" &&
          mode !== "destination_editing"
        ) {
          stopRecommendationOverlayGuard();
          return;
        }

        if (mainAdventureMenuVisible()) {
          startRecommendationOverlayGuard(eventName);
        }
      });
    });
  } catch (recommendationGuardBindingError) {}

  window.DriveIQAdventureScan = {
    committedResultCoordinates: committedResultCoordinates,
    resultDerivedBounds: resultDerivedBounds,
    safeResultCamera: safeResultCamera,
    scheduleResultMapRecovery: scheduleResultMapRecovery,

    suppressStaleRecommendationOverlays: suppressStaleRecommendationOverlays,
    startRecommendationOverlayGuard: startRecommendationOverlayGuard,
    stopRecommendationOverlayGuard: stopRecommendationOverlayGuard,

    marketVolumeProfile: marketVolumeProfile,
    providerResultLimit: providerResultLimit,

    openResultShelf: openResultShelf,
    toggleResultShelf: toggleResultShelf,
    isResultShelfVisible: resultShelfVisible,
    resultShelfAvailable: function () {
      return shelfState.available === true &&
        lastCommittedResults.length > 0;
    },
    syncResultsPill: scheduleResultsPillSync,
    resultsPillVisible: function () {
      var pill = document.getElementById(RESULTS_PILL_ID);
      return !!(
        pill &&
        pill.classList.contains("on") &&
        pill.getAttribute("aria-hidden") !== "true"
      );
    },

    destinationEditingActive: destinationEditingActive,
    suspendShelfForDestinationEditing: suspendShelfForDestinationEditing,
    registerProvider: registerProvider,
    recommendationSignals: recommendationSignals,
    strongestDecisionSignal: strongestDecisionSignal,
    normalizeDestinationHandoff: normalizeDestinationHandoff,
    recoverDestinationMap: function (destination, reason) {
      return scheduleDestinationMapRecovery(
        destination ||
        lastSafeDestinationHandoff ||
        destinationController.current(),
        reason || "manual-recovery"
      );
    },
    revealMap: revealAdventureMapCanvas,
    unregisterProvider: unregisterProviderByName,
    start: start,
    cancel: cancel,
    current: current,
    ingestLegacyResults: ingestLegacyResults,
    isCurrentToken: isCurrentToken,
    renderResultShelf: renderResultShelf,
    hideResultShelf: hideResultShelf,
    minimizeResultShelf: minimizeResultShelf,
    closeResultShelf: closeResultShelf,
    restoreResultShelf: restoreCurrentShelf,
    openResult: openResult,
    processRecommendations: processRecommendations,
    categoryTruth: categoryTruth,
    showAnotherThree: function () {
      return showRecommendationBatch("another");
    },
    showCloser: function () {
      return showRecommendationBatch("closer");
    },
    showMoreLocal: function () {
      return showRecommendationBatch("local");
    },
    showSimilar: function (reference) {
      return showRecommendationBatch(
        "similar",
        reference || currentSelectedResult()
      );
    },
    recommendationPool: function () {
      return recommendationPool.slice();
    },
    recommendationRemaining: recommendationRemainingCount,
    discoveryRound: function () {
      return currentDiscoveryRound(lastRecommendationExecution);
    },
    maxDiscoveryRounds: MAX_DISCOVERY_ROUNDS,
    recommendationSessionKey: function () {
      return activeRecommendationSessionKey;
    },
    recommendationHistory: function () {
      return Object.assign({}, shownRecommendationKeys);
    },
    refillRecommendations: requestRecommendationRefill,
    setCalmContinuationLoading: setCalmContinuationLoading,
    isRefilling: function () {
      return refillInProgress;
    },
    experienceSignals: experienceSignals,
    districtAnchorScore: districtAnchorScore,
    proximityValueScore: proximityValueScore,
    curatedBatch: curatedBatch,
    isDistrictEntity: isDistrictEntity,
    districtAllowedForCategory: districtAllowedForCategory,
    civicEmergencyEntity: civicEmergencyEntity,
    releaseRefillState: releaseRefillState,
    restoreLastUsableShelf: restoreLastUsableShelf,
    showResultShelf: function () {
      setShelfState({
        visible: false,
        minimized: true,
        userClosed: false
      }, "explicit-show");

      return restoreCurrentShelf("explicit-show");
    },
    isResultShelfVisible: resultShelfVisible,
    repaintCommittedResults: function () {
      paintCommittedResults(
        state.get("results.items", lastCommittedResults) || []
      );
      return true;
    },
    status: function () {
      return {
        build: "V602_2_BULLETPROOF_RESULT_AUTHORITY",
        active: !!activeExecution,
        providerCount: enabledProviders().length,
        lastResultCount: lastCommittedResults.length,
        lastCompletedAt: lastCompletedAt,
        photoPrewarmLimit: PHOTO_PREWARM_LIMIT,
        firstPaintDelayMs: FIRST_PAINT_DELAY_MS,
        scanEngine: "6.0",
        categoryProfiles: Object.keys(CATEGORY_PROFILES).length,
        rawFallbackFloor: RAW_FALLBACK_FLOOR,
        touristRawFallbackFloor: TOURIST_RAW_FALLBACK_FLOOR,
        majorRawFallbackFloor: MAJOR_RAW_FALLBACK_FLOOR,
        typicalCityRange: [
          TYPICAL_CITY_MIN_RESULTS,
          TYPICAL_CITY_MAX_RESULTS
        ],
        touristRange: [
          TOURIST_MIN_RESULTS,
          TOURIST_MAX_RESULTS
        ],
        majorMetroRange: [
          MAJOR_CITY_MIN_RESULTS,
          MAJOR_CITY_MAX_RESULTS
        ],
        smallTownRange: [
          SMALL_TOWN_MIN_RESULTS,
          SMALL_TOWN_MAX_RESULTS
        ],
        progressiveThresholds: PROGRESSIVE_SCORE_THRESHOLDS.slice(),
        primaryRecommendationCount: PRIMARY_RECOMMENDATION_COUNT,
        initialShelfMaxResults: INITIAL_SHELF_MAX_RESULTS,
        discoveryRoundResultCount: DISCOVERY_ROUND_RESULT_COUNT,
        maxDiscoveryRounds: MAX_DISCOVERY_ROUNDS,
        currentDiscoveryRound: currentDiscoveryRound(lastRecommendationExecution),
        refillWatchdogMs: REFILL_WATCHDOG_MS,
        refillShelfGuardActive: !!refillShelfGuardTimer,
        refillSettlementLock: true,
        singleRefillSettlementAuthority: true,
        atomicShelfRefillAuthority: true,
        calmContinuationLoadingActive: calmContinuationLoadingActive,
        refillLoadingButtonMode: refillLoadingButtonMode,
        refillExhaustionTimerActive: !!refillExhaustionTimer,
        fullPoolShelfAuthority: true,
        topShelfShowsOfCount: false,
        resultCameraLocalMaxMiles: RESULT_CAMERA_LOCAL_MAX_MILES,
        resultCameraDestinationMaxMiles: RESULT_CAMERA_DESTINATION_MAX_MILES,
        maxFallbackPasses: MAX_FALLBACK_PASSES,
        freshMarketHistorySessions: Object.keys(freshMarketSeenKeysBySession).length,
        defaultFoodChainCount: DEFAULT_FOOD_CHAIN_TERMS.length,
        recommendationPoolCount: recommendationPool.length,
        recommendationRemainingCount: recommendationRemainingCount(),
        recommendationShownCount: Object.keys(shownRecommendationKeys).length,
        recommendationSessionKey: activeRecommendationSessionKey,
        recommendationSessionCount: Object.keys(recommendationSessions).length,
        recommendationExhausted: recommendationExhausted,
        refillInProgress: refillInProgress,
        refillMode: refillMode,
        refillAttemptCount: refillAttemptCount(),
        maxRefillAttempts: MAX_REFILL_ATTEMPTS,
        refillTargetCount: REFILL_TARGET_COUNT,
        refillWatchdogActive: !!refillWatchdogTimer,
        refillWatchdogMs: REFILL_WATCHDOG_MS,
        refillRequestedAt: refillRequestedAt,
        refillBaselineCount: Object.keys(refillBaselineKeys).length,
        lastUsableShelfCount: lastUsableShelfBatch.length,
        batchVarietyBonus: BATCH_VARIETY_BONUS,
        batchDuplicatePenalty: BATCH_DUPLICATE_PENALTY,
        districtAnchorBonus: DISTRICT_ANCHOR_BONUS,
        shelfBatchCount: shelfBatch.length,
        shelfBatchMode: shelfBatchMode,
        shelfVisible: shelfState.visible,
        shelfMinimized: shelfState.minimized,
        shelfUserClosed: shelfState.userClosed,
        shelfLastReason: shelfState.lastReason,
        shelfWatchdogActive: !!shelfWatchdogTimer,
        shelfWatchdogIntervalMs: SHELF_WATCHDOG_INTERVAL_MS,
        lastSafeDestination: lastSafeDestinationHandoff
          ? {
              id: lastSafeDestinationHandoff.id,
              name: lastSafeDestinationHandoff.name,
              entityType: lastSafeDestinationHandoff.entityType,
              lat: lastSafeDestinationHandoff.lat,
              lng: lastSafeDestinationHandoff.lng,
              hasBounds: !!lastSafeDestinationHandoff.bounds,
              normalizedBy: lastSafeDestinationHandoff.__normalizedBy
            }
          : null,
        lastMapRecoveryAt: lastMapRecoveryAt,
        lastMapRecoveryReason: lastMapRecoveryReason,
        pendingMapRecoveryTimers: destinationCameraTimerIds.length,
        presentationReady: presentationState.ready,
        presentationToken: presentationState.token,
        presentationResultCount: presentationState.resultCount,
        presentationPinsPainted: presentationState.pinsPainted,
        presentationMapVisible: presentationState.mapVisible,
        presentationMenuMinimized: presentationState.menuMinimized,
        presentationReason: presentationState.lastReason,
        presentationGeneration: presentationState.generation
      };
    }
  };

  scheduleResultsPillSync("boot");
  log("V600.78 Auto-Shelf Presentation Authority loaded");
}(window));


/* =====================================================================
   DRIVE IQ V600.66 — PUBLIC FULL POOL SHELF RUNTIME AUTHORITY
===================================================================== */
(function(window){
"use strict";

var scan=window.DriveIQAdventureScan;

if(
  !scan ||
  scan.__V60066_FULL_POOL_RUNTIME_AUTHORITY__
){
  return;
}

scan.__V60066_FULL_POOL_RUNTIME_AUTHORITY__=true;

function pool(){
  try{
    return typeof scan.recommendationPool==="function"
      ? scan.recommendationPool()
      : [];
  }catch(error){
    return [];
  }
}

function renderFullPool(reason){
  var items=pool()
    .slice()
    .sort(function(a,b){
      return Number(b&&b.__scanScore||0)-
        Number(a&&a.__scanScore||0);
    })
    .slice(0,20);

  if(!items.length||typeof scan.renderResultShelf!=="function"){
    return false;
  }

  return scan.renderResultShelf(items,{
    decisionMode:"top",
    totalCount:items.length,
    remainingCount:0,
    reason:String(reason||"v600.66-full-pool-runtime")
  });
}

["openResultShelf","showResultShelf"].forEach(function(name){
  var original=scan[name];

  if(typeof original!=="function"){
    return;
  }

  scan[name]=function(){
    var result=original.apply(scan,arguments);

    window.setTimeout(function(){
      renderFullPool(name);
    },0);

    return result;
  };
});

if(typeof scan.toggleResultShelf==="function"){
  var originalToggle=scan.toggleResultShelf;

  scan.toggleResultShelf=function(){
    var result=originalToggle.apply(scan,arguments);

    if(result!==false){
      window.setTimeout(function(){
        renderFullPool("toggleResultShelf");
      },0);
    }

    return result;
  };
}

scan.renderFullPoolShelf=renderFullPool;

try{
  console.log(
    "[Drive IQ Adventure V602.0] Google Places Primary + Auto-Shelf Authority installed"
  );
}catch(error){}

})(window);
